/* eslint-disable no-unused-vars, no-useless-escape, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation, Link, Navigate } from 'react-router-dom';
import './App.css';
import Programs from './components/Programs';
import CourseDetails from './components/CourseDetails';
import AdminCourseUpload from './components/AdminCourseUpload';
import ProgramOutcomeUpload from './components/ProgramOutcomeUpload';
import ElectivePool from './components/ElectivePool';
import CurriculumPlanGenerator from './components/CurriculumPlanGenerator';
import ProgramOutcomesGenerator from './components/ProgramOutcomesGenerator';
import ProgramOutcomeYearComparison from './components/ProgramOutcomeYearComparison';
import CoursePlanYearComparison from './components/CoursePlanYearComparison';
import { API_BASE_URL } from './config';
import { sanitizedHtmlProps, toAbsoluteUploadUrl, rewriteUploadUrls, relativizeUploadUrls } from './utils/sanitize';
import { readApprovedProgramOutcomes } from './utils/approvedProgramOutcomes';
import AcademicYearSelector from './components/AcademicYearSelector';
import HeaderEducationSelector from './components/HeaderEducationSelector';
import { getAcademicYearBasename, isPlanningYear } from './config/academicYear';
import {
  filterAndSortPrograms,
  getProgramEducationLevel,
  getProgramType,
  normalizeProgramSearchText,
} from './utils/programManagementFilters';

// Tokenları sekme bazlı tut (sessionStorage). Geriye dönük uyumluluk için localStorage'dan okur, bulursa sessionStorage'a taşır.
const readAuthItem = (key) => {
  try {
    const s = window.sessionStorage.getItem(key);
    if (s) return s;
  } catch (e) { /* ignore */ }
  try {
    const l = window.localStorage.getItem(key);
    if (l) {
      try { window.sessionStorage.setItem(key, l); } catch (e) { /* ignore */ }
      try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
      return l;
    }
  } catch (e) { /* ignore */ }
  return null;
};

const writeAuthItem = (key, value) => {
  try { window.sessionStorage.setItem(key, value); } catch (e) { /* ignore */ }
  try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
};

const removeAuthItem = (key) => {
  try { window.sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
  try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
};

const isProgramManagementUser = (userType) => (
  userType === 'programAdmin' || userType === 'universityAdmin'
);

const slugifyProgramName = (text) => String(text || '')
  .toLowerCase()
  .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
  .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
  .replace(/[^a-z0-9\s-]/g, '')
  .trim()
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-');

const getStoredUniversityRoleLabel = () => {
  try {
    const storedRole = window.localStorage.getItem('universityAdminRole');
    const storedRoleLabels = {
      rector: 'Rektör',
      rector_vice: 'Rektör Yardımcısı',
      dean: 'Dekan',
      dean_vice: 'Dekan Yardımcısı',
    };
    if (storedRoleLabels[storedRole]) return storedRoleLabels[storedRole];

    const roles = JSON.parse(window.localStorage.getItem('all_active_roles') || '[]');
    const roleIds = roles.map((role) => Number(role.role_type_id));
    if (roleIds.includes(1)) return 'Rektör';
    if (roleIds.includes(2)) return 'Rektör Yardımcısı';
    if (roleIds.includes(3)) return 'Dekan';
    if (roleIds.includes(4)) return 'Dekan Yardımcısı';
  } catch (e) {
    // Rol etiketi okunamazsa genel yönetici etiketi gösterilir.
  }
  return 'Fakülte Yöneticisi';
};

const hasStoredUniversityManagementRole = () => {
  try {
    const roles = JSON.parse(window.localStorage.getItem('all_active_roles') || '[]');
    return roles.some((role) => [1, 2, 3, 4].includes(Number(role.role_type_id)));
  } catch (e) {
    return false;
  }
};

function RequireRole({ allow, userType, isLoggedIn, children }) {
  // İlk kriter: state üzerinden doğrulanmış oturum
  const okByState = Boolean(isLoggedIn) && allow.includes(userType);

  // İkinci kriter: doğrudan URL ile gelince bootstrap tamamlanmadan önce token'a göre izin ver
  // (logout sonrası tokenlar temizlendiği için bu yol güvenlik açığı oluşturmaz)
  const hasProgramAdminToken = !!readAuthItem('programAdminToken');
  const hasAdminToken = !!readAuthItem('adminToken');
  const hasUniversityAdminToken = !!readAuthItem('universityAdminToken');

  // ProgramAdmin olup aynı zamanda rektör/dekan vb. üniversite rolleri olanlar:
  // unified-login, öncelik gereği userType=programAdmin döndürüyor.
  // Bu durumda üniversite-admin sayfalarına erişimi localStorage'daki all_active_roles ile açıyoruz.
  let hasUniversityPrivilegeFromRoles = false;
  try {
    const rolesRaw = window.localStorage.getItem('all_active_roles');
    const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
    if (Array.isArray(roles)) {
      hasUniversityPrivilegeFromRoles = roles.some(r => [1, 2, 3, 4].includes(Number(r?.role_type_id)));
    }
  } catch (e) {
    // ignore
  }

  const okByToken =
    (allow.includes('programAdmin') && hasProgramAdminToken) ||
    (allow.includes('admin') && hasAdminToken) ||
    (allow.includes('universityAdmin') && (hasUniversityAdminToken || (hasProgramAdminToken && hasUniversityPrivilegeFromRoles)));

  const ok = okByState || okByToken;
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

function RequireProgramOwnerOrAdmin({ userType, isLoggedIn, programAdminProgramIds, children }) {
  const allow = ['admin', 'programAdmin', 'universityAdmin'];
  const okByState = Boolean(isLoggedIn) && allow.includes(userType);
  const hasProgramAdminToken = !!readAuthItem('programAdminToken');
  const hasAdminToken = !!readAuthItem('adminToken');
  const hasUniversityAdminToken = !!readAuthItem('universityAdminToken');
  const okRole = okByState || hasAdminToken || hasProgramAdminToken || hasUniversityAdminToken;
  if (!okRole) return <Navigate to="/" replace />;

  // Dekan/dekan yardımcısının fakülte kapsamı backend tarafından program bazında
  // doğrulanır; frontend yalnızca ilgili yönetim sayfasına geçişi açar.
  if (userType === 'admin' || userType === 'universityAdmin' || hasAdminToken || hasUniversityAdminToken) {
    return children;
  }

  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  // Akademik yıl önekli URL doğrudan açıldığında React navigation state'i
  // bulunmaz. Program bağlamı query string'de korunduğu için onu da yetki
  // kontrolünde kullan; aksi halde geçerli PÇ kanıt bağlantısı ana sayfaya
  // yönlendiriliyordu.
  const queryProgramId = new URLSearchParams(location?.search || '').get('programId');
  const programId = state?.program?.id ?? state?.programId ?? (queryProgramId ? Number(queryProgramId) : null);

  let ownedIds = Array.isArray(programAdminProgramIds) ? programAdminProgramIds : [];
  if (!ownedIds.length) {
    try { ownedIds = JSON.parse(localStorage.getItem('programAdminProgramIds') || '[]'); } catch (e) { ownedIds = []; }
  }
  const owns = programId != null && ownedIds.map(Number).includes(Number(programId));
  if (!owns) return <Navigate to="/" replace />;

  return children;
}

function App() {
  const [userType, setUserType] = useState('public'); // Varsayılan olarak 'public'
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [adminToken, setAdminToken] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [programLoginData, setProgramLoginData] = useState({ email: '', password: '' });
  const [programAdmin, setProgramAdmin] = useState(null);
  const [programAdminToken, setProgramAdminToken] = useState('');
  const [programAdminProgramIds, setProgramAdminProgramIds] = useState([]);
  const [showProgramAdminModal, setShowProgramAdminModal] = useState(false);
  const [newProgramAdmin, setNewProgramAdmin] = useState({ akademisyen_id: '', program_id: '' });
  const [allPrograms, setAllPrograms] = useState([]);

  // Rektörlük oturumundaki tüm sayfalar aynı kurumsal kabuğu paylaşır.
  useEffect(() => {
    const className = 'university-admin-session';
    document.body.classList.toggle(className, userType === 'universityAdmin');
    return () => document.body.classList.remove(className);
  }, [userType]);

  // Auth temizliği (rol çakışmalarını önlemek için)
  const clearAuthStorage = useCallback(() => {
    try {
      removeAuthItem('programAdminToken');
      removeAuthItem('adminToken');
      removeAuthItem('universityAdminToken');
      // Bu ikisi token değil ama temizlikte kalsın
      window.localStorage.removeItem('all_active_roles');
      window.localStorage.removeItem('universityAdminRole');
      window.localStorage.removeItem('programAdminProgramIds');
      window.localStorage.removeItem('adminLoggedIn');
    } catch (e) {
      // ignore
    }
  }, []);

  const clearAuthState = useCallback(() => {
    setIsLoggedIn(false);
    setUserType('public');
    setAdminToken('');
    setAdminInfo(null);
    setProgramAdmin(null);
    setProgramAdminToken('');
    setProgramAdminProgramIds([]);
  }, []);

  // Sayfa yenilenince oturumları koru - Priority: Program Admin > University Admin > System Admin
  useEffect(() => {
    const bootstrap = async () => {
      // 1. Program Admin
      const storedProgramAdminToken = readAuthItem('programAdminToken');
      if (storedProgramAdminToken) {
        try {
          const meRes = await fetch(`${API_BASE_URL}/api/program-admin/me`, {
            headers: { Authorization: `Bearer ${storedProgramAdminToken}` }
          });
          const meData = await meRes.json();
          if (meRes.ok && Array.isArray(meData.programIds)) {
            // Eski oturumlarda aynı token Program Başkanı önceliğiyle saklanmış
            // olabilir. Kullanıcının ayrıca Rektör/Dekan/Dekan Yardımcısı rolü
            // varsa daha geniş kapsamlı fakülte yönetim panelini aç.
            try {
              const universityMeRes = await fetch(`${API_BASE_URL}/api/university-admin/me`, {
                headers: { Authorization: `Bearer ${storedProgramAdminToken}` }
              });
              const universityMeData = await universityMeRes.json();
              if (universityMeRes.ok && universityMeData.admin) {
                setProgramAdmin(null);
                setProgramAdminToken('');
                setProgramAdminProgramIds([]);
                setAdminToken(storedProgramAdminToken);
                setAdminInfo(universityMeData.admin);
                if (universityMeData.admin.role) {
                  window.localStorage.setItem('universityAdminRole', universityMeData.admin.role);
                }
                writeAuthItem('universityAdminToken', storedProgramAdminToken);
                removeAuthItem('programAdminToken');
                setIsLoggedIn(true);
                setUserType('universityAdmin');
                return;
              }
            } catch (e) {
              // Üniversite yönetim rolü yoksa normal Program Başkanı oturumu sürer.
            }

            // Önce olası eski rol state'lerini temizle
            setAdminToken('');
            setAdminInfo(null);
            setProgramAdmin(null);
            setProgramAdminToken(storedProgramAdminToken);
            setProgramAdminProgramIds(meData.programIds);
            try { localStorage.setItem('programAdminProgramIds', JSON.stringify(meData.programIds || [])); } catch (e) { }
            setIsLoggedIn(true);
            setUserType('programAdmin');
            // Tek kullanıcı tipi kalsın
            localStorage.removeItem('adminToken');
            localStorage.removeItem('universityAdminToken');
            return; // Stop checking others
          } else {
            localStorage.removeItem('programAdminToken');
          }
        } catch (e) {
          localStorage.removeItem('programAdminToken');
        }
      }

      // 2. University Admin
      const storedUniversityAdminToken = readAuthItem('universityAdminToken');
      if (storedUniversityAdminToken) {
        try {
          const meRes = await fetch(`${API_BASE_URL}/api/university-admin/me`, {
            headers: { Authorization: `Bearer ${storedUniversityAdminToken}` }
          });
          const meData = await meRes.json();
          if (meRes.ok && meData.admin) {
            setAdminToken(storedUniversityAdminToken);
            setAdminInfo(meData.admin);
            if (meData.admin.role) {
              window.localStorage.setItem('universityAdminRole', meData.admin.role);
            }
            setIsLoggedIn(true);
            setUserType('universityAdmin');
            // Tek kullanıcı tipi kalsın
            localStorage.removeItem('adminToken');
            localStorage.removeItem('programAdminToken');
            return; // Stop checking others
          } else {
            localStorage.removeItem('universityAdminToken');
          }
        } catch (e) {
          localStorage.removeItem('universityAdminToken');
        }
      }

      // 3. System Admin
      const storedAdminToken = readAuthItem('adminToken');
      if (storedAdminToken) {
        try {
          const meRes = await fetch(`${API_BASE_URL}/api/admin/me`, {
            headers: { Authorization: `Bearer ${storedAdminToken}` }
          });
          const meData = await meRes.json();
          if (meRes.ok && meData.admin) {
            setAdminToken(storedAdminToken);
            setAdminInfo(meData.admin);
            setIsLoggedIn(true);
            setUserType('admin');
            // Tek kullanıcı tipi kalsın
            localStorage.removeItem('programAdminToken');
            localStorage.removeItem('universityAdminToken');
            return; // Stop checking others
          } else {
            localStorage.removeItem('adminToken');
          }
        } catch (e) {
          localStorage.removeItem('adminToken');
        }
      }

    }


    bootstrap();
  }, []);
  const [educationTypes, setEducationTypes] = useState([]);
  const [selectedEducationType, setSelectedEducationType] = useState(() => {
    try { return window.sessionStorage.getItem('ebsSelectedEducationType') || ''; } catch (e) { return ''; }
  });
  useEffect(() => {
    try {
      if (selectedEducationType) window.sessionStorage.setItem('ebsSelectedEducationType', String(selectedEducationType));
      else window.sessionStorage.removeItem('ebsSelectedEducationType');
    } catch (e) { /* storage unavailable */ }
  }, [selectedEducationType]);
  const [faculties, setFaculties] = useState([]);
  const [facultiesLoading, setFacultiesLoading] = useState(false);
  const [newFaculty, setNewFaculty] = useState('');
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  const fetchEducationTypes = useCallback(async () => {
    try {
      const endpoint = userType === 'admin' ? `${API_BASE_URL}/api/admin/education-types` : `${API_BASE_URL}/api/public/education-types`;
      const headers = {};
      if (userType === 'admin' && adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }
      const response = await fetch(endpoint, { headers });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setEducationTypes(data);
      } else {
        setEducationTypes([]);
        console.error('Beklenmeyen veri:', data);
      }
    } catch (error) {
      // Connection refused veya network hatalarını sessizce handle et
      if (error.name === 'AbortError' || error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        console.warn('Backend bağlantı hatası (sayfa yenilenebilir):', error.message);
        // Boş array set et, sayfa çalışmaya devam etsin
        setEducationTypes([]);
      } else {
        console.error('Error fetching education types:', error);
        console.error('API_BASE_URL:', API_BASE_URL);
        console.error('Endpoint:', userType === 'admin' ? `${API_BASE_URL}/api/admin/education-types` : `${API_BASE_URL}/api/public/education-types`);
        setEducationTypes([]);
      }
    }
  }, [userType, adminToken]);

  // Race condition'ı önlemek için request ID takibi
  const fetchFacultiesRequestIdRef = React.useRef(0);
  // Son fetch edilen education type'ı tutmak için ref
  const lastFetchedEducationTypeRef = React.useRef(null);

  const fetchFaculties = useCallback(async (educationTypeId) => {
    const idNum = parseInt(educationTypeId, 10);

    // Yeni bir request ID oluştur
    const currentRequestId = ++fetchFacultiesRequestIdRef.current;

    try {
      const endpoint = userType === 'admin' ? `${API_BASE_URL}/api/admin/faculties` : `${API_BASE_URL}/api/public/faculties`;

      setFacultiesLoading(true);

      if (!idNum || Number.isNaN(idNum)) {
        // Bu request hala geçerli mi kontrol et
        if (currentRequestId !== fetchFacultiesRequestIdRef.current) return;
        setFaculties([]);
        setFacultiesLoading(false);
        return;
      }

      const headers = {};
      if (userType === 'admin' && adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }

      const response = await fetch(`${endpoint}?education_type_id=${idNum}`, {
        headers
      });

      // Bu request hala geçerli mi kontrol et (race condition kontrolü)
      if (currentRequestId !== fetchFacultiesRequestIdRef.current) return;

      if (!response.ok) {
        console.error('Error fetching faculties:', response.statusText);
        setFaculties([]);
        setFacultiesLoading(false);
        // Hata durumunda ref'i temizleme - sonsuz döngüyü önlemek için
        return;
      }

      const data = await response.json();

      // Tekrar kontrol et (async işlem sırasında başka bir request başlamış olabilir)
      if (currentRequestId !== fetchFacultiesRequestIdRef.current) return;

      // Sadece doğru eğitim tipine ait fakülteleri set et
      if (Array.isArray(data)) {
        // Ekstra güvenlik: gelen verinin doğru eğitim tipine ait olduğundan emin ol
        const filtered = data.filter(f => String(f.education_type_id) === String(idNum));
        setFaculties(filtered);
        // Başarılı fetch sonrası ref'i güncelle
        lastFetchedEducationTypeRef.current = String(idNum);
      } else {
        setFaculties([]);
      }
    } catch (error) {
      // Bu request hala geçerli mi kontrol et
      if (currentRequestId !== fetchFacultiesRequestIdRef.current) return;
      console.error('Error fetching faculties:', error);
      setFaculties([]);
      // Hata durumunda ref'i temizleme - sonsuz döngüyü önlemek için
    }
    finally {
      // Bu request hala geçerli mi kontrol et
      if (currentRequestId === fetchFacultiesRequestIdRef.current) {
        setFacultiesLoading(false);
      }
    }
  }, [userType, adminToken]);

  // useEffect hook'ları - fonksiyonlardan sonra tanımlanıyor
  useEffect(() => {
    // Sadece public sayfada eğitim tiplerini yükle
    if (userType === 'public') {
      fetchEducationTypes();
    }
  }, [userType, fetchEducationTypes]);

  useEffect(() => {
    // URL'den geldiyse EducationTypeWrapper fakülteleri yükleyecek, bu useEffect atlanmalı
    const currentPath = window.location.pathname;
    const isEducationTypeRoute = currentPath.startsWith('/education/');

    if (selectedEducationType && userType === 'public' && !isEducationTypeRoute) {
      // Aynı education type için tekrar fetch yapmamak için kontrol et
      if (lastFetchedEducationTypeRef.current === String(selectedEducationType)) {
        return;
      }

      // Fetch yap (ref güncellemesi başarılı fetch sonrası fetchFaculties içinde yapılacak)
      fetchFaculties(selectedEducationType);
    } else if (!selectedEducationType) {
      // Eğitim tipi seçilmemişse fakülteleri temizle
      lastFetchedEducationTypeRef.current = null;
      setFaculties([]);
      setFacultiesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEducationType, userType]);

  // Admin sayfası için ayrı useEffect
  useEffect(() => {
    if (userType === 'admin' && isLoggedIn) {
      fetchEducationTypes();
    }
  }, [userType, isLoggedIn, fetchEducationTypes]);

  useEffect(() => {
    if (selectedEducationType && userType === 'admin' && isLoggedIn) {
      // Aynı education type için tekrar fetch yapmamak için kontrol et
      if (lastFetchedEducationTypeRef.current === String(selectedEducationType)) {
        return;
      }

      // Fetch yap (ref güncellemesi başarılı fetch sonrası fetchFaculties içinde yapılacak)
      fetchFaculties(selectedEducationType);
    } else if (!selectedEducationType && userType === 'admin' && isLoggedIn) {
      // Eğitim tipi seçilmemişse fakülteleri temizle
      lastFetchedEducationTypeRef.current = null;
      setFaculties([]);
      setFacultiesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEducationType, userType, isLoggedIn]);

  const handleUnifiedLogin = useCallback(async (e, navigate) => {
    e.preventDefault();
    try {
      // Eğer kullanıcı sadece email öneki yazdıysa (içinde @ yok ama . var) otomatik olarak
      // @kocaelisaglik.edu.tr ekle. Admin kullanıcı adları (ör: "admin") olduğu gibi gider.
      const rawIdentifier = (loginData.username || '').trim();
      const identifier = (!rawIdentifier.includes('@') && rawIdentifier.includes('.'))
        ? `${rawIdentifier}@kocaelisaglik.edu.tr`
        : rawIdentifier;

      const response = await fetch(`${API_BASE_URL}/api/unified-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier,
          password: loginData.password
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Giriş başarısız');
        return;
      }

      if (data.userType === 'admin') {
        // Rol çakışmasını önle: önce her şeyi temizle
        clearAuthStorage();
        clearAuthState();
        // Sistem admin girişi
        setAdminToken(data.token);
        setAdminInfo(data.admin);
        setIsLoggedIn(true);
        setUserType('admin');
        writeAuthItem('adminToken', data.token);
        // Clean others
        removeAuthItem('programAdminToken');
        removeAuthItem('universityAdminToken');
        navigate('/admin');
      } else if (data.userType === 'programAdmin') {
        // Rol çakışmasını önle: önce her şeyi temizle
        clearAuthStorage();
        clearAuthState();
        // Program yöneticisi girişi (role_assignments tabanlı veya eski sistem)
        setProgramAdmin(data.admin);
        setProgramAdminToken(data.token);
        // NOT: setIsLoggedIn / setUserType, landing localStorage'a yazıldıktan SONRA
        // (navigate'ten hemen önce) ayarlanıyor; aksi halde AdminPage'in yönlendirme
        // efekti landing hazır olmadan tetiklenip /program/sidebar'a atıyor.
        writeAuthItem('programAdminToken', data.token);
        // Tüm aktif rolleri kaydet (çoklu rol desteği için)
        if (data.all_active_roles) {
          window.localStorage.setItem('all_active_roles', JSON.stringify(data.all_active_roles));
        }
        // Clean others
        removeAuthItem('adminToken');
        removeAuthItem('universityAdminToken');
        // Program ID'lerini kontrol et - önce login response'undan, sonra /me endpoint'inden
        let programIds = [];
        if (data.programIds && Array.isArray(data.programIds) && data.programIds.length > 0) {
          programIds = data.programIds;
        } else if (data.admin && data.admin.program_id) {
          programIds = [data.admin.program_id];
        } else {
          try {
            const meRes = await fetch(`${API_BASE_URL}/api/program-admin/me`, {
              headers: { Authorization: `Bearer ${data.token}` }
            });
            const meData = await meRes.json();
            if (meRes.ok && Array.isArray(meData.programIds) && meData.programIds.length > 0) {
              programIds = meData.programIds;
            }
          } catch (meErr) {
            // /me endpoint hatasi sessiz — asagidaki fallback devreye girer.
          }
        }

        if (programIds.length > 0) {
          setProgramAdminProgramIds(programIds);
          try { localStorage.setItem('programAdminProgramIds', JSON.stringify(programIds)); } catch (e) { }
          // Program başkanı girişinde doğrudan Program Dersleri ekranına yönlendir.
          // Sidebar yönetimi artık o sayfadaki "Düzenle" butonuyla açılıyor.
          const pid = programIds[0];
          let pName = '';
          try {
            const pr = await fetch(`${API_BASE_URL}/api/programs`);
            if (pr.ok) {
              const programs = await pr.json();
              const found = Array.isArray(programs) ? programs.find(p => p.id === pid) : null;
              if (found) pName = found.name;
            }
          } catch (e) { /* isim alınamazsa aşağıda programId ile devam */ }
          const slugify = (text) => String(text || '').toLowerCase()
            .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
            .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
            .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
          if (pName) {
            const landing = `/program/${slugify(pName)}?programId=${pid}`;
            try { localStorage.setItem('programAdminLanding', landing); } catch (e) { }
            setIsLoggedIn(true);
            setUserType('programAdmin');
            navigate(landing, { state: { programId: pid, programName: pName, program: { id: pid, name: pName } } });
          } else {
            try { localStorage.removeItem('programAdminLanding'); } catch (e) { }
            setIsLoggedIn(true);
            setUserType('programAdmin');
            navigate(`/program/sidebar?programId=${pid}`);
          }
        } else {
          setIsLoggedIn(true);
          setUserType('programAdmin');
          alert('Bu kullanıcıya bağlı program bulunamadı');
        }
      } else if (data.userType === 'universityAdmin') {
        // Rol çakışmasını önle: önce her şeyi temizle
        clearAuthStorage();
        clearAuthState();
        // Üniversite yöneticisi girişi (role_assignments tabanlı veya eski sistem)
        setAdminToken(data.token);
        setAdminInfo(data.admin);
        if (data.admin && data.admin.role) {
          window.localStorage.setItem('universityAdminRole', data.admin.role);
        }
        setIsLoggedIn(true);
        setUserType('universityAdmin');
        writeAuthItem('universityAdminToken', data.token);
        if (data.all_active_roles) {
          window.localStorage.setItem('all_active_roles', JSON.stringify(data.all_active_roles));
        }
        // Clean others
        removeAuthItem('adminToken');
        removeAuthItem('programAdminToken');
        navigate('/university-admin/dashboard');
      } else {
        // Backend userType döndürmediyse eski state'e düşmesin (en kritik "admin'e geçiyor" sebebi)
        clearAuthStorage();
        clearAuthState();
        alert('Bu kullanıcı için aktif rol bulunamadı (userType belirlenemedi). Lütfen rol atamalarını kontrol edin.');
      }

    } catch (error) {
      console.error('Login error:', error);
      alert('Giriş sırasında bir hata oluştu');
    }
  }, [loginData, clearAuthStorage, clearAuthState]);

  const handleCreateProgramAdmin = async (e) => {
    e.preventDefault();
    if (!newProgramAdmin.akademisyen_id || !newProgramAdmin.program_id) {
      alert('Akademisyen ve program seçilmelidir');
      return;
    }
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/role-assignments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          akademisyen_id: parseInt(newProgramAdmin.akademisyen_id),
          role_type_id: 5, // Program Başkanı
          program_id: parseInt(newProgramAdmin.program_id)
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Program yöneticisi oluşturulurken bir hata oluştu');
        return;
      }
      alert('Program yöneticisi başarıyla oluşturuldu');
      setNewProgramAdmin({ akademisyen_id: '', program_id: '' });
      setShowProgramAdminModal(false);
    } catch (error) {
      alert('Program yöneticisi oluşturulurken bir hata oluştu');
    }
  };

  const fetchAllPrograms = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/programs`);
      if (response.ok) {
        const data = await response.json();
        setAllPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && userType === 'admin') {
      fetchAllPrograms();
    }
  }, [isLoggedIn, userType, fetchAllPrograms]);

  const handleAddFaculty = async (e) => {
    e.preventDefault();
    if (!newFaculty || !selectedEducationType) return;
    const facultyData = {
      name: newFaculty,
      education_type_id: parseInt(selectedEducationType)
    };
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/faculties`, {
        method: 'POST',
        headers,
        body: JSON.stringify(facultyData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Fakülte eklenirken bir hata oluştu');
        return;
      }
      setNewFaculty('');
      setShowAddForm(false);
      fetchFaculties(selectedEducationType);
    } catch (error) {
      alert('Fakülte eklenirken bir hata oluştu');
    }
  };

  const handleEditFaculty = async (e) => {
    e.preventDefault();
    if (!editingFaculty) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/faculties/${editingFaculty.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editingFaculty.name,
          education_type_id: selectedEducationType,
        }),
      });
      if (response.ok) {
        setEditingFaculty(null);
        fetchFaculties(selectedEducationType);
      }
    } catch (error) {
      alert('Fakülte güncellenirken bir hata oluştu');
    }
  };

  const handleDeleteFaculty = async (facultyId) => {
    if (!window.confirm('Bu fakülteyi silmek istediğinizden emin misiniz?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/faculties/${facultyId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        if (selectedFaculty && selectedFaculty.id === facultyId) {
          setSelectedFaculty(null);
        }
        fetchFaculties(selectedEducationType);
      }
    } catch (error) {
      alert('Fakülte silinirken bir hata oluştu');
    }
  };

  const startEditing = (faculty) => setEditingFaculty({ ...faculty });
  const cancelEditing = () => setEditingFaculty(null);
  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    if (!showAddForm) setNewFaculty('');
  };

  const handleLogout = () => {
    clearAuthStorage();
    clearAuthState();
    setLoginData({ username: '', password: '' });
    setEducationTypes([]);
    setSelectedEducationType('');
    setFaculties([]);
    setSelectedFaculty(null);
    // Ana sayfaya yönlendir
    window.location.href = '/';
  };

  // Input handler'ları - performans için optimize edildi
  const handleUsernameChange = useCallback((e) => {
    setLoginData(prev => ({ ...prev, username: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setLoginData(prev => ({ ...prev, password: e.target.value }));
  }, []);

  // AdminCourseUpload wrapper component - Router içinde navigate kullanabilmek için
  const AdminCourseUploadWrapper = ({ adminToken }) => {
    const navigate = useNavigate();
    return <AdminCourseUpload adminToken={adminToken} onBack={() => navigate('/admin')} />;
  };

  const ProgramOutcomeUploadWrapper = ({ adminToken }) => {
    const navigate = useNavigate();
    return <ProgramOutcomeUpload adminToken={adminToken} onBack={() => navigate('/admin')} />;
  };

  return (
    <Router basename={getAcademicYearBasename()}>
      <AcademicYearSelector />
      <HeaderEducationSelector educationTypes={educationTypes} selectedEducationType={selectedEducationType} />
      <Routes>
        <Route path="/" element={
          <MainPage
            educationTypes={educationTypes}
            selectedEducationType={selectedEducationType}
            setSelectedEducationType={setSelectedEducationType}
            faculties={faculties}
            facultiesLoading={facultiesLoading}
            selectedFaculty={selectedFaculty}
            setSelectedFaculty={setSelectedFaculty}
            Programs={Programs}
            userType={userType}
            isLoggedIn={isLoggedIn}
            handleLogout={handleLogout}
            showContactModal={showContactModal}
            setShowContactModal={setShowContactModal}
          />
        } />
        <Route path="/education-types" element={
          <EducationTypesRoute
            educationTypes={educationTypes}
            onSelect={setSelectedEducationType}
            fetchEducationTypes={fetchEducationTypes}
          />
        } />
        <Route path="/education/:educationTypeSlug" element={
          <EducationTypeWrapper
            educationTypes={educationTypes}
            setSelectedEducationType={setSelectedEducationType}
            selectedEducationType={selectedEducationType}
            faculties={faculties}
            facultiesLoading={facultiesLoading}
            setSelectedFaculty={setSelectedFaculty}
            fetchFaculties={fetchFaculties}
            Programs={Programs}
            userType={userType}
            isLoggedIn={isLoggedIn}
            handleLogout={handleLogout}
            showContactModal={showContactModal}
            setShowContactModal={setShowContactModal}
          />
        } />
        <Route path="/faculty/:facultySlug" element={<FacultyPage userType={userType} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />} />
        <Route path="/program/:programSlug" element={<ProgramPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />} />
        <Route path="/program/:programSlug/syllabus-status" element={<SyllabusStatusPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} handleLogout={handleLogout} />} />
        <Route path="/program/:programSlug/ders-plani-olustur" element={
          <RequireProgramOwnerOrAdmin userType={userType} isLoggedIn={isLoggedIn} programAdminProgramIds={programAdminProgramIds}>
            <DersPlaniOlusturPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          </RequireProgramOwnerOrAdmin>
        } />
        <Route path="/program/:programSlug/program-ciktilari-olustur" element={
          <RequireProgramOwnerOrAdmin userType={userType} isLoggedIn={isLoggedIn} programAdminProgramIds={programAdminProgramIds}>
            <ProgramCiktilariOlusturPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          </RequireProgramOwnerOrAdmin>
        } />
        <Route path="/program/:programSlug/pc-degisiklikleri" element={
          <RequireProgramOwnerOrAdmin userType={userType} isLoggedIn={isLoggedIn} programAdminProgramIds={programAdminProgramIds}>
            <ProgramOutcomeYearComparisonPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          </RequireProgramOwnerOrAdmin>
        } />
        <Route path="/program/:programSlug/ders-plani-degisiklikleri" element={
          <RequireProgramOwnerOrAdmin userType={userType} isLoggedIn={isLoggedIn} programAdminProgramIds={programAdminProgramIds}>
            <CoursePlanYearComparisonPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          </RequireProgramOwnerOrAdmin>
        } />
        <Route path="/program/:programSlug/secmeli-dersler" element={
          <RequireRole allow={['programAdmin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <ProgramSidebarAdminPage token={programAdminToken || adminToken} programIdProp={programAdminProgramIds && programAdminProgramIds[0]} onLogout={handleLogout} electivesOnly />
          </RequireRole>
        } />
        <Route path="/program/:programSlug/:sectionSlug" element={<ProgramInfoPage userType={userType} programAdminToken={programAdminToken} adminToken={adminToken} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />} />
        <Route path="/admin" element={
          <AdminPage
            isLoggedIn={isLoggedIn}
            loginData={loginData}
            handleUsernameChange={handleUsernameChange}
            handlePasswordChange={handlePasswordChange}
            handleUnifiedLogin={handleUnifiedLogin}
            educationTypes={educationTypes}
            selectedEducationType={selectedEducationType}
            setSelectedEducationType={setSelectedEducationType}
            faculties={faculties}
            selectedFaculty={selectedFaculty}
            setSelectedFaculty={setSelectedFaculty}
            Programs={Programs}
            userType={userType}
            handleLogout={handleLogout}
            showAddForm={showAddForm}
            toggleAddForm={toggleAddForm}
            newFaculty={newFaculty}
            setNewFaculty={setNewFaculty}
            handleAddFaculty={handleAddFaculty}
            editingFaculty={editingFaculty}
            setEditingFaculty={setEditingFaculty}
            handleEditFaculty={handleEditFaculty}
            cancelEditing={cancelEditing}
            startEditing={startEditing}
            handleDeleteFaculty={handleDeleteFaculty}
            showContactModal={showContactModal}
            setShowContactModal={setShowContactModal}
            programLoginData={programLoginData}
            setProgramLoginData={setProgramLoginData}
            showProgramAdminModal={showProgramAdminModal}
            setShowProgramAdminModal={setShowProgramAdminModal}
            newProgramAdmin={newProgramAdmin}
            setNewProgramAdmin={setNewProgramAdmin}
            handleCreateProgramAdmin={handleCreateProgramAdmin}
            allPrograms={allPrograms}
          />
        } />
        <Route
          path="/admin/syllabus-status"
          element={
            <RequireRole allow={['admin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
              <GlobalSyllabusStatusPage
                adminToken={adminToken || programAdminToken}
                handleLogout={handleLogout}
              />
            </RequireRole>
          }
        />
        <Route path="/admin/university-admins" element={
          <RequireRole allow={['admin']} userType={userType} isLoggedIn={isLoggedIn}>
            <UniversityAdminsPage adminToken={adminToken} handleLogout={handleLogout} />
          </RequireRole>
        } />
        <Route path="/admin/program/sidebar" element={
          <RequireRole allow={['programAdmin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <ProgramSidebarAdminPage token={programAdminToken || adminToken} programIdProp={programAdminProgramIds && programAdminProgramIds[0]} onLogout={handleLogout} />
          </RequireRole>
        } />
        <Route path="/program/sidebar" element={
          <RequireRole allow={['programAdmin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <ProgramSidebarAdminPage token={programAdminToken || adminToken} programIdProp={programAdminProgramIds && programAdminProgramIds[0]} onLogout={handleLogout} />
          </RequireRole>
        } />
        <Route path="/admin/course-upload" element={
          <RequireRole allow={['admin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <AdminCourseUploadWrapper adminToken={adminToken || programAdminToken} />
          </RequireRole>
        } />
        <Route path="/admin/program-outcome-upload" element={
          <RequireRole allow={['admin']} userType={userType} isLoggedIn={isLoggedIn}>
            <ProgramOutcomeUploadWrapper adminToken={adminToken} />
          </RequireRole>
        } />
        <Route path="/admin/elective-pool" element={
          <RequireRole allow={['admin', 'universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <ElectivePool adminToken={adminToken || programAdminToken} />
          </RequireRole>
        } />
        <Route path="/university-admin/dashboard" element={
          <RequireRole allow={['universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <UniversityAdminDashboard adminToken={adminToken || programAdminToken} handleLogout={handleLogout} />
          </RequireRole>
        } />
        <Route path="/university-admin/faculty/:facultyId/syllabus-status" element={
          <RequireRole allow={['universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <UniversityAdminFacultySyllabusStatusPage adminToken={adminToken || programAdminToken} handleLogout={handleLogout} />
          </RequireRole>
        } />
        <Route path="/university-admin/program/:programId/syllabus-status" element={
          <RequireRole allow={['universityAdmin']} userType={userType} isLoggedIn={isLoggedIn}>
            <UniversityAdminProgramSyllabusStatusPage adminToken={adminToken || programAdminToken} handleLogout={handleLogout} />
          </RequireRole>
        } />
        <Route
          path="/course/:programSlug/:courseSlug"
          element={
            <CourseDetailsWrapper userType={userType} isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
          }
        />
      </Routes>
    </Router>
  );
}

// EducationTypesRoute: sayfaya girildiğinde bir kez eğitim tiplerini yükler
function EducationTypesRoute({ educationTypes, onSelect, fetchEducationTypes }) {
  const didFetchRef = React.useRef(false);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    if (typeof fetchEducationTypes === 'function') {
      fetchEducationTypes();
    }
    // sadece mount'ta bir kez (StrictMode altında da korunur)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <EducationTypesPage
      educationTypes={educationTypes}
      onSelect={onSelect}
    />
  );
}

// MainPage bileşeni
function MainPage({ educationTypes, selectedEducationType, setSelectedEducationType, faculties, facultiesLoading, selectedFaculty, setSelectedFaculty, Programs, userType, isLoggedIn, handleLogout, showContactModal, setShowContactModal }) {
  const [videoLoading, setVideoLoading] = React.useState(true);
  const [videoError, setVideoError] = React.useState(false);
  const navigate = useNavigate();

  const slugify = React.useCallback((text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }, []);

  React.useEffect(() => {
    if (!selectedEducationType) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    // Egitim tipi degistiginde program cache'i temizle
    try { window.sessionStorage.removeItem('selectedProgram'); } catch (e) { }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [selectedEducationType]);

  const handleVideoLoadStart = () => {
    setVideoLoading(true);
    setVideoError(false);
  };

  const handleVideoCanPlay = () => {
    setVideoLoading(false);
  };

  const handleVideoError = () => {
    setVideoLoading(false);
    setVideoError(true);
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center">
          </div>
          <div className="header-right">
            {(userType === 'admin' && isLoggedIn) && handleLogout && (
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => navigate('/admin/course-upload')}
                  className="logout-button"
                  style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                >
                  Excel Yükle (Ders)
                </button>
                <button
                  onClick={() => navigate('/admin/syllabus-status')}
                  className="logout-button"
                  style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
                >
                  Üniversite Geneli Doluluk
                </button>
                <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
              </div>
            )}
          </div>
        </div>
      </div>
      {!selectedEducationType && (
        <div className="hero-video-wrapper">
          <div className="hero-video-overlay" />
          <video
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster="/kostu_logo.png"
            onLoadStart={handleVideoLoadStart}
            onCanPlay={handleVideoCanPlay}
            onError={handleVideoError}
          >
            <source src="/video.mp4" type="video/mp4" />
            Tarayıcınız video elementini desteklemiyor.
          </video>
          {videoLoading && (
            <div className="video-loading show">
              <div className="loading-spinner"></div>
              <p>Video yükleniyor...</p>
            </div>
          )}
          {videoError && (
            <div className="video-loading show">
              <p>Video yüklenirken hata oluştu</p>
              <button onClick={() => window.location.reload()} className="retry-button">
                Tekrar Dene
              </button>
            </div>
          )}
        </div>
      )}
      <div className="main-content">
        {selectedEducationType && (
          <div className="faculties-section">
            <div className="section-header">
              <h2>Fakülteler</h2>
            </div>
            {facultiesLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Yükleniyor...</div>
            ) : (
              <div className="faculties-list">
                {faculties.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Bu eğitim tipi için fakülte bulunmamaktadır.</div>
                ) : (
                  faculties.map((faculty) => (
                    <div
                      key={faculty.id}
                      className={`faculty-item ${selectedFaculty && selectedFaculty.id === faculty.id ? 'selected' : ''}`}
                      onClick={() => navigate(`/faculty/${slugify(faculty.name)}`, { state: { faculty } })}
                    >
                      <span className="faculty-name">{faculty.name}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
        {/* Fakülte seçimi ayrı sayfaya taşındı */}
      </div>
      {!selectedEducationType && (
        <div className="footer-image">
          <img src="/ekip.png" alt="Bu site KOSTÜ Yazılım Geliştirme Ekibi tarafından geliştirilmiştir." />
        </div>
      )}
      {showContactModal && (
        <ContactModal onClose={() => setShowContactModal(false)} />
      )}
    </div>
  );
}

// AdminPage bileşeni
function AdminPage({ isLoggedIn, loginData, handleUsernameChange, handlePasswordChange, handleUnifiedLogin, educationTypes, selectedEducationType, setSelectedEducationType, faculties, selectedFaculty, setSelectedFaculty, Programs, userType, handleLogout, showAddForm, toggleAddForm, newFaculty, setNewFaculty, handleAddFaculty, editingFaculty, setEditingFaculty, handleEditFaculty, cancelEditing, startEditing, handleDeleteFaculty, showContactModal, setShowContactModal, showProgramAdminModal, setShowProgramAdminModal, newProgramAdmin, setNewProgramAdmin, handleCreateProgramAdmin, allPrograms }) {
  const navigate = useNavigate();
  const [showOperationsDropdown, setShowOperationsDropdown] = useState(false);
  const dropdownRef = React.useRef(null);

  // Güvenlik: /admin sayfası "admin login + admin paneli" içindir.
  // Program başkanı / dekan vb. giriş yaptıysa (isLoggedIn=true) /admin'e gelince admin paneli açılmasın.
  const storedProgramAdminToken = readAuthItem('programAdminToken');
  const storedUniversityAdminToken = readAuthItem('universityAdminToken');
  const storedAdminToken = readAuthItem('adminToken');
  const hasAdminSession = userType === 'admin' || !!storedAdminToken;

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOperationsDropdown(false);
      }
    };

    if (showOperationsDropdown) {
      // capture phase kullanmıyoruz, bubble phase kullanıyoruz ki buton tıklamaları çalışsın
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showOperationsDropdown]);

  // Admin olmayan ama oturum açık kullanıcı /admin'e gelirse ekran göstermeden direkt kendi paneline yönlendir.
  // navigate() render sirasinda cagrilirsa "setState during render" uyarisi verir; useEffect icine alindi.
  const storedProgramAdminLanding = (() => { try { return localStorage.getItem('programAdminLanding'); } catch { return null; } })();
  const shouldRedirectNonAdmin = isLoggedIn && !hasAdminSession;
  const nonAdminRedirectTarget = shouldRedirectNonAdmin
    ? ((userType === 'programAdmin' || !!storedProgramAdminToken)
        ? (storedProgramAdminLanding || '/program/sidebar')
        : ((userType === 'universityAdmin' || !!storedUniversityAdminToken) ? '/university-admin/dashboard' : '/'))
    : null;
  useEffect(() => {
    if (nonAdminRedirectTarget) {
      navigate(nonAdminRedirectTarget, { replace: true });
    }
  }, [nonAdminRedirectTarget, navigate]);
  if (shouldRedirectNonAdmin) {
    return null;
  }

  if (!isLoggedIn) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
            <div className="header-center"></div>
          </div>
        </div>
        <div className="main-content" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 80px)',
          padding: '20px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(76, 175, 120, 0.25)',
            border: '3px solid #4CAF78',
            background: 'white'
          }}>
            {/* Yeşil Başlık */}
            <div style={{
              background: '#4CAF78',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem', fontWeight: 600 }}>Giriş Yap</h2>
              <Link to="/" style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                textDecoration: 'none',
                fontSize: '18px'
              }}>×</Link>
            </div>

            {/* Form İçeriği */}
            <div style={{ padding: '28px 24px' }}>
              <form onSubmit={(e) => handleUnifiedLogin(e, navigate)}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151', fontSize: '0.95rem' }}>E-posta</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="username"
                      value={loginData.username}
                      onChange={handleUsernameChange}
                      placeholder="kullanici.adi"
                      autoComplete="username"
                      required
                      style={{
                        width: '100%',
                        padding: '14px 200px 14px 16px',
                        borderRadius: '10px',
                        border: '2px solid #e5e7eb',
                        background: '#f9fafb',
                        fontSize: '1rem',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        boxSizing: 'border-box'
                      }}
                    />
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#4CAF78',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                    >
                      @kocaelisaglik.edu.tr
                    </span>
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151', fontSize: '0.95rem' }}>Şifre</label>
                  <input
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handlePasswordChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#4CAF78',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 4px 14px rgba(76, 175, 120, 0.4)'
                  }}
                >
                  →] Giriş Yap
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Yönetici arayüzü (admin yetkileriyle)
  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center">
          </div>
          <div className="header-right">
            <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
              <button
                onClick={() => navigate('/admin/syllabus-status')}
                className="admin-header-btn admin-header-btn--ghost"
              >
                Üniversite Geneli Doluluk
              </button>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowOperationsDropdown(!showOperationsDropdown)}
                  className={`admin-header-btn admin-header-btn--ghost ${showOperationsDropdown ? 'is-open' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={showOperationsDropdown}
                >
                  İşlemler
                  <span className="admin-header-btn__caret" aria-hidden="true">
                    {showOperationsDropdown ? '▲' : '▼'}
                  </span>
                </button>
                {showOperationsDropdown && (
                  <div className="admin-operations-menu" role="menu">
                    {[
                      { label: 'Üniversite Yöneticileri', onClick: () => navigate('/admin/university-admins') },
                      { label: 'Program Yöneticisi Oluştur', onClick: () => { setNewProgramAdmin({ email: '', password: '', program_ids: [] }); setShowProgramAdminModal(true); } },
                      { label: 'Seçmeli Havuzu', onClick: () => navigate('/admin/elective-pool') },
                      { label: 'Ders Yükle (Excel)', onClick: () => navigate('/admin/course-upload') },
                      { label: 'Program Çıktısı Yükleme', onClick: () => navigate('/admin/program-outcome-upload') },
                    ].map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        role="menuitem"
                        className="admin-operations-menu__item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                          setShowOperationsDropdown(false);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={handleLogout} className="admin-header-btn admin-header-btn--danger">
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="main-content">
        {!selectedEducationType && (
          <div className="faculties-section">
            <div className="section-header">
              <h2>Eğitim Türü Seçin</h2>
            </div>
            <div className="faculties-list">
              {Array.isArray(educationTypes) && educationTypes.length > 0 ? (
                educationTypes.map((type) => (
                  <div
                    key={type.id}
                    className="faculty-item"
                    onClick={() => {
                      setSelectedEducationType(String(type.id));
                      try { window.sessionStorage.removeItem('selectedProgram'); } catch (e) { }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="faculty-name">{type.name}</span>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>Eğitim türü bulunamadı.</p>
                </div>
              )}
            </div>
          </div>
        )}
        {selectedEducationType && (
          <div className="faculties-section">
            <div className="section-header">
              <button
                type="button"
                className="back-button"
                onClick={() => setSelectedEducationType('')}
              >
                ← Eğitim Türü Değiştir
              </button>
              <h2>Fakülteler</h2>
              <button
                onClick={toggleAddForm}
                className={`toggle-button ${showAddForm ? 'active' : ''}`}
              >
                {showAddForm ? 'Fakülte Ekleme Formunu Kapat' : 'Fakülte Ekle'}
              </button>
            </div>
            {showAddForm && !editingFaculty && (
              <form onSubmit={handleAddFaculty} className="add-form">
                <div className="form-group">
                  <input
                    type="text"
                    value={newFaculty}
                    onChange={(e) => setNewFaculty(e.target.value)}
                    placeholder="Fakülte Adı"
                    className="faculty-name-input"
                  />
                  <button type="submit">Kaydet</button>
                  <button type="button" onClick={toggleAddForm} className="cancel-button">İptal</button>
                </div>
              </form>
            )}
            {editingFaculty && (
              <form onSubmit={handleEditFaculty} className="edit-form">
                <div className="form-group">
                  <input
                    type="text"
                    value={editingFaculty.name}
                    onChange={(e) => setEditingFaculty({ ...editingFaculty, name: e.target.value })}
                    className="faculty-name-input"
                  />
                  <button type="submit">Güncelle</button>
                  <button type="button" onClick={cancelEditing} className="cancel-button">İptal</button>
                </div>
              </form>
            )}
            <div className="faculties-list">
              {faculties.map((faculty) => (
                <div
                  key={faculty.id}
                  className={`faculty-item ${selectedFaculty && selectedFaculty.id === faculty.id ? 'selected' : ''}`}
                  onClick={() => navigate(`/faculty/${(String(faculty.name) || '').toLowerCase().replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-')}`, { state: { faculty } })}
                >
                  <span className="faculty-name">{faculty.name}</span>
                  <div className="faculty-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(faculty);
                      }}
                      className="edit-button"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFaculty(faculty.id);
                      }}
                      className="delete-button"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Fakülte içeriği ayrı sayfaya taşındı */}
      </div>
      {showContactModal && (
        <ContactModal onClose={() => setShowContactModal(false)} />
      )}
      {showProgramAdminModal && (
        <ProgramAdminModal
          onClose={() => {
            setShowProgramAdminModal(false);
            setNewProgramAdmin({ email: '', password: '', program_ids: [] });
          }}
          newProgramAdmin={newProgramAdmin}
          setNewProgramAdmin={setNewProgramAdmin}
          handleCreateProgramAdmin={handleCreateProgramAdmin}
          allPrograms={allPrograms}
        />
      )}
    </div>
  );
}

// EducationTypesPage bileşeni
function EducationTypesPage({ educationTypes, onSelect }) {
  const navigate = useNavigate();
  const slugify = (text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };
  // Veri yükleme mantığı üstteki route wrapper'a taşındı

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
        </div>
      </div>
      <div className="main-content">
        <div className="faculties-section">
          <div className="section-header">
            <button className="back-button" onClick={() => navigate(-1)}>← Geri</button>
            <h2>Eğitim Türü Seçin</h2>
          </div>
          <div className="faculties-list">
            {Array.isArray(educationTypes) && educationTypes.length > 0 ? (
              educationTypes.map((type) => (
                <div
                  key={type.id}
                  className="faculty-item"
                  onClick={() => {
                    onSelect(String(type.id));
                    try { window.sessionStorage.removeItem('selectedProgram'); } catch (e) { }
                    navigate(`/education/${slugify(type.name)}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="faculty-name">{type.name}</span>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>Eğitim türü bulunamadı.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// CourseDetailsWrapper bileşeni
function CourseDetailsWrapper({ userType, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug, courseSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  let programId = state && state.programId ? state.programId : null;
  let courseCode = state && state.courseCode ? state.courseCode : null;
  let electiveLessonId = state && state.electiveLessonId ? state.electiveLessonId : null;
  let isElectiveLesson = state && state.isElectiveLesson ? state.isElectiveLesson : false;

  if (!programId) {
    try {
      const cached = window.sessionStorage.getItem('selectedProgram');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.id) programId = parsed.id;
      }
    } catch (e) { }
  }
  if (!courseCode) {
    try {
      const c = window.sessionStorage.getItem('selectedCourseCode');
      if (c) {
        courseCode = c;
        // Eğer elective- ile başlıyorsa elective lesson'dır
        if (c.startsWith('elective-')) {
          isElectiveLesson = true;
          electiveLessonId = parseInt(c.replace('elective-', ''));
        }
      }
    } catch (e) { }
  }
  if (!electiveLessonId && isElectiveLesson) {
    try {
      const elId = window.sessionStorage.getItem('selectedElectiveLessonId');
      if (elId) electiveLessonId = parseInt(elId);
    } catch (e) { }
  }

  // electiveLessonId'yi sayıya çevir
  if (electiveLessonId && typeof electiveLessonId !== 'number') {
    electiveLessonId = parseInt(electiveLessonId);
  }

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center">
          </div>
          {(isLoggedIn && handleLogout) && (
            <div className="header-right">
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => navigate('/admin/course-upload')}
                  className="logout-button"
                  style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                >
                  Excel Yükle (Ders)
                </button>
                <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="main-content">
        <CourseDetails
          programId={programId ? parseInt(programId) : null}
          courseCode={courseCode}
          userType={userType}
          electiveLessonId={electiveLessonId ? parseInt(electiveLessonId) : null}
          isElectiveLesson={isElectiveLesson}
        />
      </div>
    </div>
  );
}

// FacultyPage bileşeni
function FacultyPage({ userType, isLoggedIn, handleLogout }) {
  const { facultySlug } = useParams();
  const navigate = useNavigate();
  const location = require('react-router-dom').useLocation();
  const passedFaculty = location && location.state && location.state.faculty ? location.state.faculty : null;
  const faculty = passedFaculty || { id: 0, name: facultySlug };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          <div className="header-right">
            {(isLoggedIn && handleLogout) && (
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="main-content">
        <Programs faculty={faculty} userType={userType} />
      </div>
    </div>
  );
}

// Global Syllabus Status Page (system admin and rector roles)
function GlobalSyllabusStatusPage({ adminToken, handleLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [programSearch, setProgramSearch] = React.useState('');
  const isUniversityAdminSession = Boolean(readAuthItem('universityAdminToken'));

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = adminToken || readAuthItem('adminToken') || '';
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const res = await fetch(`${API_BASE_URL}/api/admin/syllabus-status`, { headers });
        const json = await res.json();
        if (!res.ok) {
          console.error('Global syllabus status error:', json);
          setData(null);
        } else {
          setData(json);
        }
      } catch (e) {
        console.error('Global syllabus status fetch error:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminToken]);

  React.useEffect(() => {
    if (!loading && location.hash === '#programlar') {
      window.requestAnimationFrame(() => {
        document.getElementById('programlar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading, location.hash, location.search]);

  if (loading) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="main-content">
          <div style={{ textAlign: 'center', padding: 50 }}>Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="main-content">
          <div style={{ textAlign: 'center', padding: 50 }}>Veri yüklenemedi</div>
        </div>
      </div>
    );
  }

  const programs = Array.isArray(data.programs) ? data.programs : [];
  const showIncompleteOnly = new URLSearchParams(location.search).get('filter') === 'incomplete';
  const incompleteProgramsCount = programs.filter((program) => Number(program.average_completion || 0) < 100).length;
  const formatCompletion = (value) => Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
  const normalizedProgramSearch = normalizeProgramSearchText(programSearch);
  const displayedPrograms = programs.filter((program) => {
    const matchesStatus = !showIncompleteOnly || Number(program.average_completion || 0) < 100;
    const matchesSearch = !normalizedProgramSearch
      || normalizeProgramSearchText(program.program_name).includes(normalizedProgramSearch);
    return matchesStatus && matchesSearch;
  });

  // Slug oluşturma fonksiyonu
  const slugify = (text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Program tıklama handler'ı
  const handleProgramClick = (program) => {
    const programSlug = slugify(program.program_name);
    navigate(`/program/${programSlug}/syllabus-status`);
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isUniversityAdminSession && (
              <>
                <span className="ua-role-badge">{getStoredUniversityRoleLabel()}</span>
                <button onClick={() => navigate('/university-admin/dashboard')} className="ua-header-button">Yönetim Paneli</button>
              </>
            )}
            {!isUniversityAdminSession && (
              <button
                onClick={() => navigate('/admin/course-upload')}
                className="logout-button"
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                Excel Yükle (Ders)
              </button>
            )}
            {handleLogout && (
              <button onClick={() => { handleLogout(); }} className="logout-button">Çıkış Yap</button>
            )}
          </div>
        </div>
      </div>

      <div className="main-content rector-dashboard-main">
        <section className="global-syllabus-page" aria-labelledby="global-syllabus-title">
          <div className="global-syllabus-back-row">
            <button
              onClick={() => navigate(isUniversityAdminSession ? '/university-admin/dashboard' : '/admin')}
              className="dean-dashboard-back"
            >
              ← Yönetim Paneli
            </button>
          </div>

          <div className="dean-dashboard-title global-syllabus-title">
            <div>
              <span className="dean-dashboard-eyebrow">REKTÖRLÜK RAPORU</span>
              <h1 id="global-syllabus-title">Üniversite Geneli İzlence Durumu</h1>
              <p>Ders izlencelerinin tamamlanma oranlarını program bazında izleyin ve eksik kayıtlara ulaşın.</p>
            </div>
            <div className="dean-dashboard-count">
              <strong>{programs.length}</strong>
              <span>Bölüm / Program</span>
            </div>
          </div>

          <div className="global-syllabus-summary" aria-label="İzlence özeti">
            <article>
              <span>Genel Ortalama</span>
              <strong>%{formatCompletion(data.overall_average_completion)}</strong>
              <small>Üniversite geneli doluluk</small>
            </article>
            <article>
              <span>Tüm Programlar</span>
              <strong>{programs.length}</strong>
              <small>Raporlanan program</small>
            </article>
            <article className="warning">
              <span>Eksik İzlence</span>
              <strong>{incompleteProgramsCount}</strong>
              <small>%100'ün altındaki program</small>
            </article>
          </div>

          <div className="global-syllabus-toolbar">
            <label className="dean-program-search global-syllabus-search">
              <span className="sr-only">Program ara</span>
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
              </svg>
              <input
                type="search"
                value={programSearch}
                onChange={(event) => setProgramSearch(event.target.value)}
                placeholder="Program adı ara..."
                autoComplete="off"
              />
              {programSearch && (
                <button type="button" className="dean-program-search__clear" onClick={() => setProgramSearch('')} aria-label="Aramayı temizle">×</button>
              )}
            </label>
            <div className="global-syllabus-view-switch" aria-label="İzlence raporu filtresi">
              <button
                type="button"
                className={!showIncompleteOnly ? 'active' : ''}
                onClick={() => navigate('/admin/syllabus-status#programlar')}
              >
                Tüm Programlar
              </button>
              <button
                type="button"
                className={showIncompleteOnly ? 'active' : ''}
                onClick={() => navigate('/admin/syllabus-status?filter=incomplete#programlar')}
              >
                Eksik İzlenceler
              </button>
            </div>
          </div>

          <div id="programlar" className="syllabus-table-container global-syllabus-programs">
            <div className="global-syllabus-table-heading">
              <div>
                <span>{showIncompleteOnly ? 'EKSİK KAYITLAR' : 'PROGRAM BAZINDA'}</span>
                <h2>{showIncompleteOnly ? 'Eksik İzlencesi Bulunan Programlar' : 'Program İzlence Durumları'}</h2>
              </div>
              <strong>{displayedPrograms.length} program</strong>
            </div>
            <table className="syllabus-table">
              <thead>
                <tr>
                  <th>Program</th>
                  <th>Toplam Ders</th>
                  <th>Ortalama İzlence Doluluk (%)</th>
                </tr>
              </thead>
              <tbody>
                {displayedPrograms.map((p) => (
                  <tr key={p.program_id}>
                    <td>
                      <button type="button" className="global-syllabus-program-link" onClick={() => handleProgramClick(p)}>
                        {p.program_name}
                      </button>
                    </td>
                    <td>{p.total_courses}</td>
                    <td>
                      <div className="global-syllabus-completion">
                        <span className={`global-syllabus-status ${Number(p.average_completion || 0) === 100 ? 'complete' : Number(p.average_completion || 0) === 0 ? 'empty' : 'partial'}`}>
                          %{formatCompletion(p.average_completion)}
                        </span>
                        <span className="global-syllabus-progress" aria-hidden="true">
                          <span style={{ width: `${Math.max(0, Math.min(100, Number(p.average_completion || 0)))}%` }} />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {displayedPrograms.length === 0 && (
                  <tr>
                    <td colSpan="3" className="global-syllabus-empty">Bu filtrede program bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

// EducationTypeWrapper bileşeni: URL'deki slug'a göre seçimi uygular ve MainPage görünümünü bu URL'de tutar
function EducationTypeWrapper({ educationTypes, setSelectedEducationType, selectedEducationType, faculties, facultiesLoading, setSelectedFaculty, fetchFaculties, Programs, userType, isLoggedIn, handleLogout, showContactModal, setShowContactModal }) {
  const { educationTypeSlug } = useParams();
  const navigate = useNavigate();
  const lastProcessedSlugRef = React.useRef('');

  const slugify = React.useCallback((text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }, []);

  // URL slug'ına göre eğitim tipini bul ve set et
  React.useEffect(() => {
    if (!educationTypeSlug) {
      lastProcessedSlugRef.current = '';
      return;
    }

    // Aynı slug tekrar işleniyorsa atla
    if (lastProcessedSlugRef.current === educationTypeSlug) {
      return;
    }

    // Eğitim tipleri yüklenene kadar bekle
    if (!Array.isArray(educationTypes) || educationTypes.length === 0) {
      return;
    }

    // Slug'a göre eğitim tipini bul
    const match = educationTypes.find(t => {
      const typeSlug = slugify(t.name);
      return typeSlug === educationTypeSlug;
    });

    if (match) {
      const matchId = String(match.id);

      // Slug'ı hemen işaretle (tekrar işlenmesini engellemek için)
      lastProcessedSlugRef.current = educationTypeSlug;

      // Eğitim tipini set et
      if (matchId !== String(selectedEducationType)) {
        setSelectedEducationType(matchId);
        setSelectedFaculty(null);
      }

      // Fakülteleri mutlaka yükle (her zaman, requestId sayesinde race condition olmaz)
      if (fetchFaculties) {
        // State güncellemesinin tamamlanması için kısa bir gecikme
        const timer = setTimeout(() => {
          fetchFaculties(matchId);
        }, 50);
        return () => clearTimeout(timer);
      }
    } else {
      // Slug'a eşleşen eğitim tipi bulunamadı
      console.warn(`Education type not found for slug: ${educationTypeSlug}`);
      // Ana sayfaya yönlendir
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }
  }, [educationTypes, educationTypeSlug, slugify, setSelectedEducationType, setSelectedFaculty, navigate, fetchFaculties]);

  // URL değiştiğinde lastProcessedSlugRef'i sıfırla
  React.useEffect(() => {
    lastProcessedSlugRef.current = '';
  }, [educationTypeSlug]);

  // selectedEducationType ve URL slug'ı eşleştiğinde fakülteleri yükle
  React.useEffect(() => {
    if (!educationTypeSlug || !selectedEducationType || !fetchFaculties) return;
    if (!Array.isArray(educationTypes) || educationTypes.length === 0) return;

    // Slug'a göre eğitim tipini bul
    const match = educationTypes.find(t => {
      const typeSlug = slugify(t.name);
      return typeSlug === educationTypeSlug;
    });

    if (match && String(match.id) === String(selectedEducationType)) {
      // Eğitim tipi eşleşiyor ama fakülteler yüklenmemişse yükle
      if ((!faculties || faculties.length === 0) && !facultiesLoading) {
        fetchFaculties(selectedEducationType);
      }
    }
  }, [selectedEducationType, educationTypeSlug, educationTypes, slugify, fetchFaculties, faculties, facultiesLoading]);

  // Eğitim tipleri yüklenene kadar loading göster
  if (!Array.isArray(educationTypes) || educationTypes.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '18px', marginBottom: '10px' }}>Yükleniyor...</div>
      </div>
    );
  }

  // MainPage görünümünü URL değiştirmeden burada sunuyoruz
  return (
    <MainPage
      educationTypes={educationTypes}
      selectedEducationType={selectedEducationType}
      setSelectedEducationType={setSelectedEducationType}
      faculties={faculties}
      facultiesLoading={facultiesLoading}
      selectedFaculty={null}
      setSelectedFaculty={setSelectedFaculty}
      Programs={Programs}
      userType={userType}
      isLoggedIn={isLoggedIn}
      handleLogout={handleLogout}
      showContactModal={showContactModal}
      setShowContactModal={setShowContactModal}
    />
  );
}

// Program başkanı sol menüsü için sade çizgi ikonları (currentColor ile renk alır)
function PaNavIcon({ name }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'list':
      return (<svg {...common}><line x1="8" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="8" y1="18" x2="20" y2="18" /><line x1="4" y1="6" x2="4.01" y2="6" /><line x1="4" y1="12" x2="4.01" y2="12" /><line x1="4" y1="18" x2="4.01" y2="18" /></svg>);
    case 'chart':
      return (<svg {...common}><line x1="4" y1="20" x2="20" y2="20" /><rect x="6" y="11" width="3" height="6" rx="0.5" /><rect x="11" y="7" width="3" height="10" rx="0.5" /><rect x="16" y="13" width="3" height="4" rx="0.5" /></svg>);
    case 'grid':
      return (<svg {...common}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>);
    case 'doc':
      return (<svg {...common}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></svg>);
    case 'layers':
      return (<svg {...common}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>);
    case 'target':
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></svg>);
    case 'settings':
      return (<svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>);
    default:
      return null;
  }
}

function ProgramAdminSidebar({ programId, programName, programSlug, activeKey, navigate }) {
  const navItems = [
    { key: 'dersler', label: 'Program Dersleri', icon: 'list', onClick: () => navigate(`/program/${programSlug}`, { state: { programId, programName, program: { id: programId, name: programName } } }) },
    { key: 'electives', label: 'Seçmeli Dersler', icon: 'layers', onClick: () => navigate(`/program/${programSlug}/secmeli-dersler?programId=${programId}`, { state: { programId, programName, program: { id: programId, name: programName } } }) },
    { key: 'syllabus', label: 'İzlence Doluluk Durumu', icon: 'chart', onClick: () => navigate(`/program/${programSlug}/syllabus-status`) },
    { key: 'ciktilar', label: 'Program Çıktıları', icon: 'target', onClick: () => navigate(`/program/${programSlug}/program-ciktilari-olustur`, { state: { program: { id: programId, name: programName }, programId } }) },
    { key: 'pc-degisiklikleri', label: 'PÇ Değişiklik Kanıtı', icon: 'doc', onClick: () => navigate(`/program/${programSlug}/pc-degisiklikleri?programId=${programId}`, { state: { program: { id: programId, name: programName }, programId } }) },
    { key: 'ders-plani-degisiklikleri', label: 'Ders Planı Değişiklik Kanıtı', icon: 'doc', onClick: () => navigate(`/program/${programSlug}/ders-plani-degisiklikleri?programId=${programId}`, { state: { program: { id: programId, name: programName }, programId } }) },
    { key: 'plan', label: 'Ders Planı Oluştur', icon: 'grid', onClick: () => navigate(`/program/${programSlug}/ders-plani-olustur`, { state: { program: { id: programId, name: programName }, programId } }) },
    { key: 'divider' },
    { key: 'sidebar', label: 'Program Bilgilerini Düzenle', icon: 'settings', onClick: () => navigate(`/program/sidebar?programId=${programId}`) },
  ];

  return (
    <aside className="pa-sidebar">
      <div className="pa-sidebar-head">
        <div className="pa-sidebar-avatar">{(programName || 'P').trim().charAt(0).toUpperCase()}</div>
        <div className="pa-sidebar-titles">
          <div className="pa-sidebar-name" title={programName}>{programName}</div>
          <div className="pa-sidebar-sub">Program Yönetimi</div>
        </div>
      </div>
      <nav className="pa-sidebar-nav">
        {navItems.map((item) => (
          item.key === 'divider' ? (
            <div key="divider" className="pa-sidebar-divider" />
          ) : (
            <button
              key={item.key}
              onClick={item.onClick}
              className={`pa-nav-item${item.key === activeKey ? ' active' : ''}`}
            >
              <span className="pa-nav-icon"><PaNavIcon name={item.icon} /></span>
              <span className="pa-nav-label">{item.label}</span>
            </button>
          )
        ))}
      </nav>
    </aside>
  );
}

function ProgramManagementHeaderActions({ userType, programName, navigate, handleLogout }) {
  const showUniversityManagement = (
    userType === 'universityAdmin' || hasStoredUniversityManagementRole()
  );
  const roleLabel = showUniversityManagement
    ? getStoredUniversityRoleLabel()
    : (userType === 'programAdmin' ? 'Program Başkanı' : '');

  return (
    <div className="header-right program-management-actions">
      {showUniversityManagement && (
        <button
          type="button"
          className="management-header-button"
          aria-label={`${roleLabel || 'Üniversite'} yönetim paneline dön`}
          onClick={() => navigate('/university-admin/dashboard')}
        >
          Yönetim Paneli
        </button>
      )}
      {programName && programName !== 'Program' && (
        <div className="management-program-context" title={programName}>
          <span>Yönetilen Program</span>
          <strong>{programName}</strong>
        </div>
      )}
      {roleLabel && (
        <span className="management-role-badge">{roleLabel}</span>
      )}
      {handleLogout && (
        <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
      )}
    </div>
  );
}

// ProgramPage bileşeni
function ProgramPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  const programIdFromUrl = (() => {
    try { const v = new URLSearchParams(location.search || '').get('programId'); return v ? parseInt(v, 10) : null; } catch (e) { return null; }
  })();
  const programId = (state && state.program && state.program.id) ? state.program.id : ((state && state.programId) ? state.programId : programIdFromUrl);
  const canManageProgram = isProgramManagementUser(userType);
  const programManagementToken = programAdminToken || adminToken;
  const [programName, setProgramName] = React.useState(
    (state && state.program && state.program.name) ? state.program.name : ((state && state.programName) ? state.programName : 'Program')
  );
  // Program adı state ile gelmediyse (ör. login sonrası/yenilemede) programId ile getir
  React.useEffect(() => {
    if (!programId || (programName && programName !== 'Program')) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs`);
        const programs = await res.json();
        const p = Array.isArray(programs) ? programs.find(x => x.id === programId) : null;
        if (p && p.name) setProgramName(p.name);
      } catch (e) { /* isim alınamadı */ }
    })();
  }, [programId, programName]);
  const [courses, setCourses] = React.useState([]);
  const [wordUploadStatus, setWordUploadStatus] = React.useState({});
  const [deleteStatus, setDeleteStatus] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [electiveGroupsPage, setElectiveGroupsPage] = React.useState([]);
  const [expandedElectiveIdsPage, setExpandedElectiveIdsPage] = React.useState([]);
  const [electiveLessonsFlat, setElectiveLessonsFlat] = React.useState([]);
  const [unpooledElectiveLessons, setUnpooledElectiveLessons] = React.useState([]);
  const [savingCourse, setSavingCourse] = React.useState(false);
  const [courseFormError, setCourseFormError] = React.useState('');
  const [showAddCourseModal, setShowAddCourseModal] = React.useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = React.useState(() => {
    try {
      return new URLSearchParams(location.search).get('duplicates') === '1';
    } catch (e) {
      return false;
    }
  });
  const [newCourse, setNewCourse] = React.useState({
    donem: '',
    course_code: '',
    course_name: '',
    course_type: '',
    teorik: '',
    uygulama: '',
    laboratuvar: '',
    kredi: '',
    akts: '',
  });

  const fetchCourses = React.useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = userType === 'admin' || canManageProgram
        ? `${API_BASE_URL}/api/programs`
        : `${API_BASE_URL}/api/public/programs`;
      if (!programId) {
        setLoading(false);
        return;
      }
      const headers = {};
      if (canManageProgram && programManagementToken) {
        headers.Authorization = `Bearer ${programManagementToken}`;
      }
      const res = await fetch(`${endpoint}/${programId}/courses`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCourses(data);
      }
    } catch (e) {
      console.error('Dersler getirilemedi', e);
    } finally {
      setLoading(false);
    }
  }, [programId, userType, canManageProgram, programManagementToken]);

  React.useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const slugify = (text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleCourseClick = (course) => {
    try { window.sessionStorage.setItem('selectedCourseCode', course.course_code); } catch (e) { }
    navigate(`/course/${programSlug}/${slugify(course.course_name || course.course_code)}`, { state: { programId, courseCode: course.course_code } });
  };

  const handleWordUpload = async (e, course) => {
    e.stopPropagation();
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Lütfen .docx seçin' }));
      return;
    }
    try {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yükleniyor...' }));
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${course.course_code}/upload-word`, { method: 'POST', body: form });
      const json = await res.json();
      if (res.ok) {
        setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yüklendi' }));
        e.target.value = '';
      } else {
        setWordUploadStatus(prev => ({ ...prev, [course.course_code]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen'}` }));
      }
    } catch (err) {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yükleme hatası' }));
    }
  };

  const handleElectiveLessonWordUpload = async (e, lesson) => {
    e.stopPropagation();
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Lütfen .docx seçin' }));
      return;
    }
    try {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yükleniyor...' }));
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lesson.id}/upload-word`, { method: 'POST', body: form });
      const json = await res.json();
      if (res.ok) {
        setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yüklendi' }));
        e.target.value = '';
      } else {
        setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen'}` }));
      }
    } catch (err) {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yükleme hatası' }));
    }
  };

  const handleElectiveLessonDelete = async (e, lesson) => {
    e.stopPropagation();
    if (!window.confirm(`${lesson.course_code} seçmeli dersine ait Word verilerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lesson.id}/delete`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Silindi' }));
      } else {
        setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Silme hatası' }));
    }
  };

  const handleCourseDelete = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`${course.course_code} dersine ait Word verilerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${course.course_code}/delete`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Silindi' }));
      } else {
        setDeleteStatus(prev => ({ ...prev, [course.course_code]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Silme hatası' }));
    }
  };

  const handleCourseFullDelete = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`${course.course_code} - ${course.course_name} dersini veritabanından TAMAMEN silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz! Derse ait tüm veriler (öğrenme çıktıları, haftalık konular, değerlendirme sistemi vb.) silinecektir.`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/admin/programs/${programId}/courses/${course.course_code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchCourses();
      } else {
        setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Silme hatası' }));
    }
  };

  const handleProgramAdminDuplicateDelete = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`${course.course_code} - ${course.course_name} kopya dersini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz! Derse ait tüm veriler (öğrenme çıktıları, haftalık konular, değerlendirme sistemi vb.) silinecektir.`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/program-admin/programs/${programId}/courses/${course.course_code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${programAdminToken}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchCourses();
      } else {
        setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Silme hatası' }));
    }
  };

  React.useEffect(() => {
    const fetchElectives = async () => {
      if (!programId) return;
      const headers = {};
      if (canManageProgram && programManagementToken) {
        headers.Authorization = `Bearer ${programManagementToken}`;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups`, { headers });
        const groups = await res.json();
        if (!res.ok || !Array.isArray(groups)) {
          setElectiveGroupsPage([]);
        } else {
          const enriched = await Promise.all(groups.map(async (g) => {
            try {
              const lr = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${g.id}/lessons`, { headers });
              const lessons = await lr.json();
              if (lr.ok && Array.isArray(lessons)) return { ...g, lessons };
            } catch (e) { }
            return { ...g, lessons: [] };
          }));
          setElectiveGroupsPage(enriched);
        }
      } catch (e) {
        setElectiveGroupsPage([]);
      }

      // Havuzu silinmiş/hiç havuza atanmamış (course_id=NULL) seçmeli dersler:
      // grup bazlı liste bunları döndürmediği için ayrıca çekilir.
      try {
        const unpooledRes = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/unpooled`, { headers });
        const unpooled = await unpooledRes.json();
        setUnpooledElectiveLessons(unpooledRes.ok && Array.isArray(unpooled) ? unpooled : []);
      } catch (e) {
        setUnpooledElectiveLessons([]);
      }
    };
    fetchElectives();
  }, [programId, canManageProgram, programManagementToken]);

  const toggleElectiveGroupPage = (id) => {
    setExpandedElectiveIdsPage(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Seçmeli dersleri düz liste olarak hazırla
  React.useEffect(() => {
    const flat = [];
    (electiveGroupsPage || []).forEach(g => {
      (g.lessons || []).forEach(ls => flat.push({ ...ls, donem: g.donem }));
    });
    setElectiveLessonsFlat(flat);
  }, [electiveGroupsPage]);

  const handleAddCourse = async (e) => {
    e.preventDefault();
    setCourseFormError('');
    if (!programId) return;
    const required = ['donem', 'course_code', 'course_name'];
    for (const key of required) {
      if (!newCourse[key] || String(newCourse[key]).trim() === '') {
        setCourseFormError('Dönem, ders kodu ve ders adı zorunlu');
        return;
      }
    }
    setSavingCourse(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (canManageProgram && programManagementToken) {
        headers.Authorization = `Bearer ${programManagementToken}`;
      }
      const body = {
        ...newCourse,
        donem: parseInt(newCourse.donem, 10),
        teorik: newCourse.teorik === '' ? 0 : Number(newCourse.teorik),
        uygulama: newCourse.uygulama === '' ? 0 : Number(newCourse.uygulama),
        laboratuvar: newCourse.laboratuvar === '' ? 0 : Number(newCourse.laboratuvar),
        kredi: newCourse.kredi === '' ? 0 : Number(newCourse.kredi),
        akts: newCourse.akts === '' ? 0 : Number(newCourse.akts),
      };
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCourseFormError(json && json.error ? json.error : 'Ders eklenemedi');
        return;
      }
      setNewCourse({
        donem: '',
        course_code: '',
        course_name: '',
        course_type: '',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: '',
      });
      fetchCourses();
    } catch (err) {
      setCourseFormError('Ders eklenemedi');
    } finally {
      setSavingCourse(false);
    }
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {isLoggedIn && (
            <ProgramManagementHeaderActions
              userType={userType}
              programName={programName}
              navigate={navigate}
              handleLogout={handleLogout}
            />
          )}
        </div>
      </div>
      <div className="main-content curriculum-plan-layout">
        {canManageProgram && (
          <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="dersler" navigate={navigate} />
        )}
        <div className="programs-section" style={{ position: 'relative', flex: canManageProgram ? 1 : undefined, minWidth: 0 }}>
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!canManageProgram && (
                <button className="back-button" onClick={() => navigate(-1)}>← Geri</button>
              )}
              <h2 style={{ margin: 0 }}>{programName} - Program Dersleri</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {userType === 'programAdmin' && (
                <button
                  onClick={() => setShowDuplicatesModal(true)}
                  className="button"
                  style={{ backgroundColor: '#f59e0b', color: 'white' }}
                >
                  Aynı İsimli Dersler
                </button>
              )}
              {userType === 'admin' && (
                <button
                  onClick={() => navigate(`/program/${programSlug}/syllabus-status`)}
                  className="button"
                  style={{ backgroundColor: '#3b82f6', color: 'white' }}
                >
                  İzlence Doluluk Durumu
                </button>
              )}
            </div>
          </div>
          {loading ? (
            <div className="loading-courses">Dersler yükleniyor...</div>
          ) : courses.length > 0 ? (
            <div className="courses-table-container">
              {Object.entries(
                courses.reduce((acc, course) => {
                  const termKey = course.donem;
                  if (!acc[termKey]) acc[termKey] = [];
                  acc[termKey].push(course);
                  return acc;
                }, {})
              )
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([term, termCourses]) => (
                  <div key={term} style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '8px 0' }}>{term}. Dönem</h4>
                    <table className="courses-table">
                      <thead>
                        <tr>
                          <th>Ders Kodu</th>
                          <th>Ders Adı</th>
                          <th>Ders Tipi</th>
                          <th>Teorik</th>
                          <th>Uygulama</th>
                          <th>Laboratuar</th>
                          <th>Kredi</th>
                          <th>AKTS</th>
                          {userType === 'admin' ? (
                            <>
                              <th>Word Yükle</th>
                              <th>Word Sil</th>
                              <th>Dersi Sil</th>
                            </>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {termCourses
                          .filter(c => String(c.course_type || '').toLowerCase() !== 'seçmeli')
                          .map((course, index) => (
                            <tr
                              key={`${course.id}-${index}`}
                              onClick={() => handleCourseClick(course)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{course.course_code}</td>
                              <td>{course.course_name}</td>
                              <td>{course.course_type || '-'}</td>
                              <td>{course.teorik}</td>
                              <td>{course.uygulama}</td>
                              <td>{course.laboratuvar}</td>
                              <td>{course.kredi}</td>
                              <td>{course.akts}</td>
                              {userType === 'admin' ? (
                                <>
                                  <td onClick={(ev) => ev.stopPropagation()}>
                                    <input type="file" accept=".docx" onChange={(ev) => handleWordUpload(ev, course)} />
                                    {wordUploadStatus[course.course_code] ? (
                                      <div style={{ fontSize: 12, marginTop: 4 }}>{wordUploadStatus[course.course_code]}</div>
                                    ) : null}
                                  </td>
                                  <td onClick={(ev) => ev.stopPropagation()}>
                                    <button
                                      type="button"
                                      className="delete-button"
                                      onClick={(ev) => handleCourseDelete(ev, course)}
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                    >
                                      Sil
                                    </button>
                                    {deleteStatus[course.course_code] ? (
                                      <div style={{ fontSize: 12, marginTop: 4 }}>{deleteStatus[course.course_code]}</div>
                                    ) : null}
                                  </td>
                                  <td onClick={(ev) => ev.stopPropagation()}>
                                    <button
                                      type="button"
                                      onClick={(ev) => handleCourseFullDelete(ev, course)}
                                      style={{ padding: '6px 12px', fontSize: 12, backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                      Dersi Sil
                                    </button>
                                    {deleteStatus[`full-${course.course_code}`] ? (
                                      <div style={{ fontSize: 12, marginTop: 4, color: '#dc2626' }}>{deleteStatus[`full-${course.course_code}`]}</div>
                                    ) : null}
                                  </td>
                                </>
                              ) : null}
                            </tr>
                          ))}
                        {/* Seçmeli grup + alt dersleri aynı dönemde göster (tıklayınca açılır) */}
                        {(electiveGroupsPage || [])
                          .filter(g => String(g.donem) === String(term))
                          .map((g) => {
                            const expanded = expandedElectiveIdsPage.includes(g.id);
                            return (
                              <React.Fragment key={`egrp-${g.id}`}>
                                <tr
                                  style={{ background: '#f9fafb', fontWeight: 600, cursor: 'pointer' }}
                                  onClick={() => toggleElectiveGroupPage(g.id)}
                                >
                                  <td>{expanded ? '▼' : '▶'} {g.course_code || g.group_name}</td>
                                  <td>{g.group_name}</td>
                                  <td>Seçmeli</td>
                                  <td>{g.teorik != null ? g.teorik : '-'}</td>
                                  <td>{g.uygulama != null ? g.uygulama : '-'}</td>
                                  <td>{g.laboratuvar != null ? g.laboratuvar : '-'}</td>
                                  <td>{g.kredi != null ? g.kredi : '-'}</td>
                                  <td>{g.akts != null ? g.akts : '-'}</td>
                                  {userType === 'admin' ? (
                                    <>
                                      <td />
                                      <td />
                                      <td />
                                    </>
                                  ) : null}
                                </tr>
                                {expanded && (g.lessons || []).map((ls) => (
                                  <tr
                                    key={`els-${g.id}-${ls.id}`}
                                    style={{ background: '#fdfdfd', cursor: 'pointer' }}
                                    onClick={() => {
                                      try {
                                        window.sessionStorage.setItem('selectedCourseCode', `elective-${ls.id}`);
                                        window.sessionStorage.setItem('selectedElectiveLessonId', ls.id);
                                      } catch (e) { }
                                      navigate(`/course/${programSlug}/${slugify(ls.course_name || ls.course_code)}`, {
                                        state: {
                                          programId,
                                          courseCode: `elective-${ls.id}`,
                                          electiveLessonId: ls.id,
                                          isElectiveLesson: true
                                        }
                                      });
                                    }}
                                  >
                                    <td>{ls.course_code}</td>
                                    <td>{ls.course_name}</td>
                                    <td>{ls.course_type || 'Seçmeli'}</td>
                                    <td>{ls.teorik}</td>
                                    <td>{ls.uygulama}</td>
                                    <td>{ls.laboratuvar}</td>
                                    <td>{ls.kredi}</td>
                                    <td>{ls.akts}</td>
                                    {userType === 'admin' ? (
                                      <>
                                        <td onClick={(ev) => ev.stopPropagation()}>
                                          <input type="file" accept=".docx" onChange={(ev) => handleElectiveLessonWordUpload(ev, ls)} />
                                          {wordUploadStatus[`elective-${ls.id}`] ? (
                                            <div style={{ fontSize: 12, marginTop: 4 }}>{wordUploadStatus[`elective-${ls.id}`]}</div>
                                          ) : null}
                                        </td>
                                        <td onClick={(ev) => ev.stopPropagation()}>
                                          <button
                                            type="button"
                                            className="delete-button"
                                            onClick={(ev) => handleElectiveLessonDelete(ev, ls)}
                                            style={{ padding: '6px 12px', fontSize: 12 }}
                                          >
                                            Sil
                                          </button>
                                          {deleteStatus[`elective-${ls.id}`] ? (
                                            <div style={{ fontSize: 12, marginTop: 4 }}>{deleteStatus[`elective-${ls.id}`]}</div>
                                          ) : null}
                                        </td>
                                        <td />
                                      </>
                                    ) : null}
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                      </tbody>
                    </table>
                    {(unpooledElectiveLessons || []).filter(ls => String(ls.donem) === String(term)).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <h5 style={{ margin: '8px 0', color: '#6b7280' }}>Havuzsuz Seçmeli Dersler</h5>
                        <table className="courses-table">
                          <thead>
                            <tr>
                              <th>Ders Kodu</th>
                              <th>Ders Adı</th>
                              <th>Ders Tipi</th>
                              <th>Teorik</th>
                              <th>Uygulama</th>
                              <th>Laboratuar</th>
                              <th>Kredi</th>
                              <th>AKTS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unpooledElectiveLessons
                              .filter(ls => String(ls.donem) === String(term))
                              .map((ls) => (
                                <tr
                                  key={`unpooled-${ls.id}`}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    try {
                                      window.sessionStorage.setItem('selectedCourseCode', `elective-${ls.id}`);
                                      window.sessionStorage.setItem('selectedElectiveLessonId', ls.id);
                                    } catch (e) { }
                                    navigate(`/course/${programSlug}/${slugify(ls.course_name || ls.course_code)}`, {
                                      state: {
                                        programId,
                                        courseCode: `elective-${ls.id}`,
                                        electiveLessonId: ls.id,
                                        isElectiveLesson: true
                                      }
                                    });
                                  }}
                                >
                                  <td>{ls.course_code}</td>
                                  <td>{ls.course_name}</td>
                                  <td>{ls.course_type || 'Seçmeli'}</td>
                                  <td>{ls.teorik}</td>
                                  <td>{ls.uygulama}</td>
                                  <td>{ls.laboratuvar}</td>
                                  <td>{ls.kredi}</td>
                                  <td>{ls.akts}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Bu program için henüz ders yüklenmemiş.</p>
            </div>
          )}
        </div>
      </div>
      {userType === 'programAdmin' && showDuplicatesModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, width: '90%', maxWidth: 900, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Aynı İsimli Dersler</h3>
              <button className="back-button" onClick={() => setShowDuplicatesModal(false)}>Kapat</button>
            </div>
            {(() => {
              const grouped = {};
              courses.forEach(c => {
                const key = (c.course_name || '').trim();
                if (!key) return;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(c);
              });
              const duplicates = Object.entries(grouped)
                .filter(([, list]) => list.length > 1)
                .sort(([a], [b]) => a.localeCompare(b, 'tr'));
              if (duplicates.length === 0) {
                return <p style={{ color: '#6b7280' }}>Bu programda aynı isimde kopya ders bulunmuyor.</p>;
              }
              return (
                <div>
                  <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
                    Aynı isimde birden fazla ders bulunuyor. Silinecek olan kaydı manuel olarak seçin.
                  </p>
                  {duplicates.map(([name, list]) => (
                    <div key={name} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>{name} <span style={{ color: '#6b7280', fontWeight: 400 }}>({list.length} kayıt)</span></div>
                      <table className="courses-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Ders Kodu</th>
                            <th>Dönem</th>
                            <th>Ders Tipi</th>
                            <th>Teorik</th>
                            <th>Uygulama</th>
                            <th>Laboratuar</th>
                            <th>Kredi</th>
                            <th>AKTS</th>
                            <th>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map(c => (
                            <tr key={c.id}>
                              <td>{c.course_code}</td>
                              <td>{c.donem}</td>
                              <td>{c.course_type || '-'}</td>
                              <td>{c.teorik}</td>
                              <td>{c.uygulama}</td>
                              <td>{c.laboratuvar}</td>
                              <td>{c.kredi}</td>
                              <td>{c.akts}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={(ev) => handleProgramAdminDuplicateDelete(ev, c)}
                                  style={{ padding: '6px 12px', fontSize: 12, backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >
                                  Sil
                                </button>
                                {deleteStatus[`full-${c.course_code}`] ? (
                                  <div style={{ fontSize: 12, marginTop: 4, color: '#dc2626' }}>{deleteStatus[`full-${c.course_code}`]}</div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      ) : null}
      {userType === 'admin' && showAddCourseModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, width: '90%', maxWidth: 900, boxShadow: '0 10px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Yeni Ders Ekle</h3>
              <button className="back-button" onClick={() => setShowAddCourseModal(false)}>Kapat</button>
            </div>
            <form onSubmit={handleAddCourse}>
              <div className="course-add-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
                <input type="number" placeholder="Dönem" value={newCourse.donem} onChange={(e) => setNewCourse(prev => ({ ...prev, donem: e.target.value }))} required />
                <input type="text" placeholder="Ders Kodu" value={newCourse.course_code} onChange={(e) => setNewCourse(prev => ({ ...prev, course_code: e.target.value }))} required />
                <input type="text" placeholder="Ders Adı" value={newCourse.course_name} onChange={(e) => setNewCourse(prev => ({ ...prev, course_name: e.target.value }))} required />
                <input type="text" placeholder="Ders Tipi" value={newCourse.course_type} onChange={(e) => setNewCourse(prev => ({ ...prev, course_type: e.target.value }))} />
                <input type="number" placeholder="Teorik" value={newCourse.teorik} onChange={(e) => setNewCourse(prev => ({ ...prev, teorik: e.target.value }))} />
                <input type="number" placeholder="Uygulama" value={newCourse.uygulama} onChange={(e) => setNewCourse(prev => ({ ...prev, uygulama: e.target.value }))} />
                <input type="number" placeholder="Laboratuvar" value={newCourse.laboratuvar} onChange={(e) => setNewCourse(prev => ({ ...prev, laboratuvar: e.target.value }))} />
                <input type="number" step="0.5" placeholder="Kredi" value={newCourse.kredi} onChange={(e) => setNewCourse(prev => ({ ...prev, kredi: e.target.value }))} />
                <input type="number" step="0.5" placeholder="AKTS" value={newCourse.akts} onChange={(e) => setNewCourse(prev => ({ ...prev, akts: e.target.value }))} />
              </div>
              {courseFormError ? <div className="form-error" style={{ marginTop: 10 }}>{courseFormError}</div> : null}
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="back-button" onClick={() => setShowAddCourseModal(false)}>Vazgeç</button>
                <button type="submit" className="back-button" style={{ backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb' }} disabled={savingCourse}>
                  {savingCourse ? 'Kaydediliyor...' : 'Ders Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DersPlaniOlusturPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  const programId = state?.program?.id ?? state?.programId ?? null;
  const programName = state?.program?.name ?? state?.programName ?? 'Program';
  const approvedSnapshot = readApprovedProgramOutcomes(programId);
  const approvedProgramOutcomes = approvedSnapshot?.outcomes || [];
  const canManageProgram = isProgramManagementUser(userType);

  useEffect(() => {
    if (programId == null || approvedSnapshot) return;
    navigate(
      `/program/${programSlug}/program-ciktilari-olustur`,
      {
        replace: true,
        state: {
          program: { id: programId, name: programName },
          programId,
          programName,
          approvalRequired: true,
        },
      },
    );
  }, [approvedSnapshot, navigate, programId, programName, programSlug]);

  if (programId != null && !approvedSnapshot) return null;

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {isLoggedIn && (
            <ProgramManagementHeaderActions
              userType={userType}
              programName={programName}
              navigate={navigate}
              handleLogout={handleLogout}
            />
          )}
        </div>
      </div>
      <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
        {canManageProgram && (
          <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="plan" navigate={navigate} />
        )}
        <div className="programs-section curriculum-plan-page" style={{ position: 'relative', flex: canManageProgram ? 1 : undefined, minWidth: 0 }}>
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="back-button" onClick={() => navigate(-1)}>← Geri</button>
            <h2 style={{ margin: 0 }}>{programName} — Ders Planı Oluştur</h2>
          </div>

          <section className="curriculum-plan-page__intro" aria-label="Ders planı oluşturma yöntemi">
            <div className="curriculum-plan-page__intro-content">
              <h3>Ders planı oluşturma ölçütleri</h3>
              <p>
                Yeni plan; güncel program çıktıları, mevcut dersler, programın alanına özgü Türkiye ve dünya eğitim
                esasları ile mesleki yeterlilikler dikkate alınarak hazırlanır. Derslerin dönemlere ve ön koşullara
                dengeli biçimde dağıtılması gözetilir.
              </p>
            </div>
          </section>

          <CurriculumPlanGenerator
            programId={programId}
            userType={userType}
            programAdminToken={programAdminToken}
            adminToken={adminToken}
            initialProgramOutcomes={approvedProgramOutcomes}
            initialExtraInstructions={state?.extraInstructions || ''}
            autoGenerate={Boolean(state?.autoGenerate && approvedProgramOutcomes.length)}
            onGoToOutcomes={() => navigate(
              `/program/${programSlug}/program-ciktilari-olustur`,
              {
                state: {
                  program: { id: programId, name: programName },
                  programId,
                  programName,
                  approvalRequired: true,
                },
              },
            )}
          />
        </div>
      </div>
    </div>
  );
}

function ProgramCiktilariOlusturPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  const programId = state?.program?.id ?? state?.programId ?? null;
  const programName = state?.program?.name ?? state?.programName ?? 'Program';
  const canManageProgram = isProgramManagementUser(userType);

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {isLoggedIn && (
            <ProgramManagementHeaderActions
              userType={userType}
              programName={programName}
              navigate={navigate}
              handleLogout={handleLogout}
            />
          )}
        </div>
      </div>
      <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
        {canManageProgram && (
          <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="ciktilar" navigate={navigate} />
        )}
        <div className="programs-section" style={{ position: 'relative', flex: canManageProgram ? 1 : undefined, minWidth: 0 }}>
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="back-button" onClick={() => navigate(-1)}>← Geri</button>
            <h2 style={{ margin: 0 }}>{programName} — Program Çıktıları</h2>
          </div>

          <ProgramOutcomesGenerator
            programId={programId}
            programName={programName}
            userType={userType}
            programAdminToken={programAdminToken}
            adminToken={adminToken}
            approvalRequired={Boolean(state?.approvalRequired)}
            onContinueToPlan={(_outcomes, planInstructions) => navigate(
              `/program/${programSlug}/ders-plani-olustur`,
              {
                state: {
                  program: { id: programId, name: programName },
                  programId,
                  programName,
                  autoGenerate: true,
                  extraInstructions: planInstructions,
                },
              },
            )}
          />
        </div>
      </div>
    </div>
  );
}

function ProgramOutcomeYearComparisonPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  const programIdFromUrl = new URLSearchParams(location.search || '').get('programId');
  const programId = state?.program?.id ?? state?.programId ?? (programIdFromUrl ? Number(programIdFromUrl) : null);
  const programName = state?.program?.name ?? state?.programName ?? 'Program';
  const canManageProgram = isProgramManagementUser(userType);

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {isLoggedIn && (
            <ProgramManagementHeaderActions
              userType={userType}
              programName={programName}
              navigate={navigate}
              handleLogout={handleLogout}
            />
          )}
        </div>
      </div>
      <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
        {canManageProgram && (
          <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="pc-degisiklikleri" navigate={navigate} />
        )}
        <main className="programs-section" style={{ position: 'relative', flex: canManageProgram ? 1 : undefined, minWidth: 0 }}>
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="back-button no-print" onClick={() => navigate(-1)}>← Geri</button>
            <h2 style={{ margin: 0 }}>{programName} — PÇ Değişiklik Kanıtı</h2>
          </div>
          <ProgramOutcomeYearComparison programId={programId} token={programAdminToken || adminToken} />
        </main>
      </div>
    </div>
  );
}

function CoursePlanYearComparisonPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const navigate = useNavigate();
  const { programSlug } = useParams();
  const location = require('react-router-dom').useLocation();
  const state = location && location.state ? location.state : {};
  const programIdFromUrl = new URLSearchParams(location.search || '').get('programId');
  const programId = state?.program?.id ?? state?.programId ?? (programIdFromUrl ? Number(programIdFromUrl) : null);
  const programName = state?.program?.name ?? state?.programName ?? 'Program';
  const canManageProgram = isProgramManagementUser(userType);

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {isLoggedIn && <ProgramManagementHeaderActions userType={userType} programName={programName} navigate={navigate} handleLogout={handleLogout} />}
        </div>
      </div>
      <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
        {canManageProgram && <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="ders-plani-degisiklikleri" navigate={navigate} />}
        <main className="programs-section" style={{ position: 'relative', flex: canManageProgram ? 1 : undefined, minWidth: 0 }}>
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="back-button no-print" onClick={() => navigate(-1)}>← Geri</button>
            <h2 style={{ margin: 0 }}>{programName} — Ders Planı Değişiklik Kanıtı</h2>
          </div>
          <CoursePlanYearComparison programId={programId} token={programAdminToken || adminToken} />
        </main>
      </div>
    </div>
  );
}

// ProgramInfoPage (public): Sidebar + Section Content
function ProgramInfoPage({ userType, programAdminToken, adminToken, isLoggedIn, handleLogout }) {
  const { programSlug, sectionSlug } = useParams();
  const navigate = useNavigate();
  const location = require('react-router-dom').useLocation();
  const search = new URLSearchParams(location.search || '');
  const state = location && location.state ? location.state : {};
  const canManageProgram = isProgramManagementUser(userType);
  const programManagementToken = programAdminToken || adminToken;
  let program = state && state.program ? state.program : null;
  let programId = program ? program.id : null;
  let programName = program && program.name ? program.name : (state && state.programName ? state.programName : 'Program');
  if (!programId) {
    try {
      const cached = window.sessionStorage.getItem('selectedProgram');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.id) {
          program = parsed;
          programId = parsed.id;
        }
      }
    } catch (e) { }
  }
  // URL query'den pid destekle
  if (!programId) {
    const pidStr = search.get('pid');
    if (pidStr) {
      const pid = parseInt(pidStr, 10);
      if (!Number.isNaN(pid)) {
        programId = pid;
      }
    }
  }

  const [sections, setSections] = React.useState([]);
  const [content, setContent] = React.useState('');
  const [loadingSections, setLoadingSections] = React.useState(true);
  const [loadingContent, setLoadingContent] = React.useState(true);
  const [coursesFallback, setCoursesFallback] = React.useState([]);
  const [loadingCourses, setLoadingCourses] = React.useState(false);
  const [wordUploadStatus, setWordUploadStatus] = React.useState({});
  const [deleteStatus, setDeleteStatus] = React.useState({});
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalHtml, setModalHtml] = React.useState('');
  const programNameForState = program ? program.name : (programName || 'Program');
  const [showAddCourseModal, setShowAddCourseModal] = React.useState(false);
  const [newCourse, setNewCourse] = React.useState({
    donem: '',
    course_code: '',
    course_name: '',
    course_type: '',
    teorik: '',
    uygulama: '',
    laboratuvar: '',
    kredi: '',
    akts: '',
  });
  const [courseFormError, setCourseFormError] = React.useState('');
  const [savingCourse, setSavingCourse] = React.useState(false);
  const [showElectiveModal, setShowElectiveModal] = React.useState(false);
  const [electiveGroups, setElectiveGroups] = React.useState([]);
  const [electiveLoading, setElectiveLoading] = React.useState(false);
  const [electiveError, setElectiveError] = React.useState('');
  const [newElectiveGroup, setNewElectiveGroup] = React.useState({ donem: '', group_name: '', order_index: '' });
  const [newElectiveLesson, setNewElectiveLesson] = React.useState({
    group_id: '',
    course_code: '',
    course_name: '',
    course_type: '',
    teorik: '',
    uygulama: '',
    laboratuvar: '',
    kredi: '',
    akts: ''
  });
  const [showUniversityElectiveModal, setShowUniversityElectiveModal] = React.useState(false);
  const [universityElectiveGroups, setUniversityElectiveGroups] = React.useState([]);
  const [universityElectiveLoading, setUniversityElectiveLoading] = React.useState(false);
  const [universityElectiveError, setUniversityElectiveError] = React.useState('');
  const [newUniversityElectiveGroup, setNewUniversityElectiveGroup] = React.useState({
    donem: '',
    course_code: '',
    course_name: '',
    course_type: 'Üniversite Seçmeli',
    teorik: '',
    uygulama: '',
    laboratuvar: '',
    kredi: '',
    akts: ''
  });
  const [newUniversityElectiveLesson, setNewUniversityElectiveLesson] = React.useState({
    group_id: '',
    course_code: '',
    course_name: '',
    course_type: '',
    teorik: '',
    uygulama: '',
    laboratuvar: '',
    kredi: '',
    akts: ''
  });
  const [expandedElectiveIds, setExpandedElectiveIds] = React.useState([]);
  const fetchCourses = React.useCallback(async () => {
    if (!programId) return;
    setLoadingCourses(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/public/programs/${programId}/courses`);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setCoursesFallback(data); else setCoursesFallback([]);
    } catch (e) { console.error(e); setCoursesFallback([]); }
    finally { setLoadingCourses(false); }
  }, [programId]);

  const electiveHeaders = React.useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (canManageProgram) {
      const token = programManagementToken
        || readAuthItem('programAdminToken')
        || readAuthItem('universityAdminToken');
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [canManageProgram, programManagementToken]);

  const fetchElectiveGroups = React.useCallback(async () => {
    if (!programId) return;
    setElectiveLoading(true);
    setElectiveError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups?course_type=seçmeli`, { headers: electiveHeaders() });
      const groups = await res.json();
      if (!res.ok || !Array.isArray(groups)) {
        setElectiveGroups([]);
        setElectiveError((groups && groups.error) || 'Seçmeli gruplar getirilemedi');
        return;
      }
      const enriched = await Promise.all(groups.map(async (g) => {
        try {
          const lr = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${g.id}/lessons`, { headers: electiveHeaders() });
          const lessons = await lr.json();
          if (lr.ok && Array.isArray(lessons)) {
            return { ...g, lessons };
          }
        } catch (e) { /* ignore */ }
        return { ...g, lessons: [] };
      }));
      setElectiveGroups(enriched);
    } catch (e) {
      setElectiveGroups([]);
      setElectiveError('Seçmeli gruplar getirilemedi');
    } finally {
      setElectiveLoading(false);
    }
  }, [programId, electiveHeaders]);

  const fetchUniversityElectiveGroups = React.useCallback(async () => {
    if (!programId) return;
    setUniversityElectiveLoading(true);
    setUniversityElectiveError('');
    try {
      const courseTypeParam = encodeURIComponent('üniversite seçmeli');
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups?course_type=${courseTypeParam}`, { headers: electiveHeaders() });
      const groups = await res.json();
      if (!res.ok || !Array.isArray(groups)) {
        setUniversityElectiveGroups([]);
        setUniversityElectiveError((groups && groups.error) || 'Üniversite seçmeli gruplar getirilemedi');
        return;
      }
      const enriched = await Promise.all(groups.map(async (g) => {
        try {
          const lr = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${g.id}/lessons`, { headers: electiveHeaders() });
          const lessons = await lr.json();
          if (lr.ok && Array.isArray(lessons)) {
            return { ...g, lessons };
          }
        } catch (e) { /* ignore */ }
        return { ...g, lessons: [] };
      }));
      setUniversityElectiveGroups(enriched);
    } catch (e) {
      setUniversityElectiveGroups([]);
      setUniversityElectiveError('Üniversite seçmeli gruplar getirilemedi');
    } finally {
      setUniversityElectiveLoading(false);
    }
  }, [programId, electiveHeaders]);

  const toggleElectiveGroup = React.useCallback((id) => {
    setExpandedElectiveIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
  }, []);
  const goToCourseManager = React.useCallback(() => {
    if (!programId) return;
    navigate(`/program/${programSlug}`, { state: { programId, programName: programNameForState, program } });
  }, [navigate, programId, programSlug, programNameForState, program]);

  const handleAddCourse = async (e) => {
    e.preventDefault();
    if (!programId) {
      setCourseFormError('Program bulunamadı');
      return;
    }
    const required = ['donem', 'course_code', 'course_name'];
    for (const key of required) {
      if (!newCourse[key] || String(newCourse[key]).trim() === '') {
        setCourseFormError('Dönem, ders kodu ve ders adı zorunlu');
        return;
      }
    }
    setSavingCourse(true);
    setCourseFormError('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (canManageProgram) {
        const token = programManagementToken
          || readAuthItem('programAdminToken')
          || readAuthItem('universityAdminToken');
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const body = {
        ...newCourse,
        donem: parseInt(newCourse.donem, 10),
        teorik: newCourse.teorik === '' ? 0 : Number(newCourse.teorik),
        uygulama: newCourse.uygulama === '' ? 0 : Number(newCourse.uygulama),
        laboratuvar: newCourse.laboratuvar === '' ? 0 : Number(newCourse.laboratuvar),
        kredi: newCourse.kredi === '' ? 0 : Number(newCourse.kredi),
        akts: newCourse.akts === '' ? 0 : Number(newCourse.akts),
      };
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCourseFormError(json && json.error ? json.error : 'Ders eklenemedi');
        return;
      }
      setNewCourse({
        donem: '',
        course_code: '',
        course_name: '',
        course_type: '',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: '',
      });
      setShowAddCourseModal(false);
      fetchCourses();
    } catch (err) {
      setCourseFormError('Ders eklenemedi');
    } finally {
      setSavingCourse(false);
    }
  };

  const handleAddElectiveGroup = async (e) => {
    e.preventDefault();
    if (!programId) return;
    setElectiveError('');
    if (!newElectiveGroup.donem || !newElectiveGroup.group_name) {
      setElectiveError('Dönem ve grup adı zorunlu');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups`, {
        method: 'POST',
        headers: electiveHeaders(),
        body: JSON.stringify({
          donem: parseInt(newElectiveGroup.donem, 10),
          group_name: newElectiveGroup.group_name,
          order_index: newElectiveGroup.order_index || 0
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setElectiveError(json && json.error ? json.error : 'Grup eklenemedi');
        return;
      }
      setNewElectiveGroup({ donem: '', group_name: '', order_index: '' });
      fetchElectiveGroups();
    } catch (err) {
      setElectiveError('Grup eklenemedi');
    }
  };

  const handleAddElectiveLesson = async (e) => {
    e.preventDefault();
    if (!programId) return;
    setElectiveError('');
    if (!newElectiveLesson.group_id || !newElectiveLesson.course_code || !newElectiveLesson.course_name) {
      setElectiveError('Grup, kod ve ad zorunlu');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${newElectiveLesson.group_id}/lessons`, {
        method: 'POST',
        headers: electiveHeaders(),
        body: JSON.stringify({
          course_code: newElectiveLesson.course_code,
          course_name: newElectiveLesson.course_name,
          course_type: newElectiveLesson.course_type,
          teorik: newElectiveLesson.teorik,
          uygulama: newElectiveLesson.uygulama,
          laboratuvar: newElectiveLesson.laboratuvar,
          kredi: newElectiveLesson.kredi,
          akts: newElectiveLesson.akts
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setElectiveError(json && json.error ? json.error : 'Seçmeli ders eklenemedi');
        return;
      }
      setNewElectiveLesson({
        group_id: newElectiveLesson.group_id,
        course_code: '',
        course_name: '',
        course_type: '',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: ''
      });
      fetchElectiveGroups();
    } catch (err) {
      setElectiveError('Seçmeli ders eklenemedi');
    }
  };

  const handleAddUniversityElectiveGroup = async (e) => {
    e.preventDefault();
    setUniversityElectiveError('');
    if (!programId) return;
    const required = ['donem', 'course_code', 'course_name'];
    for (const key of required) {
      if (!newUniversityElectiveGroup[key] || String(newUniversityElectiveGroup[key]).trim() === '') {
        setUniversityElectiveError('Dönem, ders kodu ve ders adı zorunlu');
        return;
      }
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (canManageProgram && programManagementToken) {
        headers.Authorization = `Bearer ${programManagementToken}`;
      }
      const body = {
        ...newUniversityElectiveGroup,
        donem: parseInt(newUniversityElectiveGroup.donem, 10),
        course_type: 'Üniversite Seçmeli', // Otomatik olarak Üniversite Seçmeli yap
        teorik: newUniversityElectiveGroup.teorik === '' ? 0 : Number(newUniversityElectiveGroup.teorik),
        uygulama: newUniversityElectiveGroup.uygulama === '' ? 0 : Number(newUniversityElectiveGroup.uygulama),
        laboratuvar: newUniversityElectiveGroup.laboratuvar === '' ? 0 : Number(newUniversityElectiveGroup.laboratuvar),
        kredi: newUniversityElectiveGroup.kredi === '' ? 0 : Number(newUniversityElectiveGroup.kredi),
        akts: newUniversityElectiveGroup.akts === '' ? 0 : Number(newUniversityElectiveGroup.akts),
      };
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUniversityElectiveError(json && json.error ? json.error : 'Üniversite seçmeli grup eklenemedi');
        return;
      }
      setNewUniversityElectiveGroup({
        donem: '',
        course_code: '',
        course_name: '',
        course_type: 'Üniversite Seçmeli',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: ''
      });
      fetchUniversityElectiveGroups();
      fetchCourses();
    } catch (err) {
      setUniversityElectiveError('Üniversite seçmeli grup eklenemedi');
    }
  };

  const handleAddUniversityElectiveLesson = async (e) => {
    e.preventDefault();
    if (!newUniversityElectiveLesson.group_id || !newUniversityElectiveLesson.course_code || !newUniversityElectiveLesson.course_name) {
      setUniversityElectiveError('Grup, kod ve ad zorunlu');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${newUniversityElectiveLesson.group_id}/lessons`, {
        method: 'POST',
        headers: electiveHeaders(),
        body: JSON.stringify({
          course_code: newUniversityElectiveLesson.course_code,
          course_name: newUniversityElectiveLesson.course_name,
          course_type: newUniversityElectiveLesson.course_type,
          teorik: newUniversityElectiveLesson.teorik,
          uygulama: newUniversityElectiveLesson.uygulama,
          laboratuvar: newUniversityElectiveLesson.laboratuvar,
          kredi: newUniversityElectiveLesson.kredi,
          akts: newUniversityElectiveLesson.akts
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUniversityElectiveError(json && json.error ? json.error : 'Üniversite seçmeli ders eklenemedi');
        return;
      }
      setNewUniversityElectiveLesson({
        group_id: newUniversityElectiveLesson.group_id,
        course_code: '',
        course_name: '',
        course_type: '',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: ''
      });
      fetchUniversityElectiveGroups();
    } catch (err) {
      setUniversityElectiveError('Üniversite seçmeli ders eklenemedi');
    }
  };

  const enhanceDownloadLinks = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || '', 'text/html');
      const anchors = doc.querySelectorAll('a[href]');
      anchors.forEach(a => {
        const href = a.getAttribute('href') || '';
        const isUpload = href.includes('/uploads/') || /\.(pdf|docx?|xlsx?|pptx?|zip|rar)$/i.test(href);
        if (isUpload) {
          // Backend uzerine yeniden yaz
          if (href.startsWith('/uploads/')) {
            a.setAttribute('href', `${API_BASE_URL}${href}`);
          }
          a.setAttribute('download', '');
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener noreferrer');
          // Ikon ekle (sadece bir kez)
          const hasIcon = a.querySelector('.file-icon');
          if (!hasIcon) {
            const span = doc.createElement('span');
            span.textContent = '';
            span.className = 'file-icon';
            a.insertBefore(span, a.firstChild);
          }
        }
      });
      return doc.body.innerHTML;
    } catch (e) {
      return html || '';
    }
  };

  const slugify = (text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleCourseClick = (course) => {
    try {
      if (program) window.sessionStorage.setItem('selectedProgram', JSON.stringify(program));
      if (course && course.course_code) window.sessionStorage.setItem('selectedCourseCode', course.course_code);
    } catch (e) { }
    navigate(`/course/${programSlug}/${slugify(course.course_name || course.course_code)}`, { state: { programId, courseCode: course.course_code } });
  };

  const handleWordUpload = async (e, course) => {
    e.stopPropagation();
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Lütfen .docx seçin' }));
      return;
    }
    try {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yükleniyor...' }));
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${course.course_code}/upload-word`, { method: 'POST', body: form });
      const json = await res.json();
      if (res.ok) {
        setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yüklendi' }));
        e.target.value = '';
      } else {
        setWordUploadStatus(prev => ({ ...prev, [course.course_code]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen'}` }));
      }
    } catch (err) {
      setWordUploadStatus(prev => ({ ...prev, [course.course_code]: 'Yükleme hatası' }));
    }
  };

  const handleCourseDelete = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`${course.course_code} dersine ait Word verilerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${course.course_code}/delete`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Silindi' }));
      } else {
        setDeleteStatus(prev => ({ ...prev, [course.course_code]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [course.course_code]: 'Silme hatası' }));
    }
  };

  const handleCourseFullDelete = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`${course.course_code} - ${course.course_name} dersini veritabanından TAMAMEN silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz! Derse ait tüm veriler (öğrenme çıktıları, haftalık konular, değerlendirme sistemi vb.) silinecektir.`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/admin/programs/${programId}/courses/${course.course_code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchCourses();
      } else {
        setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [`full-${course.course_code}`]: 'Silme hatası' }));
    }
  };

  const handleElectiveLessonWordUpload = async (e, lesson) => {
    e.stopPropagation();
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Lütfen .docx seçin' }));
      return;
    }
    try {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yükleniyor...' }));
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lesson.id}/upload-word`, { method: 'POST', body: form });
      const json = await res.json();
      if (res.ok) {
        setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yüklendi' }));
        e.target.value = '';
      } else {
        setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen'}` }));
      }
    } catch (err) {
      setWordUploadStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Yükleme hatası' }));
    }
  };

  const handleElectiveLessonDelete = async (e, lesson) => {
    e.stopPropagation();
    if (!window.confirm(`${lesson.course_code} seçmeli dersine ait Word verilerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Siliniyor...' }));
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lesson.id}/delete`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Silindi' }));
      } else {
        setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: `Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}` }));
      }
    } catch (err) {
      setDeleteStatus(prev => ({ ...prev, [`elective-${lesson.id}`]: 'Silme hatası' }));
    }
  };

  React.useEffect(() => {
    if (!programId) return;
    const loadSections = async () => {
      setLoadingSections(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/public/programs/${programId}/sections`);
        const data = await res.json();
        if (res.ok) setSections(data); else console.error(data.error || 'Hata');
      } catch (e) { console.error(e); }
      finally { setLoadingSections(false); }
    };
    loadSections();
  }, [programId]);

  React.useEffect(() => {
    if (!programId || !sectionSlug) return;
    const loadContent = async () => {
      setLoadingContent(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/public/programs/${programId}/sections/${sectionSlug}`);
        const data = await res.json();
        if (res.ok) setContent(enhanceDownloadLinks(data.content_html || '')); else setContent('');
      } catch (e) { console.error(e); setContent(''); }
      finally { setLoadingContent(false); }
    };
    loadContent();
  }, [programId, sectionSlug]);

  // Ders planı bölümü her zaman veritabanındaki güncel derslerden oluşturulur.
  // Bu bölüme daha önce kaydedilmiş bağlantı/HTML içeriği ders tablosunu gizlememeli.
  React.useEffect(() => {
    if (!programId || sectionSlug !== 'ders-plani-icerik') return;
    fetchCourses();
  }, [programId, sectionSlug, fetchCourses]);

  // Seçmeli grupları yükle (ders-plani-icerik görünümünde)
  React.useEffect(() => {
    if (!programId || sectionSlug !== 'ders-plani-icerik') return;
    fetchElectiveGroups();
  }, [programId, sectionSlug, fetchElectiveGroups]);

  if (!programId) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
            <div className="header-center"></div>
            {(isLoggedIn && handleLogout) && (
              <div className="header-right">
                <div className="user-info">
                  <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="main-content" style={{ padding: 16 }}>
          <div>Program bilgisi bulunamadı.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-center"></div>
          {(isLoggedIn && handleLogout) && (
            <div className="header-right">
              <div className="user-info">
                <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="main-content admin-editor-container">
        <div className="admin-editor-left">
          <div className="programs-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="back-button" onClick={() => navigate(-1)}>← Geri</button>
              <h2 style={{ margin: 0 }}>{program && program.name}</h2>
            </div>
          </div>
          {loadingSections ? (
            <div className="loading-courses">Başlıklar yükleniyor...</div>
          ) : (
            <div className="faculties-list">
              {sections.map(sec => (
                <div
                  key={sec.id}
                  className={`faculty-item ${sec.slug === sectionSlug ? 'selected' : ''}`}
                  onClick={() => navigate(`/program/${programSlug}/${sec.slug}`, { state: { program } })}
                >
                  <span className="faculty-name">{sec.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          {sectionSlug !== 'ders-plani-icerik' && (
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="back-button"
                onClick={() => navigate(`/program/${programSlug}/ders-plani-icerik${programId ? `?pid=${programId}` : ''}`, { state: { program } })}
              >
                ← Ders Planına Dön
              </button>
            </div>
          )}
          {sectionSlug === 'ders-plani-icerik' ? (
            <div className="course-details-section" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              {loadingCourses ? (
                <div>Ders planı yükleniyor...</div>
              ) : coursesFallback.length > 0 ? (
                <div className="courses-table-container">
                  {(userType === 'admin' && isLoggedIn) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                      <button
                        type="button"
                        className="back-button"
                        style={{
                          backgroundColor: '#2563eb',
                          color: '#fff',
                          borderColor: '#2563eb',
                          padding: '10px 18px',
                          minWidth: 140,
                          boxShadow: '0 4px 12px rgba(37,99,235,0.25)',
                          transform: 'none'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'none'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                        onClick={() => { setCourseFormError(''); setShowAddCourseModal(true); }}
                      >
                        Ders Ekle
                      </button>
                      <button
                        type="button"
                        className="back-button"
                        style={{
                          backgroundColor: '#10b981',
                          color: '#fff',
                          borderColor: '#10b981',
                          padding: '10px 18px',
                          minWidth: 160,
                          boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
                          transform: 'none'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'none'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                        onClick={() => { setElectiveError(''); setShowElectiveModal(true); fetchElectiveGroups(); }}
                      >
                        Seçmeli Ders Ekle
                      </button>
                      {/* Geçici olarak gizlendi - sonra bakılacak
                        <button
                          className="back-button"
                          style={{
                            backgroundColor: '#10b981',
                            color: '#fff',
                            borderColor: '#10b981',
                            padding: '10px 18px',
                            minWidth: 160,
                            boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
                            transform: 'none'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'none'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                          onClick={() => { setUniversityElectiveError(''); setShowUniversityElectiveModal(true); fetchUniversityElectiveGroups(); }}
                        >
                          Üniversite Seçmeli Ders Ekle
                        </button>
                        */}
                    </div>
                  )}
                  {Object.entries(
                    coursesFallback.reduce((acc, course) => {
                      const termKey = course.donem;
                      if (!acc[termKey]) acc[termKey] = [];
                      acc[termKey].push(course);
                      return acc;
                    }, {})
                  )
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([term, termCourses]) => (
                      <div key={term} style={{ marginBottom: '24px' }}>
                        <h4 style={{ margin: '8px 0' }}>{term}. Dönem</h4>
                        <table className="courses-table">
                          <thead>
                            <tr>
                              <th>Ders Kodu</th>
                              <th>Ders Adı</th>
                              <th>Ders Tipi</th>
                              <th>Teorik</th>
                              <th>Uygulama</th>
                              <th>Laboratuvar</th>
                              <th>Kredi</th>
                              <th>AKTS</th>
                              {userType === 'admin' ? (
                                <>
                                  <th>Word Yükle</th>
                                  <th>Word Sil</th>
                                  <th>Dersi Sil</th>
                                </>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {termCourses
                              .filter(c => String(c.course_type || '').toLowerCase() !== 'seçmeli')
                              .map((course, index) => (
                                <tr key={`${course.id}-${index}`} onClick={() => handleCourseClick(course)} style={{ cursor: 'pointer' }}>
                                  <td>{course.course_code}</td>
                                  <td>{course.course_name}</td>
                                  <td>{course.course_type || '-'}</td>
                                  <td>{course.teorik}</td>
                                  <td>{course.uygulama}</td>
                                  <td>{course.laboratuvar}</td>
                                  <td>{course.kredi}</td>
                                  <td>{course.akts}</td>
                                  {userType === 'admin' ? (
                                    <>
                                      <td onClick={(ev) => ev.stopPropagation()}>
                                        <input type="file" accept=".docx" onChange={(ev) => handleWordUpload(ev, course)} />
                                        {wordUploadStatus[course.course_code] ? (
                                          <div style={{ fontSize: 12, marginTop: 4 }}>{wordUploadStatus[course.course_code]}</div>
                                        ) : null}
                                      </td>
                                      <td onClick={(ev) => ev.stopPropagation()}>
                                        <button
                                          type="button"
                                          className="delete-button"
                                          onClick={(ev) => handleCourseDelete(ev, course)}
                                          style={{ padding: '6px 12px', fontSize: 12 }}
                                        >
                                          Sil
                                        </button>
                                        {deleteStatus[course.course_code] ? (
                                          <div style={{ fontSize: 12, marginTop: 4 }}>{deleteStatus[course.course_code]}</div>
                                        ) : null}
                                      </td>
                                      <td onClick={(ev) => ev.stopPropagation()}>
                                        <button
                                          type="button"
                                          onClick={(ev) => handleCourseFullDelete(ev, course)}
                                          style={{ padding: '6px 12px', fontSize: 12, backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                        >
                                          Dersi Sil
                                        </button>
                                        {deleteStatus[`full-${course.course_code}`] ? (
                                          <div style={{ fontSize: 12, marginTop: 4, color: '#dc2626' }}>{deleteStatus[`full-${course.course_code}`]}</div>
                                        ) : null}
                                      </td>
                                    </>
                                  ) : null}
                                </tr>
                              ))}
                            {(electiveGroups || [])
                              .filter(g => String(g.donem) === String(term))
                              .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                              .map((g) => {
                                const expanded = expandedElectiveIds.includes(g.id);
                                return (
                                  <React.Fragment key={`egrp-${g.id}`}>
                                    <tr
                                      style={{ background: '#f9fafb', fontWeight: 600, cursor: 'pointer' }}
                                      onClick={() => toggleElectiveGroup(g.id)}
                                    >
                                      <td>{expanded ? '▼' : '▶'} {g.course_code || g.group_name}</td>
                                      <td>{g.group_name}</td>
                                      <td>Seçmeli</td>
                                      <td>{g.teorik != null ? g.teorik : '-'}</td>
                                      <td>{g.uygulama != null ? g.uygulama : '-'}</td>
                                      <td>{g.laboratuvar != null ? g.laboratuvar : '-'}</td>
                                      <td>{g.kredi != null ? g.kredi : '-'}</td>
                                      <td>{g.akts != null ? g.akts : '-'}</td>
                                      {userType === 'admin' ? (
                                        <>
                                          <td />
                                          <td />
                                          <td />
                                        </>
                                      ) : null}
                                    </tr>
                                    {expanded && (g.lessons || []).map((ls) => (
                                      <tr
                                        key={`els-${g.id}-${ls.id}`}
                                        style={{ background: '#fdfdfd', cursor: 'pointer' }}
                                        onClick={() => {
                                          try {
                                            window.sessionStorage.setItem('selectedCourseCode', `elective-${ls.id}`);
                                            window.sessionStorage.setItem('selectedElectiveLessonId', ls.id);
                                          } catch (e) { }
                                          navigate(`/course/${programSlug}/${slugify(ls.course_name || ls.course_code)}`, {
                                            state: {
                                              programId,
                                              courseCode: `elective-${ls.id}`,
                                              electiveLessonId: ls.id,
                                              isElectiveLesson: true
                                            }
                                          });
                                        }}
                                      >
                                        <td>{ls.course_code}</td>
                                        <td>{ls.course_name}</td>
                                        <td>{ls.course_type || 'Seçmeli'}</td>
                                        <td>{ls.teorik}</td>
                                        <td>{ls.uygulama}</td>
                                        <td>{ls.laboratuvar}</td>
                                        <td>{ls.kredi}</td>
                                        <td>{ls.akts}</td>
                                        {userType === 'admin' ? (
                                          <>
                                            <td onClick={(ev) => ev.stopPropagation()}>
                                              <input type="file" accept=".docx" onChange={(ev) => handleElectiveLessonWordUpload(ev, ls)} />
                                              {wordUploadStatus[`elective-${ls.id}`] ? (
                                                <div style={{ fontSize: 12, marginTop: 4 }}>{wordUploadStatus[`elective-${ls.id}`]}</div>
                                              ) : null}
                                            </td>
                                            <td onClick={(ev) => ev.stopPropagation()}>
                                              <button
                                                type="button"
                                                className="delete-button"
                                                onClick={(ev) => handleElectiveLessonDelete(ev, ls)}
                                                style={{ padding: '6px 12px', fontSize: 12 }}
                                              >
                                                Sil
                                              </button>
                                              {deleteStatus[`elective-${ls.id}`] ? (
                                                <div style={{ fontSize: 12, marginTop: 4 }}>{deleteStatus[`elective-${ls.id}`]}</div>
                                              ) : null}
                                            </td>
                                            <td />
                                          </>
                                        ) : null}
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                </div>
              ) : (
                <div>Bu program için henüz ders yüklenmemiş.</div>
              )}
            </div>
          ) : loadingContent ? (
            <div>İçerik yükleniyor...</div>
          ) : content && content.trim() !== '' ? (
            <div className="course-details-section" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              <div dangerouslySetInnerHTML={sanitizedHtmlProps(content)} />
            </div>
          ) : (
            <div className="course-details-section" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
              <div>Bu başlık için içerik bulunamadı.</div>
            </div>
          )}
          {userType === 'admin' && showAddCourseModal ? (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 18 }}>
              <div style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)', padding: 30, borderRadius: 14, width: '95%', maxWidth: 1050, boxShadow: '0 24px 80px rgba(15,23,42,0.22)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', letterSpacing: 0.3, textTransform: 'uppercase' }}>Ders Yönetimi</div>
                    <h3 style={{ margin: '4px 0 0 0', fontSize: 22, fontWeight: 700, color: '#111827' }}>Yeni Ders Ekle</h3>
                  </div>
                  <button className="back-button" onClick={() => setShowAddCourseModal(false)} style={{ minWidth: 96 }}>Kapat</button>
                </div>
                <form onSubmit={handleAddCourse}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Dönem</label>
                      <input type="number" placeholder="Örn: 1" value={newCourse.donem} onChange={(e) => setNewCourse(prev => ({ ...prev, donem: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Ders Kodu</label>
                      <input type="text" placeholder="Örn: MAT101" value={newCourse.course_code} onChange={(e) => setNewCourse(prev => ({ ...prev, course_code: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Ders Adı</label>
                      <input type="text" placeholder="Örn: Lineer Cebir" value={newCourse.course_name} onChange={(e) => setNewCourse(prev => ({ ...prev, course_name: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Ders Tipi</label>
                      <input type="text" placeholder="Zorunlu / Seçmeli" value={newCourse.course_type} onChange={(e) => setNewCourse(prev => ({ ...prev, course_type: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Teorik</label>
                      <input type="number" placeholder="Saat" value={newCourse.teorik} onChange={(e) => setNewCourse(prev => ({ ...prev, teorik: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Uygulama</label>
                      <input type="number" placeholder="Saat" value={newCourse.uygulama} onChange={(e) => setNewCourse(prev => ({ ...prev, uygulama: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Laboratuvar</label>
                      <input type="number" placeholder="Saat" value={newCourse.laboratuvar} onChange={(e) => setNewCourse(prev => ({ ...prev, laboratuvar: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>Kredi</label>
                      <input type="number" step="0.5" placeholder="Örn: 3" value={newCourse.kredi} onChange={(e) => setNewCourse(prev => ({ ...prev, kredi: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>AKTS</label>
                      <input type="number" step="0.5" placeholder="Örn: 5" value={newCourse.akts} onChange={(e) => setNewCourse(prev => ({ ...prev, akts: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                    </div>
                  </div>
                  {courseFormError ? <div className="form-error" style={{ marginTop: 14 }}>{courseFormError}</div> : null}
                  <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button type="button" className="back-button" onClick={() => setShowAddCourseModal(false)} style={{ minWidth: 120, backgroundColor: '#e5e7eb', color: '#111827', borderColor: '#e5e7eb' }}>Vazgeç</button>
                    <button type="submit" className="back-button" style={{ backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb', minWidth: 130 }} disabled={savingCourse}>
                      {savingCourse ? 'Kaydediliyor...' : 'Ders Ekle'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
          {userType === 'admin' && showElectiveModal ? (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, padding: 18 }}>
              <div style={{ background: 'linear-gradient(180deg,#fdfefe 0%,#f3f4f6 100%)', padding: 28, borderRadius: 14, width: '96%', maxWidth: 1100, boxShadow: '0 28px 90px rgba(15,23,42,0.25)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', letterSpacing: 0.3, textTransform: 'uppercase' }}>Seçmeli Dersler</div>
                    <h3 style={{ margin: '4px 0 0 0', fontSize: 22, fontWeight: 700, color: '#111827' }}>Seçmeli Grup / Ders Ekle</h3>
                  </div>
                  <button className="back-button" onClick={() => setShowElectiveModal(false)} style={{ minWidth: 96 }}>Kapat</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr)', gap: 16, marginBottom: 16 }}>
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 10, color: '#111827' }}>Seçmeli Derse Alt Ders Ekle</div>
                    <form onSubmit={handleAddElectiveLesson} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <select
                        value={newElectiveLesson.group_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          const selected = (electiveGroups || []).find(g => String(g.id) === String(val));
                          setNewElectiveLesson(prev => ({
                            ...prev,
                            group_id: val,
                            course_type: selected && selected.course_type ? selected.course_type : (prev.course_type || 'Seçmeli')
                          }));
                        }}
                        required
                        style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }}
                      >
                        <option value="">Seçmeli Ders Seçin</option>
                        {electiveGroups
                          .filter(g => g.course_type !== 'Üniversite Seçmeli' && g.course_type !== 'üniversite seçmeli')
                          .map(g => (
                            <option key={g.id} value={g.id}>{`${g.group_name} (${g.donem}. Dönem)`}</option>
                          ))}
                      </select>
                      <input type="text" placeholder="Ders Kodu" value={newElectiveLesson.course_code} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, course_code: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Adı" value={newElectiveLesson.course_name} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, course_name: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Tipi" value={newElectiveLesson.course_type} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, course_type: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                        <input type="number" placeholder="Teorik" value={newElectiveLesson.teorik} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, teorik: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Uygulama" value={newElectiveLesson.uygulama} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, uygulama: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Laboratuvar" value={newElectiveLesson.laboratuvar} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, laboratuvar: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="Kredi" value={newElectiveLesson.kredi} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, kredi: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="AKTS" value={newElectiveLesson.akts} onChange={(e) => setNewElectiveLesson(prev => ({ ...prev, akts: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      </div>
                      <button type="submit" className="back-button" style={{ backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb', minWidth: 140 }}>Seçmeli Ders Ekle</button>
                    </form>
                  </div>
                </div>

                {electiveError ? <div className="form-error" style={{ marginBottom: 12 }}>{electiveError}</div> : null}
                <div style={{ maxHeight: 320, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
                  {electiveLoading ? (
                    <div style={{ padding: 8 }}>Seçmeli listesi yükleniyor...</div>
                  ) : electiveGroups.length === 0 ? (
                    <div style={{ padding: 8, color: '#6b7280' }}>Henüz seçmeli grup yok.</div>
                  ) : (
                    electiveGroups.map((g) => (
                      <div key={g.id} style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>{g.group_name} <span style={{ color: '#6b7280', fontWeight: 500 }}>({g.donem}. Dönem)</span></div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}># {g.order_index || 0}</div>
                        </div>
                        <div style={{ marginTop: 6, marginLeft: 8 }}>
                          {(g.lessons && g.lessons.length > 0) ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Kod</th>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Ad</th>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Tip</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>T</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>U</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>L</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>Kredi</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>AKTS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.lessons.map(ls => (
                                  <tr key={ls.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '6px' }}>{ls.course_code}</td>
                                    <td style={{ padding: '6px' }}>{ls.course_name}</td>
                                    <td style={{ padding: '6px' }}>{ls.course_type || '-'}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.teorik}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.uygulama}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.laboratuvar}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.kredi}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.akts}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ color: '#6b7280', fontSize: 13 }}>Alt ders yok.</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}
          {userType === 'admin' && showUniversityElectiveModal ? (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, padding: 18 }}>
              <div style={{ background: 'linear-gradient(180deg,#fdfefe 0%,#f3f4f6 100%)', padding: 28, borderRadius: 14, width: '96%', maxWidth: 1100, boxShadow: '0 28px 90px rgba(15,23,42,0.25)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', letterSpacing: 0.3, textTransform: 'uppercase' }}>Üniversite Seçmeli Dersler</div>
                    <h3 style={{ margin: '4px 0 0 0', fontSize: 22, fontWeight: 700, color: '#111827' }}>Üniversite Seçmeli Grup / Ders Ekle</h3>
                  </div>
                  <button className="back-button" onClick={() => setShowUniversityElectiveModal(false)} style={{ minWidth: 96 }}>Kapat</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 10, color: '#111827' }}>Üniversite Seçmeli Üst Grup Ekle</div>
                    <form onSubmit={handleAddUniversityElectiveGroup} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input type="number" placeholder="Dönem" value={newUniversityElectiveGroup.donem} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, donem: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Kodu" value={newUniversityElectiveGroup.course_code} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, course_code: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Adı" value={newUniversityElectiveGroup.course_name} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, course_name: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Tipi" value={newUniversityElectiveGroup.course_type} readOnly style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#e5e7eb', color: '#6b7280' }} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                        <input type="number" placeholder="Teorik" value={newUniversityElectiveGroup.teorik} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, teorik: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Uygulama" value={newUniversityElectiveGroup.uygulama} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, uygulama: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Laboratuvar" value={newUniversityElectiveGroup.laboratuvar} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, laboratuvar: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="Kredi" value={newUniversityElectiveGroup.kredi} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, kredi: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="AKTS" value={newUniversityElectiveGroup.akts} onChange={(e) => setNewUniversityElectiveGroup(prev => ({ ...prev, akts: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      </div>
                      <button type="submit" className="back-button" style={{ backgroundColor: '#10b981', color: '#fff', borderColor: '#10b981', minWidth: 140 }}>Üniversite Seçmeli Grup Ekle</button>
                    </form>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 10, color: '#111827' }}>Üniversite Seçmeli Derse Alt Ders Ekle</div>
                    <form onSubmit={handleAddUniversityElectiveLesson} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <select
                        value={newUniversityElectiveLesson.group_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          const selected = (universityElectiveGroups || []).find(g => String(g.id) === String(val));
                          setNewUniversityElectiveLesson(prev => ({
                            ...prev,
                            group_id: val,
                            course_type: selected && selected.course_type ? selected.course_type : (prev.course_type || 'Üniversite Seçmeli')
                          }));
                        }}
                        required
                        style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }}
                      >
                        <option value="">Üniversite Seçmeli Ders Seçin</option>
                        {universityElectiveGroups
                          .filter(g => {
                            const ct = (g.course_type || '').toLowerCase();
                            return ct === 'üniversite seçmeli' || ct === 'universite secmeli' || ct.includes('üniversite');
                          })
                          .map(g => (
                            <option key={g.id} value={g.id}>{`${g.group_name} (${g.donem}. Dönem)`}</option>
                          ))}
                      </select>
                      <input type="text" placeholder="Ders Kodu" value={newUniversityElectiveLesson.course_code} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, course_code: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Adı" value={newUniversityElectiveLesson.course_name} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, course_name: e.target.value }))} required style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <input type="text" placeholder="Ders Tipi" value={newUniversityElectiveLesson.course_type} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, course_type: e.target.value }))} style={{ height: 40, padding: '9px 12px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                        <input type="number" placeholder="Teorik" value={newUniversityElectiveLesson.teorik} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, teorik: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Uygulama" value={newUniversityElectiveLesson.uygulama} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, uygulama: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" placeholder="Laboratuvar" value={newUniversityElectiveLesson.laboratuvar} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, laboratuvar: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="Kredi" value={newUniversityElectiveLesson.kredi} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, kredi: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                        <input type="number" step="0.5" placeholder="AKTS" value={newUniversityElectiveLesson.akts} onChange={(e) => setNewUniversityElectiveLesson(prev => ({ ...prev, akts: e.target.value }))} style={{ height: 38, padding: '8px 10px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f8fafc' }} />
                      </div>
                      <button type="submit" className="back-button" style={{ backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb', minWidth: 140 }}>Üniversite Seçmeli Ders Ekle</button>
                    </form>
                  </div>
                </div>

                {universityElectiveError ? <div className="form-error" style={{ marginBottom: 12 }}>{universityElectiveError}</div> : null}
                <div style={{ maxHeight: 320, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
                  {universityElectiveLoading ? (
                    <div style={{ padding: 8 }}>Üniversite seçmeli listesi yükleniyor...</div>
                  ) : universityElectiveGroups.length === 0 ? (
                    <div style={{ padding: 8, color: '#6b7280' }}>Henüz üniversite seçmeli grup yok.</div>
                  ) : (
                    universityElectiveGroups.map((g) => (
                      <div key={g.id} style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>{g.group_name} <span style={{ color: '#6b7280', fontWeight: 500 }}>({g.donem}. Dönem)</span></div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}># {g.order_index || 0}</div>
                        </div>
                        <div style={{ marginTop: 6, marginLeft: 8 }}>
                          {(g.lessons && g.lessons.length > 0) ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: '#f3f4f6' }}>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Kod</th>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Ad</th>
                                  <th style={{ textAlign: 'left', padding: '6px' }}>Tip</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>T</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>U</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>L</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>Kredi</th>
                                  <th style={{ textAlign: 'center', padding: '6px' }}>AKTS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.lessons.map(ls => (
                                  <tr key={ls.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '6px' }}>{ls.course_code}</td>
                                    <td style={{ padding: '6px' }}>{ls.course_name}</td>
                                    <td style={{ padding: '6px' }}>{ls.course_type || '-'}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.teorik}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.uygulama}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.laboratuvar}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.kredi}</td>
                                    <td style={{ padding: '6px', textAlign: 'center' }}>{ls.akts}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ color: '#6b7280', fontSize: 13 }}>Alt ders yok.</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {/* Modal kaldırıldı: başlıklar yeni sayfada açılıyor */}
    </div>
  );
}
// ProgramSidebarAdminPage: Baştan tasarlanmış sade ve kurumsal arayüz
function ProgramSidebarAdminPage({ token, programIdProp, onLogout, electivesOnly = false }) {
  const navigate = useNavigate();
  const location = require('react-router-dom').useLocation();
  const search = new URLSearchParams(location.search || '');

  // Token'ı state olarak yönet ve sessionStorage/localStorage'dan oku
  const [tokenMemo, setTokenMemo] = React.useState(() => {
    return token || readAuthItem('programAdminToken') || readAuthItem('universityAdminToken') || '';
  });

  // Token prop'u değiştiğinde güncelle
  React.useEffect(() => {
    const currentToken = token || readAuthItem('programAdminToken') || readAuthItem('universityAdminToken') || '';
    setTokenMemo(prev => (prev !== currentToken ? currentToken : prev));
  }, [token]);

  // URL'den programId al (query parametresi)
  const programIdFromUrl = search.get('programId') ? parseInt(search.get('programId'), 10) : null;
  const [programId, setProgramId] = React.useState(programIdProp || programIdFromUrl || null);
  const [programName, setProgramName] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [sections, setSections] = React.useState([]);
  const [filterText, setFilterText] = React.useState('');
  
  // Çoklu rol desteği: Aktif rolleri kontrol et
  const [hasDeanRole, setHasDeanRole] = React.useState(false);
  
  // Aktif rolleri yükle
  React.useEffect(() => {
    const storedRoles = localStorage.getItem('all_active_roles');
    if (storedRoles) {
      try {
        const roles = JSON.parse(storedRoles);
        // Dekan (3) veya Dekan Yardımcısı (4) rolü var mı kontrol et
        const hasDean = roles.some(r => [3, 4].includes(Number(r.role_type_id)));
        setHasDeanRole(hasDean);
      } catch (e) {
        console.error('Error parsing active roles:', e);
      }
    }
  }, []);
  
  // İzlence doluluk durumu state'leri
  const [syllabusData, setSyllabusData] = React.useState(null);
  const [syllabusLoading, setSyllabusLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [semesterFilter, setSemesterFilter] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('semester');

  const [selectedSection, setSelectedSection] = React.useState(null);
  const [contentHtml, setContentHtml] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  // Program outcomes için state'ler
  const [programOutcomes, setProgramOutcomes] = React.useState([]);
  const [loadingOutcomes, setLoadingOutcomes] = React.useState(false);
  const [editingOutcome, setEditingOutcome] = React.useState(null);
  const [showOutcomeForm, setShowOutcomeForm] = React.useState(false);
  const [isCopyingPreviousOutcomes, setIsCopyingPreviousOutcomes] = React.useState(false);
  const [planningProgramOutcomes, setPlanningProgramOutcomes] = React.useState(isPlanningYear());
  const [outcomeFormData, setOutcomeFormData] = React.useState({ number: '', name: '' });
  const [isPreview, setIsPreview] = React.useState(false);
  const [isSplitPreview, setIsSplitPreview] = React.useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = React.useState(false);
  const [saveNotification, setSaveNotification] = React.useState({ show: false, message: '', type: 'success' });
  const [fontSizePx, setFontSizePx] = React.useState('16');
  const fontSizeDebounceRef = React.useRef(null);
  const lastRangeRef = React.useRef(null);
  const [selectedImgInfo, setSelectedImgInfo] = React.useState({ el: null, widthPct: 100 });
  const draggedImgRef = React.useRef(null);
  const [blockMode, setBlockMode] = React.useState(true);
  const [blocks, setBlocks] = React.useState([]);
  const [activeBlockId, setActiveBlockId] = React.useState(null);
  const blockRefs = React.useRef({});
  const blockSelectionRef = React.useRef({});
  const blocksInitializedRef = React.useRef(new Set());

  React.useEffect(() => {
    const updatePlanningState = (event) => {
      setPlanningProgramOutcomes(event?.detail?.status === 'planning' || isPlanningYear());
    };
    window.addEventListener('academic-year-meta-changed', updatePlanningState);
    setPlanningProgramOutcomes(isPlanningYear());
    return () => window.removeEventListener('academic-year-meta-changed', updatePlanningState);
  }, []);

  // Seçmeli dersler state'i
  const [electiveLessons, setElectiveLessons] = React.useState([]);
  const [loadingElectives, setLoadingElectives] = React.useState(false);

  // Seçmeli dersleri yükle
  const fetchElectiveLessons = React.useCallback(async () => {
    if (!programId || !tokenMemo) return;
    setLoadingElectives(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons`, {
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      if (res.ok) {
        const data = await res.json();
        setElectiveLessons(data || []);
      }
    } catch (e) {
      console.error('Seçmeli dersler yüklenemedi:', e);
    } finally {
      setLoadingElectives(false);
    }
  }, [programId, tokenMemo]);

  // --- Elective Pool Management State ---
  const [selectedLessonIds, setSelectedLessonIds] = React.useState([]);
  const [programGroups, setProgramGroups] = React.useState([]);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [selectedGroupId, setSelectedGroupId] = React.useState('');
  const [expandedPoolIds, setExpandedPoolIds] = React.useState([]);

  // Fetch Program Groups (Existing courses that can be pools)
  const fetchProgramGroups = React.useCallback(async () => {
    if (!programId || !tokenMemo) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses`, {
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter 'Seçmeli' types
        setProgramGroups(data.filter(c => c.course_type.toLowerCase().includes('seçmeli')));
      }
    } catch (err) { console.error(err); }
  }, [programId, tokenMemo]);

  // Handle Promote
  const handlePromoteToGroup = async () => {
    if (selectedLessonIds.length !== 1) return;
    const lessonId = selectedLessonIds[0];
    const lesson = electiveLessons.find(c => c.id === lessonId);
    if (!lesson) return;

    if (!window.confirm(`"${lesson.course_name}" dersini ANA DERS (Havuz) yapmak istiyor musunuz? Bu işlem dersi seçmeli listesinden kaldırıp müfredata ekleyecektir.`)) return;

    try {
      setLoadingElectives(true);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/promote-to-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenMemo}`
        }
      });

      if (res.ok) {
        alert('Ders başarıyla havuza dönüştürüldü!');
        setSelectedLessonIds([]);
        await fetchElectiveLessons();
        await fetchProgramGroups();
      } else {
        const d = await res.json();
        alert(d.error || 'Hata oluştu');
      }
    } catch (err) {
      alert('Hata: ' + err);
    } finally {
      setLoadingElectives(false);
    }
  };

  // Handle Link
  const handleLinkLessons = async () => {
    if (!selectedGroupId || selectedLessonIds.length === 0) return;

    try {
      setLoadingElectives(true);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${selectedGroupId}/link-lessons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenMemo}`
        },
        body: JSON.stringify({ lesson_ids: selectedLessonIds })
      });

      if (res.ok) {
        alert('Dersler başarıyla bağlandı!');
        setShowLinkModal(false);
        setSelectedLessonIds([]);
        await fetchElectiveLessons();
      } else {
        const d = await res.json();
        alert(d.error || d.message || 'Hata oluştu');
      }
    } catch (err) {
      console.error(err);
      alert('Hata: ' + err);
    } finally {
      setLoadingElectives(false);
    }
  };

  // Handle Pool (Havuz) Delete — linked lessons are unlinked, pool course is removed
  const handleDeletePool = async (pool) => {
    if (!window.confirm(`"${pool.course_name}" havuzunu silmek istediğinize emin misiniz? Havuza bağlı seçmeli dersler silinmeyecek, sadece havuzdan ayrılacaktır.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-groups/${pool.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      if (res.ok) {
        await fetchProgramGroups();
        await fetchElectiveLessons();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Havuz silinemedi');
      }
    } catch (err) {
      alert('Hata: ' + err);
    }
  };

  // Handle Unlink
  const handleUnlinkLesson = async (lessonId) => {
    if (!window.confirm('Bu dersi havuzdan çıkarmak istediğinize emin misiniz?')) return;
    try {
      setLoadingElectives(true);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/unlink`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenMemo}`
        }
      });

      if (res.ok) {
        // Refresh data
        await fetchElectiveLessons();
      } else {
        const d = await res.json();
        alert(d.error || 'Hata oluştu');
      }
    } catch (err) {
      console.error(err);
      alert('Hata oluştu');
    } finally {
      setLoadingElectives(false);
    }
  };

  // 1) Program ID yoksa URL'den veya me'den al
  React.useEffect(() => {
    const ensureProgram = async () => {
      // Önce URL'den kontrol et
      const urlProgramId = search.get('programId') ? parseInt(search.get('programId'), 10) : null;
      if (urlProgramId && !isNaN(urlProgramId)) {
        setProgramId(urlProgramId);
        return;
      }

      // Prop'tan kontrol et
      if (programIdProp) {
        setProgramId(programIdProp);
        return;
      }

      // Mevcut programId varsa işlem yapma
      if (programId) return;

      // Token yoksa işlem yapma
      if (!tokenMemo) return;

      // API'den program ID'lerini al
      try {
        const meRes = await fetch(`${API_BASE_URL}/api/program-admin/me`, { headers: { Authorization: `Bearer ${tokenMemo}` } });
        const meData = await meRes.json();
        if (meRes.ok && Array.isArray(meData.programIds) && meData.programIds.length > 0) {
          setProgramId(meData.programIds[0]);
        }
      } catch (e) { }
    };
    ensureProgram();
  }, [tokenMemo, programId, programIdProp, search]);

  // 2) Bölümleri yükle
  const fetchSections = React.useCallback(async (pid) => {
    if (!pid) return;
    if (!tokenMemo) {
      console.error('[ProgramSidebar] No token available for fetchSections');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${pid}/sections`, {
        headers: {
          'Authorization': `Bearer ${tokenMemo}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (res.ok) {
        setSections(data || []);
      } else {
        console.error('[ProgramSidebar] Sections fetch failed:', res.status, data);
      }
    } catch (e) {
      console.error('[ProgramSidebar] Sections fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [tokenMemo]);

  React.useEffect(() => { if (programId) fetchSections(programId); }, [programId, fetchSections]);

  // Bağımsız Seçmeli Dersler sayfasında (electivesOnly) veya ?section=electives ile gelindiğinde
  // Seçmeli Dersler bölümünü otomatik aç
  React.useEffect(() => {
    if (electivesOnly || search.get('section') === 'electives') {
      setSelectedSection({ id: 'electives', title: 'Seçmeli Dersler', slug: 'secmeli-dersler' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electivesOnly]);

  // Program adını al
  React.useEffect(() => {
    const fetchProgramName = async () => {
      if (!programId) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs`);
        const programs = await res.json();
        const program = programs.find(p => p.id === programId);
        if (program) setProgramName(program.name);
      } catch (e) { console.error('Program adı alınamadı:', e); }
    };
    fetchProgramName();
  }, [programId]);

  // İzlence doluluk durumu verilerini al
  React.useEffect(() => {
    const fetchSyllabusStatus = async () => {
      if (!programId) return;
      if (!tokenMemo) {
        console.error('[ProgramSidebar] No token available for fetchSyllabusStatus');
        return;
      }
      setSyllabusLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/syllabus-status`, {
          headers: {
            'Authorization': `Bearer ${tokenMemo}`,
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          setSyllabusData(data);
        } else {
          console.error('[ProgramSidebar] Syllabus status fetch failed:', res.status);
        }
      } catch (e) {
        console.error('[ProgramSidebar] İzlence durumu alınamadı:', e);
      }
      finally {
        setSyllabusLoading(false);
      }
    };
    fetchSyllabusStatus();
  }, [programId, tokenMemo]);

  // Filtrelenmiş dersler
  const filteredCourses = React.useMemo(() => {
    if (!syllabusData || !syllabusData.courses) return [];

    let filtered = syllabusData.courses.filter(course => {
      const matchesSearch = searchTerm === '' ||
        course.course_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.course_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSemester = semesterFilter === 'all' || String(course.semester) === semesterFilter;
      const matchesType = typeFilter === 'all' ||
        (typeFilter === 'zorunlu' && !course.course_type.toLowerCase().includes('seçmeli')) ||
        (typeFilter === 'secmeli' && course.course_type.toLowerCase().includes('seçmeli'));
      return matchesSearch && matchesSemester && matchesType;
    });

    if (sortBy === 'semester') {
      filtered.sort((a, b) => {
        if (a.semester !== b.semester) return a.semester - b.semester;
        return a.course_code.localeCompare(b.course_code);
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.course_name.localeCompare(b.course_name));
    } else if (sortBy === 'completion') {
      filtered.sort((a, b) => b.completion_percentage - a.completion_percentage);
    }

    return filtered;
  }, [syllabusData, searchTerm, semesterFilter, typeFilter, sortBy]);

  // Renk yardımcı fonksiyonları
  const getCompletionColor = (percentage) => {
    if (percentage === 0) return '#fee2e2';
    if (percentage === 100) return '#d1fae5';
    return '#fed7aa';
  };

  const getCompletionTextColor = (percentage) => {
    if (percentage === 0) return '#dc2626';
    if (percentage === 100) return '#059669';
    return '#ea580c';
  };

  // 3) İçerik yükle
  const loadContent = React.useCallback(async (section) => {
    if (!programId || !section) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/sections/${section.id}/content`, { headers: { Authorization: `Bearer ${tokenMemo}` } });
      const data = await res.json();
      if (res.ok) {
        const html = data.content_html || '';
        setContentHtml(html);

        // Normal editör modu için içeriği yükle
        if (!blockMode) {
          const el = document.getElementById('admin-v2-editor');
          if (el) {
            el.innerHTML = rewriteUploadUrls(html);
          }
        } else {
          // Block modu için HTML'i parse et ve blocks'a çevir
          const parser = new DOMParser();
          const doc = parser.parseFromString(html || '<p></p>', 'text/html');
          const body = doc.body;
          const newBlocks = [];

          // HTML'i bloklara ayır (paragraflar, resimler, vs.)
          const walker = document.createTreeWalker(body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
          let currentBlock = null;

          // Basit parse: her p, div, img, ul, ol, h1-h6 bir blok
          Array.from(body.childNodes).forEach(node => {
            if (node.nodeType === 1) { // Element node
              if (node.tagName === 'IMG') {
                const src = node.getAttribute('src');
                const widthMatch = node.style.width ? node.style.width.match(/(\d+)%/) : null;
                const widthPct = widthMatch ? parseInt(widthMatch[1], 10) : 100;
                newBlocks.push({
                  id: String(Date.now() + Math.random()),
                  type: 'image',
                  src: src,
                  widthPct: widthPct
                });
              } else if (node.tagName === 'P' || node.tagName === 'DIV') {
                // Paragraf veya div içeriğini al
                const innerHtml = node.innerHTML || '';
                if (innerHtml.trim() || node.textContent.trim()) {
                  newBlocks.push({
                    id: String(Date.now() + Math.random()),
                    type: 'text',
                    html: innerHtml || `<p>${node.textContent || ''}</p>`
                  });
                }
              } else {
                // Diğer elementler (h1-h6, ul, ol, vs.)
                const innerHtml = node.outerHTML || '';
                if (innerHtml.trim() || node.textContent.trim()) {
                  newBlocks.push({
                    id: String(Date.now() + Math.random()),
                    type: 'text',
                    html: innerHtml
                  });
                }
              }
            }
          });

          // Eğer hiç blok yoksa, içeriği tek bir text bloğu olarak ekle
          if (newBlocks.length === 0 && html.trim()) {
            newBlocks.push({
              id: String(Date.now()),
              type: 'text',
              html: html
            });
          }

          setBlocks(newBlocks);
        }
      }
    } catch (e) {
      console.error('İçerik yükleme hatası:', e);
    }
  }, [programId, tokenMemo, blockMode]);

  // Program outcomes yükle
  const loadProgramOutcomes = React.useCallback(async () => {
    if (!programId || !tokenMemo) return;
    setLoadingOutcomes(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/program-outcomes`, {
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      const data = await res.json();
      if (res.ok) {
        setProgramOutcomes(data || []);
      } else {
        console.error('Program outcomes yüklenemedi:', data);
        setProgramOutcomes([]);
      }
    } catch (e) {
      console.error('Program outcomes yükleme hatası:', e);
      setProgramOutcomes([]);
    } finally {
      setLoadingOutcomes(false);
    }
  }, [programId, tokenMemo]);

  // Program outcomes kaydet/güncelle
  const saveProgramOutcome = React.useCallback(async (outcomeData) => {
    if (!programId || !tokenMemo) return;
    try {
      setIsSaving(true);
      const method = outcomeData.id ? 'PUT' : 'POST';
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/program-outcomes`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenMemo}`
        },
        body: JSON.stringify(outcomeData)
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveNotification({ show: true, message: data.error || 'Kaydetme hatası', type: 'error' });
        setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
        return false;
      }
      await loadProgramOutcomes();
      setSaveNotification({ show: true, message: 'Kaydedildi', type: 'success' });
      setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
      setShowOutcomeForm(false);
      setEditingOutcome(null);
      setOutcomeFormData({ number: '', name: '' });
      return true;
    } catch (e) {
      setSaveNotification({ show: true, message: 'Kaydetme sırasında bir hata oluştu', type: 'error' });
      setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [programId, tokenMemo, loadProgramOutcomes]);

  // Program outcome sil
  const deleteProgramOutcome = React.useCallback(async (outcomeId) => {
    if (!programId || !tokenMemo || !window.confirm('Bu program çıktısını silmek istediğinize emin misiniz?')) return;
    try {
      setIsSaving(true);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/program-outcomes?id=${outcomeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveNotification({ show: true, message: data.error || 'Silme hatası', type: 'error' });
        setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
        return false;
      }
      await loadProgramOutcomes();
      setSaveNotification({ show: true, message: 'Silindi', type: 'success' });
      setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
      return true;
    } catch (e) {
      setSaveNotification({ show: true, message: 'Silme sırasında bir hata oluştu', type: 'error' });
      setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [programId, tokenMemo, loadProgramOutcomes]);

  const copyPreviousYearProgramOutcomes = React.useCallback(async () => {
    if (!programId || !tokenMemo || isCopyingPreviousOutcomes) return;
    if (!window.confirm(
      'En yakın önceki yılın program çıktıları seçili hazırlık yılına bağımsız taslak olarak kopyalanacak. ' +
      'Eski yıl kayıtları ve hiçbir PÇ eşleştirmesi değişmeyecek. Devam edilsin mi?'
    )) return;
    setIsCopyingPreviousOutcomes(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/program-outcomes/copy-previous`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenMemo}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveNotification({ show: true, message: data.error || 'Önceki yıl PÇ’leri kopyalanamadı', type: 'error' });
        return;
      }
      await loadProgramOutcomes();
      setSaveNotification({
        show: true,
        message: `${data.source_year} yılından ${data.copied_count} PÇ taslak olarak kopyalandı`,
        type: 'success'
      });
    } catch (e) {
      setSaveNotification({ show: true, message: 'Önceki yıl PÇ’leri kopyalanamadı', type: 'error' });
    } finally {
      setIsCopyingPreviousOutcomes(false);
      setTimeout(() => setSaveNotification({ show: false, message: '', type: 'success' }), 5000);
    }
  }, [programId, tokenMemo, isCopyingPreviousOutcomes, loadProgramOutcomes]);

  // selectedSection değiştiğinde içeriği yükle
  React.useEffect(() => {
    if (selectedSection) {
      // Programın Öğrenme Çıktıları section'ı için program_outcomes yükle
      if (selectedSection.slug === 'programin-ogrenme-ciktilari') {
        loadProgramOutcomes();
      } else if (selectedSection.slug === 'secmeli-dersler') {
        fetchElectiveLessons();
        fetchProgramGroups(); // Load available groups
      } else {
        loadContent(selectedSection);
        // Blocks initialize edildiğini temizle (yeni section yükleniyor)
        blocksInitializedRef.current.clear();
      }
    } else {
      // Section seçilmemişse içeriği temizle
      setContentHtml('');
      setBlocks([]);
      setProgramOutcomes([]);
      blocksInitializedRef.current.clear();
      const el = document.getElementById('admin-v2-editor');
      if (el) el.innerHTML = '';
    }
  }, [selectedSection, loadContent, loadProgramOutcomes]);

  // blockMode değiştiğinde mevcut içeriği yeniden yükle
  React.useEffect(() => {
    if (selectedSection && contentHtml) {
      // Mevcut içeriği yeni moda göre yeniden yükle
      const html = contentHtml;

      if (!blockMode) {
        // Normal editör moduna geçildiğinde
        const el = document.getElementById('admin-v2-editor');
        if (el) el.innerHTML = rewriteUploadUrls(html);
      } else {
        // Block moduna geçildiğinde HTML'i parse et
        const parser = new DOMParser();
        const doc = parser.parseFromString(html || '<p></p>', 'text/html');
        const body = doc.body;
        const newBlocks = [];

        Array.from(body.childNodes).forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IMG') {
              const src = node.getAttribute('src');
              const widthMatch = node.style.width ? node.style.width.match(/(\d+)%/) : null;
              const widthPct = widthMatch ? parseInt(widthMatch[1], 10) : 100;
              newBlocks.push({
                id: String(Date.now() + Math.random()),
                type: 'image',
                src: src,
                widthPct: widthPct
              });
            } else if (node.tagName === 'P' || node.tagName === 'DIV') {
              const innerHtml = node.innerHTML || '';
              if (innerHtml.trim() || node.textContent.trim()) {
                newBlocks.push({
                  id: String(Date.now() + Math.random()),
                  type: 'text',
                  html: innerHtml || `<p>${node.textContent || ''}</p>`
                });
              }
            } else {
              const innerHtml = node.outerHTML || '';
              if (innerHtml.trim() || node.textContent.trim()) {
                newBlocks.push({
                  id: String(Date.now() + Math.random()),
                  type: 'text',
                  html: innerHtml
                });
              }
            }
          }
        });

        if (newBlocks.length === 0 && html.trim()) {
          newBlocks.push({
            id: String(Date.now()),
            type: 'text',
            html: html
          });
        }

        setBlocks(newBlocks);
      }
    }
  }, [blockMode, selectedSection, contentHtml]);

  // Block mode içeriklerini contentEditable div'lere yükle (sadece ilk yükleme için)
  React.useEffect(() => {
    if (!blockMode) {
      blocksInitializedRef.current.clear();
      return;
    }

    blocks.forEach(block => {
      if (block && block.type === 'text' && block.id) {
        const el = blockRefs.current[block.id];
        if (el && el.innerHTML !== undefined) {
          // Sadece daha önce initialize edilmemişse içeriği yükle
          if (!blocksInitializedRef.current.has(block.id)) {
            const html = rewriteUploadUrls(block.html || '<p></p>');
            try {
              if (el.innerHTML !== html) {
                el.innerHTML = html;
              }
              blocksInitializedRef.current.add(block.id);
            } catch (e) {
              console.error('Block içerik yükleme hatası:', e);
            }
          }
        }
      }
    });
  }, [blocks, blockMode]);

  // 4) Kaydet
  const saveContent = React.useCallback(async () => {
    if (!programId || !selectedSection) return;
    try {
      setIsSaving(true);
      let latestHtml = contentHtml;
      if (blockMode) {
        latestHtml = (blocks || []).map(b => {
          if (b.type === 'image') {
            const w = b.widthPct || 100;
            return `<p style=\"text-align:center\"><img src=\"${b.src}\" alt=\"\" style=\"max-width:100%;height:auto;width:${w}%\"/></p>`;
          }
          return `<div>${b.html || ''}</div>`;
        }).join('\n');
      } else {
        const el = document.getElementById('admin-v2-editor');
        latestHtml = el ? el.innerHTML : contentHtml;
      }
      latestHtml = relativizeUploadUrls(latestHtml);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/sections/${selectedSection.id}/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenMemo}` },
        body: JSON.stringify({ content_html: latestHtml })
      });
      if (!res.ok) {
        const d = await res.json();
        // Hata popup'ı göster
        setSaveNotification({ show: true, message: d.error || 'Kaydetme hatası', type: 'error' });
        setTimeout(() => {
          setSaveNotification({ show: false, message: '', type: 'success' });
        }, 5000);
      } else {
        setContentHtml(latestHtml);
        // Başarılı popup'ı göster
        setSaveNotification({ show: true, message: 'Kaydedildi', type: 'success' });
        setTimeout(() => {
          setSaveNotification({ show: false, message: '', type: 'success' });
        }, 5000);
      }
    } catch (e) {
      // Hata popup'ı göster
      setSaveNotification({ show: true, message: 'Kaydetme sırasında bir hata oluştu', type: 'error' });
      setTimeout(() => {
        setSaveNotification({ show: false, message: '', type: 'success' });
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  }, [programId, selectedSection, contentHtml, tokenMemo, blockMode, blocks]);

  // Autosave (2s)
  React.useEffect(() => {
    if (!autosaveEnabled) return;
    if (!selectedSection || !programId) return;
    const h = setTimeout(() => { saveContent(); }, 2000);
    return () => clearTimeout(h);
  }, [contentHtml, autosaveEnabled, selectedSection, programId, saveContent]);

  // 5) Editör fonksiyonları - Sıfırdan yazılmış basit ve çalışan versiyon

  // Seçimi kaydet ve geri yükle yardımcı fonksiyonları
  const saveSelection = () => {
    const el = document.getElementById('admin-v2-editor');
    if (!el) return null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      try {
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;
        if (container && el.contains(container)) {
          lastRangeRef.current = range.cloneRange();
          return lastRangeRef.current;
        }
      } catch (e) { }
    }
    return lastRangeRef.current;
  };

  const restoreSelection = (range) => {
    const el = document.getElementById('admin-v2-editor');
    if (!el) return;
    const sel = window.getSelection();
    const targetRange = range || lastRangeRef.current;
    if (targetRange) {
      try {
        const container = targetRange.startContainer.nodeType === 1
          ? targetRange.startContainer
          : targetRange.startContainer.parentElement;
        if (container && el.contains(container)) {
          sel.removeAllRanges();
          sel.addRange(targetRange);
          return true;
        }
      } catch (e) { }
    }
    return false;
  };

  // Block mode için seçim kaydetme
  const saveBlockSelection = (blockId) => {
    const el = blockRefs.current[blockId];
    if (!el) return null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      try {
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;
        if (container && el.contains(container)) {
          blockSelectionRef.current[blockId] = range.cloneRange();
          return blockSelectionRef.current[blockId];
        }
      } catch (e) { }
    }
    return blockSelectionRef.current[blockId];
  };

  const restoreBlockSelection = (blockId, range) => {
    const el = blockRefs.current[blockId];
    if (!el) return false;
    const sel = window.getSelection();
    const targetRange = range || blockSelectionRef.current[blockId];
    if (targetRange) {
      try {
        const container = targetRange.startContainer.nodeType === 1
          ? targetRange.startContainer
          : targetRange.startContainer.parentElement;
        if (container && el.contains(container)) {
          sel.removeAllRanges();
          sel.addRange(targetRange);
          return true;
        }
      } catch (e) { }
    }
    return false;
  };

  // Toolbar komutları - Basit ve çalışan versiyon
  const applyCmd = (cmd, val = null) => {
    if (blockMode && activeBlockId) {
      // Block modu için contentEditable işlemleri
      const el = blockRefs.current[activeBlockId];
      if (!el || !el.isContentEditable) return;

      // Önce seçimi kaydet
      const savedRange = saveBlockSelection(activeBlockId);

      // Editöre focus ver
      el.focus();

      // Kısa bir gecikme ile komutu uygula
      setTimeout(() => {
        // Seçimi geri yükle
        if (!restoreBlockSelection(activeBlockId, savedRange)) {
          // Seçim geri yüklenemezse editörün sonuna git
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }

        const sel = window.getSelection();

        // Font name için özel işlem
        if (cmd === 'fontName' && val && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          if (range.collapsed) {
            const span = document.createElement('span');
            span.style.fontFamily = val;
            const textNode = document.createTextNode('');
            span.appendChild(textNode);
            range.insertNode(span);
            const newRange = document.createRange();
            newRange.setStart(textNode, 0);
            newRange.setEnd(textNode, 0);
            sel.removeAllRanges();
            sel.addRange(newRange);
          } else {
            const contents = range.extractContents();
            const span = document.createElement('span');
            span.style.fontFamily = val;
            span.appendChild(contents);
            range.insertNode(span);
            const newRange = document.createRange();
            newRange.selectNodeContents(span);
            newRange.collapse(false);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        } else {
          // Diğer tüm komutlar için document.execCommand
          document.execCommand(cmd, false, val);
        }

        // İçeriği güncelle
        setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));

        // Seçimi tekrar kaydet
        saveBlockSelection(activeBlockId);
      }, 50);
      return;
    }

    // Normal editör modu (contentEditable)
    const el = document.getElementById('admin-v2-editor');
    if (!el) return;

    // Önce seçimi kaydet
    const savedRange = saveSelection();

    // Editöre focus ver
    el.focus();

    // Kısa bir gecikme ile komutu uygula (buton tıklaması tamamlanana kadar bekle)
    setTimeout(() => {
      // Seçimi geri yükle
      if (!restoreSelection(savedRange)) {
        // Seçim geri yüklenemezse editörün sonuna git
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }

      const sel = window.getSelection();

      // Font name için özel işlem
      if (cmd === 'fontName' && val && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          const span = document.createElement('span');
          span.style.fontFamily = val;
          const textNode = document.createTextNode('');
          span.appendChild(textNode);
          range.insertNode(span);
          const newRange = document.createRange();
          newRange.setStart(textNode, 0);
          newRange.setEnd(textNode, 0);
          sel.removeAllRanges();
          sel.addRange(newRange);
        } else {
          const contents = range.extractContents();
          const span = document.createElement('span');
          span.style.fontFamily = val;
          span.appendChild(contents);
          range.insertNode(span);
          const newRange = document.createRange();
          newRange.selectNodeContents(span);
          newRange.collapse(false);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      } else {
        // Diğer tüm komutlar için document.execCommand
        document.execCommand(cmd, false, val);
      }

      // İçeriği güncelle
      setContentHtml(el.innerHTML);

      // Seçimi tekrar kaydet
      saveSelection();
    }, 50);
  };

  // Upload fonksiyonları
  const insertHtmlAtCursor = (html) => {
    if (blockMode && activeBlockId) {
      // Block mode için aktif blok içine ekle
      const el = blockRefs.current[activeBlockId];
      if (!el || !el.isContentEditable) {
        // Eğer aktif blok yoksa veya contentEditable değilse, yeni bir text bloğu oluştur
        const newBlockId = String(Date.now());
        setBlocks(prev => [...prev, {
          id: newBlockId,
          type: 'text',
          html: html
        }]);
        setTimeout(() => {
          const newEl = blockRefs.current[newBlockId];
          if (newEl) {
            newEl.focus();
            setActiveBlockId(newBlockId);
          }
        }, 100);
        return;
      }

      el.focus();
      const savedRange = saveBlockSelection(activeBlockId);
      setTimeout(() => {
        restoreBlockSelection(activeBlockId, savedRange);
        if (document.queryCommandSupported('insertHTML')) {
          document.execCommand('insertHTML', false, html);
        } else {
          const sel = window.getSelection();
          if (sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const div = document.createElement('div');
            div.innerHTML = html;
            const fragment = document.createDocumentFragment();
            while (div.firstChild) {
              fragment.appendChild(div.firstChild);
            }
            range.insertNode(fragment);
          }
        }
        setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
        saveBlockSelection(activeBlockId);
      }, 10);
    } else {
      // Normal editör modu
      const el = document.getElementById('admin-v2-editor');
      if (!el) return;
      el.focus();
      const savedRange = saveSelection();
      setTimeout(() => {
        restoreSelection(savedRange);
        if (document.queryCommandSupported('insertHTML')) {
          document.execCommand('insertHTML', false, html);
        } else {
          const sel = window.getSelection();
          if (sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            const div = document.createElement('div');
            div.innerHTML = html;
            const fragment = document.createDocumentFragment();
            while (div.firstChild) {
              fragment.appendChild(div.firstChild);
            }
            range.insertNode(fragment);
          }
        }
        setContentHtml(el.innerHTML);
      }, 10);
    }
  };

  const onUpload = async (kind, file) => {
    if (!programId) {
      alert('Program seçilmedi. Lütfen önce bir program seçin.');
      return;
    }
    if (!file) {
      alert('Dosya seçilmedi.');
      return;
    }

    // Yükleme başlıyor mesajı (isteğe bağlı)
    const form = new FormData();
    form.append('file', file);
    const url = `${API_BASE_URL}/api/uploads/${kind === 'image' ? 'images' : 'files'}?program_id=${programId}`;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenMemo}` },
        body: form
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Yükleme başarısız oldu' }));
        alert(errorData.error || 'Yükleme hatası');
        return;
      }

      const data = await res.json();

      if (kind === 'image') {
        if (blockMode) {
          // Block mode için - her zaman aktif text block'un içine ekle
          let targetBlockId = activeBlockId;

          setBlocks(prev => {
            // Aktif block'u bul
            const activeBlock = prev.find(b => b.id === activeBlockId);

            if (activeBlock && activeBlock.type === 'text') {
              // Aktif text block varsa, onu kullan
              targetBlockId = activeBlockId;
              return prev;
            } else {
              // Aktif block yoksa veya text değilse, son text block'u bul
              const textBlocks = prev.filter(b => b.type === 'text');
              if (textBlocks.length > 0) {
                // Son text block'u kullan
                targetBlockId = textBlocks[textBlocks.length - 1].id;
                return prev;
              } else {
                // Hiç text block yoksa yeni bir text block oluştur
                const newBlockId = String(Date.now());
                targetBlockId = newBlockId;
                return [...prev, {
                  id: newBlockId,
                  type: 'text',
                  html: '<p></p>'
                }];
              }
            }
          });

          // Block mode'da görsel ekleme işlemi (setBlocks'tan sonra)
          setTimeout(() => {
            if (targetBlockId) {
              setActiveBlockId(targetBlockId);
              setTimeout(() => {
                insertHtmlAtCursor(`<img src="${toAbsoluteUploadUrl(data.url)}" alt="" style="max-width:100%; height:auto; width:100%;" />`);
              }, 150);
            }
          }, 50);
        } else {
          // Normal editör modu için cursor pozisyonuna ekle
          insertHtmlAtCursor(`<img src="${toAbsoluteUploadUrl(data.url)}" alt="" style="max-width:100%; height:auto; width:100%;" />`);
        }
      } else {
        // Dosya için - her zaman aktif bloğa veya cursor pozisyonuna ekle
        insertHtmlAtCursor(`<a href="${toAbsoluteUploadUrl(data.url)}" target="_blank" rel="noopener noreferrer">${data.fileName || 'dosya'}</a>`);
      }
    } catch (e) {
      console.error('Yükleme hatası:', e);
      alert('Yükleme hatası: ' + (e.message || 'Bilinmeyen hata'));
    }
  };

  // Yardımcı fonksiyonlar
  const findReplace = async () => {
    const find = prompt('Bul:');
    if (!find) return;
    const replace = prompt('Değiştir (boş=yalnızca vurgula)');
    const el = document.getElementById('admin-v2-editor');
    if (!el || replace === null) return;
    if (replace === '') {
      const regex = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      el.innerHTML = (el.innerHTML || '').replace(regex, m => `<mark>${m}</mark>`);
    } else {
      const regex = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      el.innerHTML = (el.innerHTML || '').replace(regex, replace);
    }
    setContentHtml(el.innerHTML);
  };

  const insertTable = () => {
    const r = Math.max(1, parseInt(prompt('Satır sayısı', '2') || '2', 10));
    const c = Math.max(1, parseInt(prompt('Sütun sayısı', '2') || '2', 10));
    let html = '<table style="width:100%; border-collapse:collapse;">';
    for (let i = 0; i < r; i++) {
      html += '<tr>';
      for (let j = 0; j < c; j++) {
        html += '<td style="border:1px solid #e5e7eb; padding:8px;">&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</table>';
    insertHtmlAtCursor(html);
  };

  const insertChecklist = () => {
    insertHtmlAtCursor('<ul style="list-style:none; padding-left:0;"><li><input type="checkbox"/> Görev 1</li><li><input type="checkbox"/> Görev 2</li></ul>');
  };

  // Font size uygulama
  const applyFontSizePx = () => {
    const size = parseInt(fontSizePx, 10);
    if (!size || Number.isNaN(size)) return;

    if (blockMode && activeBlockId) {
      // Block mode için
      const el = blockRefs.current[activeBlockId];
      if (!el || !el.isContentEditable) return;
      el.focus();
      const savedRange = saveBlockSelection(activeBlockId);
      setTimeout(() => {
        if (!restoreBlockSelection(activeBlockId, savedRange)) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          const span = document.createElement('span');
          span.style.fontSize = `${size}px`;
          const textNode = document.createTextNode('');
          span.appendChild(textNode);
          range.insertNode(span);
          const newRange = document.createRange();
          newRange.setStart(textNode, 0);
          newRange.setEnd(textNode, 0);
          sel.removeAllRanges();
          sel.addRange(newRange);
        } else {
          const contents = range.extractContents();
          const span = document.createElement('span');
          span.style.fontSize = `${size}px`;
          span.appendChild(contents);
          range.insertNode(span);
          const newRange = document.createRange();
          newRange.selectNodeContents(span);
          newRange.collapse(false);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
        setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
        saveBlockSelection(activeBlockId);
      }, 50);
      return;
    }

    // Normal editör modu
    const el = document.getElementById('admin-v2-editor');
    if (!el) return;
    el.focus();
    const savedRange = saveSelection();
    setTimeout(() => {
      if (!restoreSelection(savedRange)) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (range.collapsed) {
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        const textNode = document.createTextNode('');
        span.appendChild(textNode);
        range.insertNode(span);
        const newRange = document.createRange();
        newRange.setStart(textNode, 0);
        newRange.setEnd(textNode, 0);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        const contents = range.extractContents();
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        span.appendChild(contents);
        range.insertNode(span);
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        newRange.collapse(false);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
      setContentHtml(el.innerHTML);
      saveSelection();
    }, 50);
  };

  const scheduleApplyFontSize = (value) => {
    setFontSizePx(value);
    if (fontSizeDebounceRef.current) clearTimeout(fontSizeDebounceRef.current);
    fontSizeDebounceRef.current = setTimeout(() => {
      applyFontSizePx();
    }, 250);
  };

  // Seçim takibi - Normal editör modu
  React.useEffect(() => {
    if (blockMode) return;
    const editor = document.getElementById('admin-v2-editor');
    if (!editor) return;
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement;
      if (container && editor.contains(container)) {
        try {
          lastRangeRef.current = range.cloneRange();
        } catch (e) { }
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [selectedSection, blockMode]);

  // Seçim takibi - Block mode
  React.useEffect(() => {
    if (!blockMode || !activeBlockId) return;
    const editor = blockRefs.current[activeBlockId];
    if (!editor) return;
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement;
      if (container && editor.contains(container)) {
        try {
          blockSelectionRef.current[activeBlockId] = range.cloneRange();
          // Seçili görseli kontrol et
          findSelectedImage();
        } catch (e) { }
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [blockMode, activeBlockId]);

  // Seçim takibi - Normal editör modu (görsel seçimi için)
  React.useEffect(() => {
    if (blockMode) return;
    const editor = document.getElementById('admin-v2-editor');
    if (!editor) return;
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement;
      if (container && editor.contains(container)) {
        findSelectedImage();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [blockMode]);

  // Paragraf aralığı ve satır aralığı
  const [lineSpacing, setLineSpacing] = React.useState('1.7');
  const [spaceBefore, setSpaceBefore] = React.useState('0');
  const [spaceAfter, setSpaceAfter] = React.useState('0');
  const applyParagraphSpacing = () => {
    const el = document.getElementById('admin-v2-editor');
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
    const block = node && (node.closest('p,div,li,td,th') || node);
    if (block && block.style) {
      block.style.lineHeight = lineSpacing;
      block.style.marginTop = `${parseFloat(spaceBefore) || 0}px`;
      block.style.marginBottom = `${parseFloat(spaceAfter) || 0}px`;
      setContentHtml(el.innerHTML);
    }
  };

  // Seçili görseli bul ve state'e kaydet
  const findSelectedImage = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setSelectedImgInfo(prev => ({ ...prev, el: null }));
      return null;
    }

    const node = sel.anchorNode && sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
    const img = node && node.closest && node.closest('img');

    if (img) {
      // Mevcut genişliği al - önce style'dan, yoksa computed style'dan
      let widthPct = 100;

      // Inline style kontrolü
      if (img.style.width) {
        const widthMatch = img.style.width.match(/(\d+)%?/);
        if (widthMatch) {
          widthPct = parseInt(widthMatch[1], 10);
        }
      } else {
        // Computed style kontrolü
        const computedStyle = window.getComputedStyle(img);
        const width = computedStyle.width;
        if (width) {
          // Parent genişliğine göre yüzde hesapla
          const parent = img.parentElement;
          if (parent) {
            const parentWidth = parent.offsetWidth || parent.clientWidth;
            if (parentWidth > 0) {
              const imgWidth = parseFloat(width);
              widthPct = Math.round((imgWidth / parentWidth) * 100);
            }
          }
        }
      }

      setSelectedImgInfo({ el: img, widthPct });
      return { img, widthPct };
    } else {
      // Görsel seçili değilse state'i sıfırlama, sadece el'i null yap
      setSelectedImgInfo(prev => prev.el ? { ...prev, el: null } : prev);
    }
    return null;
  };

  // Görsel hizalama ve boyut
  const alignImage = (dir) => {
    if (blockMode && activeBlockId) {
      // Block mode için
      const el = blockRefs.current[activeBlockId];
      if (!el || !el.isContentEditable) return;
      const imgInfo = findSelectedImage();
      if (imgInfo && imgInfo.img) {
        const img = imgInfo.img;
        img.style.display = 'inline-block';
        img.style.float = dir === 'left' ? 'left' : dir === 'right' ? 'right' : 'none';
        if (dir === 'center') {
          img.style.float = 'none';
          img.style.display = 'block';
          img.style.margin = '12px auto';
        }
        setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
      }
    } else {
      // Normal editör modu
      const el = document.getElementById('admin-v2-editor');
      if (!el) return;
      const imgInfo = findSelectedImage();
      if (imgInfo && imgInfo.img) {
        const img = imgInfo.img;
        img.style.display = 'inline-block';
        img.style.float = dir === 'left' ? 'left' : dir === 'right' ? 'right' : 'none';
        if (dir === 'center') {
          img.style.float = 'none';
          img.style.display = 'block';
          img.style.margin = '12px auto';
        }
        setContentHtml(el.innerHTML);
      }
    }
  };

  const setImageWidth = (percent) => {
    let img = null;

    if (blockMode && activeBlockId) {
      // Block mode için - önce aktif block'taki görseli bul
      const el = blockRefs.current[activeBlockId];
      if (el && el.isContentEditable) {
        // Seçimden görseli bul
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const node = sel.anchorNode && sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
          img = node && node.closest && node.closest('img');
        }

        // Eğer seçimden bulamazsa, state'ten dene
        if (!img && selectedImgInfo?.el) {
          // State'teki görsel hala aktif block içinde mi kontrol et
          if (el.contains(selectedImgInfo.el)) {
            img = selectedImgInfo.el;
          }
        }

        // Hala bulamazsa, aktif block içindeki tüm görselleri kontrol et
        if (!img) {
          const allImages = el.querySelectorAll('img');
          if (allImages.length > 0) {
            // Son görseli veya ilk görseli kullan
            img = allImages[allImages.length - 1];
          }
        }

        if (img) {
          // Görseli güncelle
          img.style.width = `${percent}%`;
          img.style.height = 'auto';
          setSelectedImgInfo({ el: img, widthPct: percent });

          // HTML'i güncelle
          setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
        }
      }
    } else {
      // Normal editör modu
      const el = document.getElementById('admin-v2-editor');
      if (!el) return;

      // Seçimden görseli bul
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const node = sel.anchorNode && sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        img = node && node.closest && node.closest('img');
      }

      // Eğer seçimden bulamazsa, state'ten dene
      if (!img && selectedImgInfo?.el && el.contains(selectedImgInfo.el)) {
        img = selectedImgInfo.el;
      }

      if (img) {
        // Görseli güncelle
        img.style.width = `${percent}%`;
        img.style.height = 'auto';
        setSelectedImgInfo({ el: img, widthPct: percent });
        setContentHtml(el.innerHTML);
      }
    }
  };

  const updateSection = async (sectionId, patch) => {
    if (!programId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/sections/${sectionId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenMemo}` }, body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Güncelleme hatası'); return; }
      fetchSections(programId);
    } catch (e) { console.error(e); alert('Hata'); }
  };

  const persistOrder = async (ordered) => {
    try {
      const order = ordered.map(s => s.id);
      const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/sections/order`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenMemo}` }, body: JSON.stringify({ order })
      });
      if (!res.ok) {
        const d = await res.json();
        alert(d.error || 'Sıralama kaydedilemedi');
      }
    } catch (e) { console.error(e); }
  };

  const reorderIndex = (idx, direction) => {
    const toIdx = idx + direction;
    if (toIdx < 0 || toIdx >= sections.length) return;
    const next = sections.slice();
    const tmp = next[idx];
    next[idx] = next[toIdx];
    next[toIdx] = tmp;
    setSections(next);
    persistOrder(next);
  };

  const programSlug = (programName || '').toLowerCase()
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
    .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-').replace(/-+/g, '-');

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">{electivesOnly ? 'Seçmeli Dersler Yönetimi' : 'Program Sidebar Yönetimi'}</span>
              </div>
            </Link>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {programName && (
              <div className="management-program-context" title={programName}>
                <span>Yönetilen Program</span>
                <strong>{programName}</strong>
              </div>
            )}
            {hasDeanRole && (
              <button
                type="button"
                onClick={() => navigate('/university-admin/dashboard')}
                className="management-header-button active"
              >
                Program Yönetimi
              </button>
            )}
            <span className="management-role-badge">
              {hasDeanRole ? getStoredUniversityRoleLabel() : 'Program Başkanı'}
            </span>
            {onLogout && (
              <button onClick={() => { onLogout(); }} className="logout-button">Çıkış Yap</button>
            )}
          </div>
        </div>
      </div>
      <div className="main-content admin-editor-container">
        <ProgramAdminSidebar
          programId={programId}
          programName={programName}
          programSlug={programSlug}
          activeKey={electivesOnly ? 'electives' : 'sidebar'}
          navigate={navigate}
        />
        {!electivesOnly && (
        <div className="admin-editor-left">
          <div className="form-group">
            <input type="text" placeholder="Başlık ara..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
          </div>
          {loading ? (
            <div>Yükleniyor...</div>
          ) : (
            <div className="faculties-list">
              {sections
                .filter(s => !filterText || (s.title || '').toLowerCase().includes(filterText.toLowerCase()))
                .map((s, idx) => (
                <div
                  key={s.id}
                  className={`faculty-item ${selectedSection && selectedSection.id === s.id ? 'selected' : ''}`}
                  style={s.slug === 'ders-plani-icerik' ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
                  onClick={() => {
                    if (s.slug === 'ders-plani-icerik') {
                      // Program baskani sidebar editorunde bu baslik sadece gorunsun;
                      // tiklandiginda baska sayfaya yonlendirme yapma.
                      return;
                    }
                    setSelectedSection(s);
                  }}
                >
                  <div>
                    <span className="faculty-name">{s.title}</span>
                    <span className="list-item-subtitle">{s.slug}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="icon-btn" title="Yukarı Taşı" onClick={(e) => { e.stopPropagation(); reorderIndex(idx, -1); }}>↑</button>
                    <button className="icon-btn" title="Aşağı Taşı" onClick={(e) => { e.stopPropagation(); reorderIndex(idx, 1); }}>↓</button>
                    <button className="icon-btn" title="Gizle/Göster" onClick={(e) => { e.stopPropagation(); updateSection(s.id, { is_visible: !s.is_visible }); }}>{s.is_visible ? 'Gizle' : 'Göster'}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
        <div className="admin-editor-right">
          {/* Kaydetme bildirimi popup */}
          {saveNotification.show && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 10000,
              backgroundColor: saveNotification.type === 'success' ? '#10b981' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              fontWeight: '500',
              minWidth: '200px',
              animation: 'slideIn 0.3s ease-out'
            }}>
              <span style={{ fontSize: '14px', fontWeight: 700 }}>{saveNotification.type === 'success' ? 'Tamam' : 'Hata'}</span>
              <span>{saveNotification.message}</span>
            </div>
          )}
          {selectedSection ? (
            <>
              <div className="admin-header-bar">
                <div className="admin-header-title">
                  <span className="chip">Program</span>
                  <strong>{electivesOnly ? 'Seçmeli Dersler' : (sections && sections.length > 0 ? 'Sidebar Düzenleme' : 'Bölümler')}</strong>
                  {!electivesOnly && selectedSection && <span style={{ color: '#6b7280' }}>• {selectedSection.title}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedSection.slug === 'programin-ogrenme-ciktilari' ? (
                    <>
                    {planningProgramOutcomes && programOutcomes.length === 0 && (
                      <button
                        className="save-button"
                        onClick={copyPreviousYearProgramOutcomes}
                        disabled={isCopyingPreviousOutcomes || isSaving}
                        style={{ background: '#2563eb' }}
                      >
                        {isCopyingPreviousOutcomes ? 'Kopyalanıyor...' : 'Önceki Yıl PÇ’lerini Taslak Kopyala'}
                      </button>
                    )}
                    <button
                      className="save-button"
                      onClick={() => {
                        // En yüksek numarayı bul ve bir sonraki numarayı set et
                        let nextNumber = 1;
                        if (programOutcomes.length > 0) {
                          const numbers = programOutcomes
                            .map(o => {
                              const num = parseInt(o.number);
                              return isNaN(num) ? 0 : num;
                            })
                            .filter(n => n > 0);
                          if (numbers.length > 0) {
                            nextNumber = Math.max(...numbers) + 1;
                          }
                        }
                        setOutcomeFormData({ number: String(nextNumber), name: '' });
                        setEditingOutcome(null);
                        setShowOutcomeForm(true);
                      }}
                      disabled={isSaving}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: 6,
                        background: isSaving ? '#9ca3af' : '#10b981',
                        color: '#fff',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontWeight: 500
                      }}
                    >
                      + Yeni Program Çıktısı
                    </button>
                    </>
                  ) : (
                    <button className="save-button" onClick={saveContent} disabled={isSaving}>
                      {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  )}
                </div>
              </div>
              {selectedSection.slug === 'secmeli-dersler' ? (
                <div className="editor-panel" style={{ padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3>Seçmeli Dersler</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handlePromoteToGroup}
                        disabled={selectedLessonIds.length !== 1}
                        style={{
                          background: selectedLessonIds.length === 1 ? '#8b5cf6' : 'var(--gray-300)',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: 6,
                          cursor: selectedLessonIds.length === 1 ? 'pointer' : 'not-allowed',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        Havuz Yap
                      </button>
                      <button
                        onClick={() => setShowLinkModal(true)}
                        disabled={selectedLessonIds.length === 0}
                        style={{
                          background: selectedLessonIds.length > 0 ? '#2563eb' : 'var(--gray-300)',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: 6,
                          cursor: selectedLessonIds.length > 0 ? 'pointer' : 'not-allowed',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        Havuza Ata ({selectedLessonIds.length})
                      </button>
                    </div>
                  </div>

                  {loadingElectives ? (
                    <div>Yükleniyor...</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                          <tr style={{ background: 'var(--gray-50)', textAlign: 'left' }}>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', width: 40, textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedLessonIds(electiveLessons.map(l => l.id));
                                  else setSelectedLessonIds([]);
                                }}
                                checked={electiveLessons.length > 0 && selectedLessonIds.length === electiveLessons.length}
                              />
                            </th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>Ders Kodu</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>Ders Adı</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>Bağlı Olduğu Havuz</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>T</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>U</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>L</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>K</th>
                            <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>AKTS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {electiveLessons.length === 0 ? (
                            <tr>
                              <td colSpan="9" style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)' }}>
                                Henüz seçmeli ders bulunmamaktadır.
                              </td>
                            </tr>
                          ) : (
                            electiveLessons.map((lesson) => (
                              <tr key={lesson.id} style={{ borderBottom: '1px solid var(--gray-200)', background: selectedLessonIds.includes(lesson.id) ? '#eff6ff' : 'transparent' }}>
                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedLessonIds.includes(lesson.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedLessonIds(prev => [...prev, lesson.id]);
                                      else setSelectedLessonIds(prev => prev.filter(id => id !== lesson.id));
                                    }}
                                  />
                                </td>
                                <td style={{ padding: '8px 12px' }}>{lesson.course_code}</td>
                                <td style={{ padding: '8px 12px' }}>{lesson.course_name}</td>
                                <td style={{ padding: '8px 12px' }}>
                                  {lesson.course_id ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ color: '#2563eb', fontWeight: 500 }}>
                                        {programGroups.find(g => g.id === lesson.course_id)?.course_name || `Grup #${lesson.course_id}`}
                                      </span>
                                      <button
                                        onClick={() => handleUnlinkLesson(lesson.id)}
                                        style={{ fontSize: 10, color: 'white', background: 'var(--danger-color)', border: 'none', borderRadius: 3, padding: '2px 4px', cursor: 'pointer' }}
                                        title="Havuzdan Çıkar"
                                      >
                                        x
                                      </button>
                                    </div>
                                  ) : <span style={{ color: 'var(--gray-400)' }}>-</span>}
                                </td>
                                <td style={{ padding: '8px 12px' }}>{lesson.teorik}</td>
                                <td style={{ padding: '8px 12px' }}>{lesson.uygulama}</td>
                                <td style={{ padding: '8px 12px' }}>{lesson.laboratuvar}</td>
                                <td style={{ padding: '8px 12px' }}>{lesson.kredi}</td>
                                <td style={{ padding: '8px 12px' }}>{lesson.akts}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Varolan Havuzlar Tablosu */}
                  <div style={{ marginTop: 32 }}>
                    <h3 style={{ marginBottom: 12 }}>Varolan Havuzlar</h3>
                    {programGroups.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', background: 'var(--gray-50)', borderRadius: 6 }}>
                        Henüz tanımlı havuz bulunmamaktadır.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                          <thead>
                            <tr style={{ background: 'var(--gray-50)', textAlign: 'left' }}>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', width: 36 }}></th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>Ders Kodu</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)' }}>Havuz Adı</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>Dönem</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>Bağlı Ders</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>T</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>U</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>L</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>K</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center' }}>AKTS</th>
                              <th style={{ padding: '8px 12px', borderBottom: '2px solid var(--gray-200)', textAlign: 'center', width: 80 }}>İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {programGroups.map(pool => {
                              const linkedLessons = electiveLessons.filter(l => l.course_id === pool.id);
                              const linkedCount = linkedLessons.length;
                              const isExpanded = expandedPoolIds.includes(pool.id);
                              const togglePool = () => {
                                setExpandedPoolIds(prev => prev.includes(pool.id) ? prev.filter(id => id !== pool.id) : [...prev, pool.id]);
                              };
                              return (
                                <React.Fragment key={`pool-${pool.id}`}>
                                  <tr
                                    onClick={togglePool}
                                    style={{ borderBottom: '1px solid var(--gray-200)', cursor: 'pointer', background: isExpanded ? 'var(--light-bg)' : 'transparent' }}
                                  >
                                    <td style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--gray-500)', fontWeight: 600 }}>
                                      {isExpanded ? '▾' : '▸'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{pool.course_code}</td>
                                    <td style={{ padding: '8px 12px' }}>{pool.course_name}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.donem}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                      <span style={{ background: linkedCount > 0 ? '#dbeafe' : 'var(--gray-100)', color: linkedCount > 0 ? '#1e40af' : 'var(--gray-500)', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500 }}>
                                        {linkedCount}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.teorik}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.uygulama}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.laboratuvar}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.kredi}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{pool.akts}</td>
                                    <td style={{ padding: '8px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => handleDeletePool(pool)}
                                        style={{ background: 'var(--danger-color)', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
                                        title="Havuzu sil (bağlı dersler korunur)"
                                      >
                                        Sil
                                      </button>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr style={{ background: 'var(--light-bg)', borderBottom: '1px solid var(--gray-200)' }}>
                                      <td colSpan="11" style={{ padding: '12px 16px 16px 48px' }}>
                                        {linkedLessons.length === 0 ? (
                                          <div style={{ color: 'var(--gray-500)', fontStyle: 'italic', fontSize: 13 }}>
                                            Bu havuza bağlı ders yok.
                                          </div>
                                        ) : (
                                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'white', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
                                            <thead>
                                              <tr style={{ background: '#f1f5f9' }}>
                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--gray-200)' }}>Ders Kodu</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--gray-200)' }}>Ders Adı</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)' }}>T</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)' }}>U</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)' }}>L</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)' }}>K</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)' }}>AKTS</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid var(--gray-200)', width: 110 }}>İşlem</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {linkedLessons.map(lesson => (
                                                <tr key={`linked-${lesson.id}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                  <td style={{ padding: '6px 10px', fontWeight: 500 }}>{lesson.course_code}</td>
                                                  <td style={{ padding: '6px 10px' }}>{lesson.course_name}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{lesson.teorik}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{lesson.uygulama}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{lesson.laboratuvar}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{lesson.kredi}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{lesson.akts}</td>
                                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                                    <button
                                                      onClick={() => handleUnlinkLesson(lesson.id)}
                                                      style={{ background: 'var(--warning-color)', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}
                                                      title="Dersi havuzdan çıkar"
                                                    >
                                                      Havuzdan Çıkar
                                                    </button>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Link Modal */}
                  {showLinkModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                      <div style={{ background: 'white', padding: 24, borderRadius: 8, width: 400, maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0 }}>Seçilen Dersleri Havuza Ata</h3>
                        <p style={{ color: '#666', fontSize: 14 }}>Seçilen {selectedLessonIds.length} dersi aşağıdaki havuza bağla:</p>

                        <select
                          value={selectedGroupId}
                          onChange={e => setSelectedGroupId(e.target.value)}
                          style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid var(--gray-300)', marginBottom: 20 }}
                        >
                          <option value="">-- Havuz Seçin --</option>
                          {programGroups.map(g => (
                            <option key={g.id} value={g.id}>
                              {g.course_code} - {g.course_name} ({g.donem}. Dönem)
                            </option>
                          ))}
                        </select>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                          <button type="button" onClick={() => setShowLinkModal(false)} style={{ padding: '8px 16px', border: '1px solid var(--gray-300)', background: 'white', borderRadius: 4, cursor: 'pointer' }}>İptal</button>
                          <button type="button" onClick={handleLinkLessons} disabled={!selectedGroupId} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: !selectedGroupId ? 0.6 : 1 }}>Bağla</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedSection.slug === 'programin-ogrenme-ciktilari' ? (
                <div className="editor-panel" style={{ position: 'relative', padding: 20 }}>
                  <h3 style={{ marginTop: 0, marginBottom: 16 }}>Program Öğrenme Çıktıları</h3>
                  {planningProgramOutcomes && (
                    <div style={{ marginBottom: 16, padding: 12, borderRadius: 6, background: '#eff6ff', color: '#1e3a8a' }}>
                      Hazırlık yılı PÇ’lerini düzenliyorsunuz. Buradaki ekleme, düzenleme ve silmeler eski akademik yıl kayıtlarını etkilemez;
                      ÖÇ–PÇ ve ders–PÇ eşleştirmeleri ayrıca bu yıl için yapılır.
                    </div>
                  )}

                  {/* Form Modal */}
                  {showOutcomeForm && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10000
                    }} onClick={() => { setShowOutcomeForm(false); setEditingOutcome(null); setOutcomeFormData({ number: '', name: '' }); }}>
                      <div style={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        padding: 24,
                        maxWidth: 500,
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                      }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>{editingOutcome ? 'Program Çıktısını Düzenle' : 'Yeni Program Çıktısı'}</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Numara *</label>
                            <input
                              type="text"
                              value={outcomeFormData.number}
                              onChange={(e) => setOutcomeFormData({ ...outcomeFormData, number: e.target.value })}
                              readOnly={!editingOutcome}
                              style={{
                                width: '100%',
                                padding: 8,
                                border: '1px solid #e5e7eb',
                                borderRadius: 4,
                                backgroundColor: editingOutcome ? '#fff' : '#f3f4f6',
                                cursor: editingOutcome ? 'text' : 'not-allowed'
                              }}
                              placeholder="Örn: 1, 2, 3..."
                            />
                            {!editingOutcome && (
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                Numara otomatik olarak belirlenir
                              </div>
                            )}
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Açıklama *</label>
                            <textarea
                              value={outcomeFormData.name}
                              onChange={(e) => setOutcomeFormData({ ...outcomeFormData, name: e.target.value })}
                              style={{ width: '100%', padding: 8, border: '1px solid #e5e7eb', borderRadius: 4, minHeight: 100 }}
                              placeholder="Program çıktısı açıklaması..."
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => { setShowOutcomeForm(false); setEditingOutcome(null); setOutcomeFormData({ number: '', name: '', contribution_level: 1 }); }}
                              style={{
                                padding: '8px 16px',
                                border: '1px solid #d1d5db',
                                borderRadius: 4,
                                background: '#f9fafb',
                                color: '#374151',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 500
                              }}
                            >
                              İptal
                            </button>
                            <button
                              onClick={() => {
                                if (!outcomeFormData.number.trim() || !outcomeFormData.name.trim()) {
                                  alert('Numara ve açıklama zorunludur');
                                  return;
                                }
                                saveProgramOutcome(editingOutcome ? { ...editingOutcome, ...outcomeFormData } : outcomeFormData);
                              }}
                              disabled={isSaving}
                              style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: 4,
                                background: isSaving ? '#9ca3af' : '#2563eb',
                                color: '#fff',
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                fontSize: 14,
                                fontWeight: 500
                              }}
                            >
                              {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {loadingOutcomes ? (
                    <div>Yükleniyor...</div>
                  ) : programOutcomes.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                      Henüz program çıktısı eklenmemiş. Yeni program çıktısı eklemek için yukarıdaki butona tıklayın.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {programOutcomes.map((outcome) => (
                        <div key={outcome.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: 16,
                          background: '#fff'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                {outcome.number}. {outcome.name}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => {
                                  setEditingOutcome(outcome);
                                  setOutcomeFormData({
                                    number: outcome.number,
                                    name: outcome.name
                                  });
                                  setShowOutcomeForm(true);
                                }}
                                style={{ padding: '6px 12px', border: '1px solid #3b82f6', borderRadius: 4, background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
                              >
                                Düzenle
                              </button>
                              <button
                                onClick={() => deleteProgramOutcome(outcome.id)}
                                style={{ padding: '6px 12px', border: '1px solid #ef4444', borderRadius: 4, background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
                              >
                                Sil
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="editor-panel" style={{ position: 'relative' }}>
                  <div className="editor-toolbar" style={{ position: 'sticky', top: 84, display: 'flex', flexWrap: 'wrap', gap: 8, padding: 10, alignItems: 'center', zIndex: 2 }}>
                    <select aria-label="Biçim" defaultValue="" onChange={(e) => applyCmd('formatBlock', e.target.value)}>
                      <option value="" disabled>Biçim</option>
                      <option value="p">Paragraf</option>
                      <option value="h1">Başlık 1</option>
                      <option value="h2">Başlık 2</option>
                      <option value="h3">Başlık 3</option>
                      <option value="blockquote">Alıntı</option>
                      <option value="pre">Kod Bloğu</option>
                    </select>
                    <select aria-label="Yazı Tipi" defaultValue="" onChange={(e) => { const v = e.target.value; if (v) applyCmd('fontName', v); }}>
                      <option value="" disabled>Yazı Tipi</option>
                      <option value="Arial">Arial</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Tahoma">Tahoma</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Courier New">Courier New</option>
                    </select>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <select aria-label="Punto (px)" value={fontSizePx} onChange={(e) => scheduleApplyFontSize(e.target.value)} onBlur={applyFontSizePx}>
                        <option value="10">10 px</option>
                        <option value="12">12 px</option>
                        <option value="14">14 px</option>
                        <option value="16">16 px</option>
                        <option value="18">18 px</option>
                        <option value="20">20 px</option>
                        <option value="22">22 px</option>
                        <option value="24">24 px</option>
                        <option value="28">28 px</option>
                        <option value="32">32 px</option>
                        <option value="36">36 px</option>
                        <option value="40">40 px</option>
                      </select>
                    </div>
                    <span className="toolbar-divider" />
                    <button className="tool-btn" type="button" title="Kalın" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); applyCmd('bold'); }}><b>B</b></button>
                    <button className="tool-btn" type="button" title="İtalik" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); applyCmd('italic'); }}><i>I</i></button>
                    <button className="tool-btn" type="button" title="Altı Çizili" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); applyCmd('underline'); }}><u>U</u></button>
                    <button className="tool-btn" type="button" title="Üst Simge" onMouseDown={(e) => { e.preventDefault(); applyCmd('superscript'); }}>x²</button>
                    <button className="tool-btn" type="button" title="Alt Simge" onMouseDown={(e) => { e.preventDefault(); applyCmd('subscript'); }}>x₂</button>
                    <button className="tool-btn" type="button" title="Biçimi Temizle" onMouseDown={(e) => { e.preventDefault(); applyCmd('removeFormat'); }}>Aa</button>
                    <button className="tool-btn" type="button" title="Madde İşaretli Liste" onMouseDown={(e) => { e.preventDefault(); applyCmd('insertUnorderedList'); }}>•</button>
                    <button className="tool-btn" type="button" title="Numaralı Liste" onMouseDown={(e) => { e.preventDefault(); applyCmd('insertOrderedList'); }}>1.</button>
                    <button className="tool-btn" type="button" title="Onay Kutulu Liste" onMouseDown={(e) => { e.preventDefault(); insertChecklist(); }}>Liste</button>
                    <span className="toolbar-divider" />
                    <button className="tool-btn" type="button" title="Sola" onMouseDown={(e) => { e.preventDefault(); applyCmd('justifyLeft'); }}>⟸</button>
                    <button className="tool-btn" type="button" title="Ortala" onMouseDown={(e) => { e.preventDefault(); applyCmd('justifyCenter'); }}>↔</button>
                    <button className="tool-btn" type="button" title="Sağa" onMouseDown={(e) => { e.preventDefault(); applyCmd('justifyRight'); }}>⟹</button>
                    <button className="tool-btn" type="button" title="İkiye Yasla" onMouseDown={(e) => { e.preventDefault(); applyCmd('justifyFull'); }}>≋</button>
                    <button className="tool-btn" type="button" title="Girinti Azalt" onMouseDown={(e) => { e.preventDefault(); applyCmd('outdent'); }}>⬅︎</button>
                    <button className="tool-btn" type="button" title="Girinti Artır" onMouseDown={(e) => { e.preventDefault(); applyCmd('indent'); }}>Girinti</button>
                    <button className="tool-btn" type="button" title="Link" onMouseDown={(e) => { e.preventDefault(); const url = prompt('Bağlantı URL'); if (url) applyCmd('createLink', url); }}>Bağlantı</button>
                    <span className="toolbar-divider" />
                    <label title="Metin Rengi" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 6px' }}>A
                      <input type="color" onChange={(e) => {
                        const v = e.target.value;
                        if (blockMode && activeBlockId) {
                          const el = blockRefs.current[activeBlockId];
                          if (el && el.isContentEditable) {
                            const savedRange = saveBlockSelection(activeBlockId);
                            el.focus();
                            setTimeout(() => {
                              if (restoreBlockSelection(activeBlockId, savedRange)) {
                                document.execCommand('foreColor', false, v);
                                setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
                                saveBlockSelection(activeBlockId);
                              }
                            }, 50);
                          }
                        } else {
                          const el = document.getElementById('admin-v2-editor');
                          if (el) {
                            const savedRange = saveSelection();
                            el.focus();
                            setTimeout(() => {
                              if (restoreSelection(savedRange)) {
                                document.execCommand('foreColor', false, v);
                                setContentHtml(el.innerHTML);
                                saveSelection();
                              }
                            }, 50);
                          }
                        }
                      }} />
                    </label>
                    <label title="Vurgu Rengi" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 6px' }}>Vurgu
                      <input type="color" onChange={(e) => {
                        const v = e.target.value;
                        if (blockMode && activeBlockId) {
                          const el = blockRefs.current[activeBlockId];
                          if (el && el.isContentEditable) {
                            const savedRange = saveBlockSelection(activeBlockId);
                            el.focus();
                            setTimeout(() => {
                              if (restoreBlockSelection(activeBlockId, savedRange)) {
                                document.execCommand('backColor', false, v);
                                setBlocks(prev => prev.map(x => x.id === activeBlockId ? { ...x, html: el.innerHTML } : x));
                                saveBlockSelection(activeBlockId);
                              }
                            }, 50);
                          }
                        } else {
                          const el = document.getElementById('admin-v2-editor');
                          if (el) {
                            const savedRange = saveSelection();
                            el.focus();
                            setTimeout(() => {
                              if (restoreSelection(savedRange)) {
                                document.execCommand('backColor', false, v);
                                setContentHtml(el.innerHTML);
                                saveSelection();
                              }
                            }, 50);
                          }
                        }
                      }} />
                    </label>
                    <button className="tool-btn" type="button" title="Yatay Çizgi" onMouseDown={(e) => { e.preventDefault(); applyCmd('insertHorizontalRule'); }}>—</button>
                    <button className="tool-btn" type="button" title="Bul/Değiştir" onMouseDown={(e) => { e.preventDefault(); findReplace(); }}>Ara</button>
                    <button className="tool-btn" type="button" title="Tablo" onMouseDown={(e) => { e.preventDefault(); insertTable(); }}>▦</button>
                    <span className="toolbar-divider" />
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 6px' }}>
                      <select aria-label="Satır Aralığı" value={lineSpacing} onChange={(e) => setLineSpacing(e.target.value)} onBlur={applyParagraphSpacing}>
                        <option value="1.0">1.0</option>
                        <option value="1.15">1.15</option>
                        <option value="1.5">1.5</option>
                        <option value="1.7">1.7</option>
                        <option value="2.0">2.0</option>
                      </select>
                      <input type="number" placeholder="Önce(px)" value={spaceBefore} onChange={(e) => setSpaceBefore(e.target.value)} onBlur={applyParagraphSpacing} style={{ width: 70 }} />
                      <input type="number" placeholder="Sonra(px)" value={spaceAfter} onChange={(e) => setSpaceAfter(e.target.value)} onBlur={applyParagraphSpacing} style={{ width: 70 }} />
                    </div>
                    <span className="toolbar-divider" />
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 6px' }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Görsel:</span>
                      <button className="tool-btn" type="button" title="Sola" onClick={() => alignImage('left')}>◧</button>
                      <button className="tool-btn" type="button" title="Ortala" onClick={() => alignImage('center')}>↔</button>
                      <button className="tool-btn" type="button" title="Sağa" onClick={() => alignImage('right')}>◨</button>
                      <select
                        aria-label="Görsel Genişlik (%)"
                        value={selectedImgInfo.widthPct || 100}
                        onChange={(e) => {
                          const val = Math.max(10, Math.min(100, parseInt(e.target.value, 10) || 100));
                          setImageWidth(val);
                          setSelectedImgInfo(prev => ({ ...prev, widthPct: val }));
                        }}
                        onClick={(e) => {
                          findSelectedImage();
                        }}
                      >
                        {Array.from({ length: 10 }).map((_, i) => {
                          const v = (i + 1) * 10; return <option key={v} value={v}>{v}%</option>;
                        })}
                      </select>
                    </div>
                    <span className="toolbar-divider" />
                    <span className="upload-group">
                      <label title="Resim Yükle" style={{ cursor: 'pointer' }}>
                        <input
                          aria-label="Resim Yükle"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              onUpload('image', file);
                              e.target.value = '';
                            }
                          }}
                        />
                        <span className="upload-chip image" title="Resim Yükle">Resim Yükle</span>
                      </label>
                      <label title="Dosya Yükle" style={{ cursor: 'pointer' }}>
                        <input
                          aria-label="Dosya Yükle"
                          type="file"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              onUpload('file', file);
                              e.target.value = '';
                            }
                          }}
                        />
                        <span className="upload-chip file" title="Dosya Yükle">Dosya Yükle</span>
                      </label>
                    </span>
                  </div>
                  {!blockMode && (
                    <div
                      id="admin-v2-editor"
                      contentEditable
                      suppressContentEditableWarning
                      style={{
                        minHeight: '400px',
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        outline: 'none',
                        background: '#fff',
                        lineHeight: '1.7',
                        fontSize: '16px',
                        color: '#1f2937',
                        fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
                      }}
                      onInput={(e) => {
                        setContentHtml(e.currentTarget.innerHTML);
                      }}
                      onBlur={() => {
                        const el = document.getElementById('admin-v2-editor');
                        if (el) setContentHtml(el.innerHTML);
                      }}
                      onClick={(e) => {
                        setTimeout(() => {
                          findSelectedImage();
                        }, 10);
                      }}
                    />
                  )}
                  {blockMode && (
                    <div style={{ padding: 12, borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <button className="icon-btn" onClick={() => setBlocks(prev => [...prev, { id: String(Date.now()), type: 'text', html: '<p>Yeni metin...</p>' }])}>+ Metin</button>
                        <label className="icon-btn" style={{ cursor: 'pointer' }}>
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                            const f = e.target.files && e.target.files[0]; if (!f) return;
                            const form = new FormData(); form.append('file', f);
                            const url = `${API_BASE_URL}/api/uploads/images?program_id=${programId}`;
                            const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${tokenMemo}` }, body: form });
                            const data = await res.json(); if (!res.ok) { alert(data.error || 'Yükleme hatası'); return; }
                            setBlocks(prev => [...prev, { id: String(Date.now()), type: 'image', src: data.url, widthPct: 100 }]);
                          }} />
                          + Görsel
                        </label>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {blocks.map((b, idx) => (
                          <div key={b.id} style={{ paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>{b.type === 'text' ? 'Metin Bloğu' : 'Görsel Bloğu'}</div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="icon-btn" onClick={() => { if (idx === 0) return; const copy = blocks.slice(); const t = copy[idx - 1]; copy[idx - 1] = copy[idx]; copy[idx] = t; setBlocks(copy); }}>↑</button>
                                <button className="icon-btn" onClick={() => { if (idx === blocks.length - 1) return; const copy = blocks.slice(); const t = copy[idx + 1]; copy[idx + 1] = copy[idx]; copy[idx] = t; setBlocks(copy); }}>↓</button>
                                <button className="icon-btn" onClick={() => setBlocks(prev => prev.filter(x => x.id !== b.id))}>Sil</button>
                              </div>
                            </div>
                            {b.type === 'text' ? (
                              <div>
                                <div
                                  ref={(el) => {
                                    if (el) {
                                      blockRefs.current[b.id] = el;
                                    }
                                  }}
                                  contentEditable
                                  suppressContentEditableWarning
                                  onFocus={() => setActiveBlockId(b.id)}
                                  onClick={(e) => {
                                    setActiveBlockId(b.id);
                                    setTimeout(() => {
                                      findSelectedImage();
                                    }, 10);
                                    setTimeout(() => {
                                      findSelectedImage();
                                    }, 100);
                                  }}
                                  onMouseUp={(e) => {
                                    setTimeout(() => {
                                      findSelectedImage();
                                    }, 10);
                                    setTimeout(() => {
                                      findSelectedImage();
                                    }, 100);
                                  }}
                                  onKeyUp={(e) => {
                                    setTimeout(() => {
                                      findSelectedImage();
                                    }, 10);
                                  }}
                                  onInput={(e) => {
                                    const target = e.currentTarget;
                                    if (target) {
                                      setBlocks(prev => prev.map(x => x.id === b.id ? { ...x, html: target.innerHTML } : x));
                                    }
                                  }}
                                  onBlur={() => {
                                    const el = blockRefs.current[b.id];
                                    if (el && el.innerHTML !== undefined) {
                                      setBlocks(prev => prev.map(x => x.id === b.id ? { ...x, html: el.innerHTML } : x));
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    minHeight: 160,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: 10,
                                    lineHeight: 1.7,
                                    textAlign: 'left',
                                    outline: 'none',
                                    background: '#fff',
                                    fontFamily: "-apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Segoe UI Symbol'",
                                    fontSize: 15,
                                    color: '#1f2937',
                                    boxSizing: 'border-box',
                                    overflowWrap: 'break-word',
                                    wordWrap: 'break-word'
                                  }}
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                                  <label style={{ fontSize: 12, color: '#6b7280' }}>Genişlik
                                    <select value={b.widthPct || 100} onChange={(e) => {
                                      const val = Math.max(10, Math.min(100, parseInt(e.target.value, 10) || 100));
                                      setBlocks(prev => prev.map(x => x.id === b.id ? { ...x, widthPct: val } : x));
                                    }} style={{ marginLeft: 6 }}>
                                      {Array.from({ length: 10 }).map((_, i) => { const v = (i + 1) * 10; return <option key={v} value={v}>{v}%</option>; })}
                                    </select>
                                  </label>
                                </div>
                                <img src={toAbsoluteUploadUrl(b.src)} alt="" style={{ maxWidth: '100%', height: 'auto', width: `${b.widthPct || 100}%` }} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {isPreview && !isSplitPreview && (
                    <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <div dangerouslySetInnerHTML={sanitizedHtmlProps(contentHtml)} />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="faculties-section">Soldan bir başlık seçin.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ProgramAdminModal bileşeni
function ProgramAdminModal({ onClose, newProgramAdmin, setNewProgramAdmin, handleCreateProgramAdmin, allPrograms }) {
  const [activeTab, setActiveTab] = React.useState('create'); // 'create' | 'delete'
  const [existingAdmins, setExistingAdmins] = React.useState([]);
  const [loadingAdmins, setLoadingAdmins] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState(null);
  const [programSearch, setProgramSearch] = React.useState('');
  const [academics, setAcademics] = React.useState([]);
  const [loadingAcademics, setLoadingAcademics] = React.useState(false);

  // Türkçe karakterleri normalize eden fonksiyon
  const turkishToLower = (str) => {
    return str
      .replace(/İ/g, 'i')
      .replace(/I/g, 'ı')
      .replace(/Ğ/g, 'ğ')
      .replace(/Ü/g, 'ü')
      .replace(/Ş/g, 'ş')
      .replace(/Ö/g, 'ö')
      .replace(/Ç/g, 'ç')
      .toLowerCase();
  };

  // Programları filtrele (Türkçe karakter duyarlı, büyük/küçük harf duyarsız)
  const filteredPrograms = React.useMemo(() => {
    if (!programSearch.trim()) return allPrograms;
    const searchNormalized = turkishToLower(programSearch);
    return allPrograms.filter(p => turkishToLower(p.name).includes(searchNormalized));
  }, [allPrograms, programSearch]);

  // Akademisyenleri yükle
  const fetchAcademics = React.useCallback(async () => {
    setLoadingAcademics(true);
    try {
      const token = readAuthItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/academics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAcademics(data);
      }
    } catch (err) {
      console.error('Akademisyenler yüklenemedi:', err);
    }
    setLoadingAcademics(false);
  }, []);

  // Mevcut program yöneticilerini yükle (role assignments'dan)
  const fetchExistingAdmins = React.useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const token = readAuthItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/role-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Sadece Program Başkanı (role_type_id: 5) rollerini göster
        const programChairs = data.filter(a => a.role_type_id === 5);
        setExistingAdmins(programChairs);
      }
    } catch (err) {
      console.error('Program yöneticileri yüklenemedi:', err);
    }
    setLoadingAdmins(false);
  }, []);

  React.useEffect(() => {
    if (activeTab === 'create') {
      fetchAcademics();
    } else if (activeTab === 'delete') {
      fetchExistingAdmins();
    }
  }, [activeTab, fetchAcademics, fetchExistingAdmins]);

  const handleDelete = async (assignmentId, email) => {
    if (!window.confirm(`"${email}" kullanıcısını silmek istediğinize emin misiniz?`)) return;

    setDeletingId(assignmentId);
    try {
      const token = readAuthItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/role-assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setExistingAdmins(prev => prev.filter(a => a.id !== assignmentId));
        alert('Program yöneticisi silindi');
      } else {
        const data = await res.json();
        alert(data.error || 'Silme işlemi başarısız');
      }
    } catch (err) {
      console.error('Silme hatası:', err);
      alert('Silme sırasında hata oluştu');
    }
    setDeletingId(null);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        maxWidth: '550px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>Program Yöneticisi Yönetimi</h2>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: '2px solid #e5e7eb',
              background: '#f9fafb',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.target.style.background = '#fee2e2'; e.target.style.borderColor = '#dc3545'; e.target.style.color = '#dc3545'; }}
            onMouseOut={(e) => { e.target.style.background = '#f9fafb'; e.target.style.borderColor = '#e5e7eb'; e.target.style.color = '#6b7280'; }}
          >×</button>
        </div>

        {/* Tab Butonları */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            onClick={() => setActiveTab('create')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              background: activeTab === 'create' ? '#28a745' : '#e5e7eb',
              color: activeTab === 'create' ? 'white' : '#374151'
            }}
          >
            Yönetici Oluştur
          </button>
          <button
            onClick={() => setActiveTab('delete')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              background: activeTab === 'delete' ? '#dc3545' : '#e5e7eb',
              color: activeTab === 'delete' ? 'white' : '#374151'
            }}
          >
            Yönetici Sil
          </button>
        </div>

        {activeTab === 'create' ? (
          <form onSubmit={handleCreateProgramAdmin} autoComplete="off">
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Akademisyen:</label>
              {loadingAcademics ? (
                <p>Yükleniyor...</p>
              ) : (
                <select
                  value={newProgramAdmin.akademisyen_id || ''}
                  onChange={(e) => setNewProgramAdmin({ ...newProgramAdmin, akademisyen_id: e.target.value ? parseInt(e.target.value) : '' })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' }}
                >
                  <option value="">Akademisyen Seçiniz</option>
                  {academics.map(academic => (
                    <option key={academic.id} value={academic.id}>
                      {academic.full_name} ({academic.email})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Program:</label>
              <input
                type="text"
                placeholder="Program ara..."
                value={programSearch}
                onChange={(e) => setProgramSearch(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box', marginBottom: '8px' }}
              />
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '6px', padding: '10px' }}>
                {filteredPrograms.length === 0 ? (
                  <p style={{ color: '#6b7280', margin: 0 }}>{programSearch ? 'Eşleşen program bulunamadı' : 'Program bulunamadı'}</p>
                ) : (
                  filteredPrograms.map((program) => (
                    <label key={program.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="program_selection"
                        checked={newProgramAdmin.program_id === program.id}
                        onChange={() => setNewProgramAdmin({ ...newProgramAdmin, program_id: program.id })}
                        style={{ marginRight: '8px' }}
                      />
                      {program.name}
                    </label>
                  ))
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f5f5f5', cursor: 'pointer' }}>İptal</button>
              <button type="submit" style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#28a745', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Oluştur</button>
            </div>
          </form>
        ) : (
          <div>
            {loadingAdmins ? (
              <p style={{ textAlign: 'center', color: '#6b7280' }}>Yükleniyor...</p>
            ) : existingAdmins.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#6b7280' }}>Henüz program yöneticisi yok</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {existingAdmins.map((admin) => (
                  <div key={admin.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    background: '#f9fafb'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>{admin.academic_name || admin.academic_email || 'Bilinmeyen'}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {admin.program_name || 'Program atanmamış'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(admin.id, admin.academic_email || admin.academic_name)}
                      disabled={deletingId === admin.id}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        cursor: deletingId === admin.id ? 'not-allowed' : 'pointer',
                        opacity: deletingId === admin.id ? 0.6 : 1,
                        fontWeight: 600
                      }}
                    >
                      {deletingId === admin.id ? '...' : 'Sil'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f5f5f5', cursor: 'pointer' }}>Kapat</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ContactModal bileşeni
function ContactModal({ onClose }) {
  // Kocaeli Sağlık ve Teknoloji Üniversitesi'nin yaklaşık koordinatları
  const universityLat = 40.7099;
  const universityLon = 29.9909;

  // OpenStreetMap embed URL'si, marker ile birlikte
  // bbox'ı marker'ın etrafında biraz daha geniş tutarak daha iyi bir başlangıç görünümü sağlayabiliriz.
  // Örneğin, marker'ın 0.01 derece etrafında bir kutu:
  const bboxMinLon = (universityLon - 0.01).toFixed(4);
  const bboxMinLat = (universityLat - 0.01).toFixed(4);
  const bboxMaxLon = (universityLon + 0.01).toFixed(4);
  const bboxMaxLat = (universityLat + 0.01).toFixed(4);

  const mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bboxMinLon}%2C${bboxMinLat}%2C${bboxMaxLon}%2C${bboxMaxLat}&layer=mapnik&marker=${universityLat}%2C${universityLon}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>İletişim</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="contact-info">
            <div className="contact-section">
              <h3>Contact</h3>
              <p><strong>International Office</strong></p>
              <p><strong>Kocaeli Sağlık ve Teknoloji Üniversitesi</strong></p>
              <p>Yeniköy Mahallesi Ilıca Caddesi No:29,</p>
              <p>Başiskele/Kocaeli</p>
              <p>Turkey</p>
            </div>

            <div className="contact-section">
              <h3>Konum</h3>
              <div className="map-container">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="300"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  title="Kocaeli Sağlık ve Teknoloji Üniversitesi Konumu"
                ></iframe>
                <div className="map-link">
                  <a href={`https://www.openstreetmap.org/?mlat=${universityLat}&mlon=${universityLon}#map=16/${universityLat}/${universityLon}`} target="_blank" rel="noopener noreferrer">
                    Büyük haritada görüntüle
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-section">
              <h3>Email</h3>
              <p><strong>Email:</strong> info@kocaelisaglik.edu.tr - kostu@hs03.kep.tr</p>
              <p><strong>Email 2:</strong> kostu@hs03.kep.tr</p>
            </div>

            <div className="contact-section">
              <h3>Website</h3>
              <p><a href="https://kocaelisaglik.edu.tr/iletisim/" target="_blank" rel="noopener noreferrer">https://kocaelisaglik.edu.tr/iletisim/</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Syllabus Status Page Component
function SyllabusStatusPage({ userType, programAdminToken, adminToken, handleLogout }) {
  // userType'ı localStorage'dan kontrol et (university admin için)
  const currentUserType = userType || (readAuthItem('universityAdminToken') ? 'universityAdmin' : (readAuthItem('adminToken') ? 'admin' : (readAuthItem('programAdminToken') ? 'programAdmin' : 'public')));
  const canManageProgram = isProgramManagementUser(currentUserType);

  // Token'ı aktif userType'a göre seç (rol çakışmalarını önle)
  const token = currentUserType === 'programAdmin'
    ? (programAdminToken || readAuthItem('programAdminToken') || '')
    : (adminToken || readAuthItem('universityAdminToken') || readAuthItem('adminToken') || '');
  const { programSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [programId, setProgramId] = React.useState(null);
  const [programName, setProgramName] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [semesterFilter, setSemesterFilter] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('semester');

  // Get program ID from slug
  React.useEffect(() => {
    const fetchProgramId = async () => {
      try {
        // /api/programs endpoint'i admin/programAdmin/universityAdmin yetkisi isteyebilir.
        // Bu sayfa admin, programAdmin veya universityAdmin tarafından kullanıldığı için uygun token'ı header'a ekliyoruz.
        const headers = {};
        if (currentUserType === 'admin' && adminToken) {
          headers.Authorization = `Bearer ${adminToken}`;
        } else if (currentUserType === 'programAdmin' && programAdminToken) {
          headers.Authorization = `Bearer ${programAdminToken}`;
        } else if (currentUserType === 'universityAdmin') {
          const uniToken = adminToken || readAuthItem('universityAdminToken') || readAuthItem('adminToken') || '';
          if (uniToken) {
            headers.Authorization = `Bearer ${uniToken}`;
          }
        } else if (token) {
          // Fallback: token varsa kullan
          headers.Authorization = `Bearer ${token}`;
        } else {
          // Son çare: storage'dan token al
          const fallbackToken = readAuthItem('adminToken') || readAuthItem('universityAdminToken') || readAuthItem('programAdminToken') || '';
          if (fallbackToken) {
            headers.Authorization = `Bearer ${fallbackToken}`;
          }
        }
        const response = await fetch(`${API_BASE_URL}/api/programs`, { headers });
        const programs = await response.json();
        const program = programs.find(p => {
          const slug = p.name.toLowerCase()
            .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
            .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
            .replace(/[^a-z0-9\s-]/g, '').trim()
            .replace(/\s+/g, '-').replace(/-+/g, '-');
          return slug === programSlug;
        });
        if (program) {
          setProgramId(program.id);
          setProgramName(program.name);
        }
      } catch (error) {
        console.error('Error fetching program:', error);
      }
    };
    fetchProgramId();
  }, [programSlug, currentUserType, adminToken, programAdminToken, token]);

  // Fetch syllabus status data
  React.useEffect(() => {
    if (!programId) return;

    let isMounted = true;
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = {};
        // Token'ı öncelik sırasına göre kontrol et
        if (currentUserType === 'admin' && adminToken) {
          headers['Authorization'] = `Bearer ${adminToken}`;
        } else if (currentUserType === 'programAdmin' && programAdminToken) {
          headers['Authorization'] = `Bearer ${programAdminToken}`;
        } else if (currentUserType === 'universityAdmin') {
          const uniToken = adminToken || readAuthItem('universityAdminToken') || readAuthItem('adminToken') || '';
          if (uniToken) {
            headers['Authorization'] = `Bearer ${uniToken}`;
          }
        } else if (token) {
          // Fallback: token varsa kullan
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          // Son çare: storage'dan token al
          const fallbackToken = readAuthItem('adminToken') || readAuthItem('universityAdminToken') || readAuthItem('programAdminToken') || '';
          if (fallbackToken) {
            headers['Authorization'] = `Bearer ${fallbackToken}`;
          }
        }

        const response = await fetch(
          `${API_BASE_URL}/api/programs/${programId}/syllabus-status`,
          { headers, signal: abortController.signal }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch syllabus status');
        }

        const result = await response.json();
        if (isMounted) {
          setData(result);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Error fetching syllabus status:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [programId, currentUserType, adminToken, programAdminToken, token]);

  const pcInfo = React.useMemo(() => {
    const defined = Boolean(data && data.program_pc_defined);
    const counts = data && data.program_pc_counts ? data.program_pc_counts : null;
    return { defined, counts };
  }, [data]);

  // Filter and sort courses
  const filteredCourses = React.useMemo(() => {
    if (!data || !data.courses) return [];

    let filtered = data.courses.filter(course => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        course.course_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.course_name.toLowerCase().includes(searchTerm.toLowerCase());

      // Semester filter
      const matchesSemester = semesterFilter === 'all' ||
        String(course.semester) === semesterFilter;

      // Type filter
      const matchesType = typeFilter === 'all' ||
        (typeFilter === 'zorunlu' && !course.course_type.toLowerCase().includes('seçmeli')) ||
        (typeFilter === 'secmeli' && course.course_type.toLowerCase().includes('seçmeli'));

      return matchesSearch && matchesSemester && matchesType;
    });

    // Sort
    if (sortBy === 'semester') {
      filtered.sort((a, b) => {
        if (a.semester !== b.semester) return a.semester - b.semester;
        return a.course_code.localeCompare(b.course_code);
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.course_name.localeCompare(b.course_name));
    } else if (sortBy === 'completion') {
      filtered.sort((a, b) => b.completion_percentage - a.completion_percentage);
    }

    return filtered;
  }, [data, searchTerm, semesterFilter, typeFilter, sortBy]);

  if (loading) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
          {canManageProgram && (
            <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="syllabus" navigate={navigate} />
          )}
          <div style={{ flex: 1, textAlign: 'center', padding: '50px' }}>Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="App">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="logo">
                  <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                  <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
          {canManageProgram && (
            <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="syllabus" navigate={navigate} />
          )}
          <div style={{ flex: 1, textAlign: 'center', padding: '50px' }}>Veri yüklenemedi</div>
        </div>
      </div>
    );
  }

  const getCompletionColor = (percentage) => {
    if (percentage === 0) return '#fee2e2';
    if (percentage === 100) return '#d1fae5';
    return '#fed7aa';
  };

  const getCompletionTextColor = (percentage) => {
    if (percentage === 0) return '#dc2626';
    if (percentage === 100) return '#059669';
    return '#ea580c';
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <ProgramManagementHeaderActions
            userType={currentUserType}
            programName={programName}
            navigate={navigate}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      <div className="main-content" style={canManageProgram ? { display: 'flex', gap: 16, alignItems: 'flex-start' } : undefined}>
        {canManageProgram && (
          <ProgramAdminSidebar programId={programId} programName={programName} programSlug={programSlug} activeKey="syllabus" navigate={navigate} />
        )}
        <div style={canManageProgram ? { flex: 1, minWidth: 0 } : undefined}>
        <div className="syllabus-status-container">
          <div className="syllabus-header">
            {!canManageProgram && (
              <button onClick={() => navigate(-1)} className="back-button">← Geri</button>
            )}
            <h1>{programName} - İzlence Doluluk Durumu</h1>
          </div>

          {/* Statistics Table */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: 1,
              minWidth: '100px',
              background: 'white',
              border: '2px solid #2c3e50',
              borderRadius: '8px',
              padding: '12px 8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Toplam Ders</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2c3e50' }}>{data.statistics.total_courses}</div>
            </div>
            <div style={{
              flex: 1,
              minWidth: '100px',
              background: 'white',
              border: '2px solid #2c3e50',
              borderRadius: '8px',
              padding: '12px 8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Ortalama Doluluk</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: getCompletionTextColor(data.statistics.average_completion) }}>%{data.statistics.average_completion}</div>
            </div>
            <div style={{
              flex: 1,
              minWidth: '100px',
              background: 'white',
              border: '2px solid #2c3e50',
              borderRadius: '8px',
              padding: '12px 8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Tamamlanan</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#27ae60' }}>{data.statistics.completed_courses}</div>
            </div>
            <div style={{
              flex: 1,
              minWidth: '100px',
              background: 'white',
              border: '2px solid #2c3e50',
              borderRadius: '8px',
              padding: '12px 8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Veri Yok</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#e74c3c' }}>{data.statistics.empty_courses}</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-bar-container">
            <div className="progress-label">
              <span>Program Geneli İzlence Doluluk Oranı</span>
              <span style={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: getCompletionTextColor(data.statistics.average_completion)
              }}>
                %{data.statistics.average_completion}
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${data.statistics.average_completion}%`,
                  background: `linear-gradient(90deg, ${getCompletionTextColor(data.statistics.average_completion)}, ${getCompletionTextColor(data.statistics.average_completion)}dd)`
                }}
              >
              </div>
            </div>
          </div>

          {/* Detailed Statistics */}
          <div className="detailed-stats">
            <h3>Detaylı İstatistikler</h3>
            {data && pcInfo && pcInfo.defined === false && (
              <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Program Çıktıları (PÇ) tanımı bulunamadı</div>
                <div style={{ fontSize: 13 }}>
                  Bu programda PÇ verisi olmadığı için “Program Çıktıları” sütunu tüm derslerde “—” görünür.
                  {pcInfo.counts ? ` (Tanım: ${pcInfo.counts.definitions}, Eşleştirme: ${pcInfo.counts.mappings})` : ''}
                </div>
              </div>
            )}
            <div className="stats-grid">
              <div className="stats-section">
                <h4>Dönem Bazlı</h4>
                {Object.keys(data.by_semester).sort((a, b) => a - b).map(sem => (
                  <div key={sem} className="stats-row">
                    <span>{sem}. Dönem:</span>
                    <span>{data.by_semester[sem].total} ders - %{data.by_semester[sem].average} doluluk</span>
                  </div>
                ))}
              </div>
              <div className="stats-section">
                <h4>Ders Tipi Bazlı</h4>
                <div className="stats-row">
                  <span>Zorunlu:</span>
                  <span>{data.by_type.zorunlu.total} ders - %{data.by_type.zorunlu.average} doluluk</span>
                </div>
                <div className="stats-row">
                  <span>Seçmeli:</span>
                  <span>{data.by_type.secmeli.total} ders - %{data.by_type.secmeli.average} doluluk</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-container">
            <input
              type="text"
              placeholder="Ders kodu veya adı ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={semesterFilter}
              onChange={(e) => setSemesterFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tüm Dönemler</option>
              {[...new Set(data.courses.map(c => c.semester))].sort((a, b) => a - b).map(sem => (
                <option key={sem} value={String(sem)}>{sem}. Dönem</option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tüm Dersler</option>
              <option value="zorunlu">Zorunlu</option>
              <option value="secmeli">Seçmeli</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="semester">Dönem → Kod</option>
              <option value="name">Ders Adı (A-Z)</option>
              <option value="completion">Doluluk Oranı</option>
            </select>
          </div>

          {/* Table */}
          <div className="syllabus-table-container">
            <table className="syllabus-table">
              <thead>
                <tr>
                  <th>Dönem</th>
                  <th>Ders Kodu</th>
                  <th>Ders Adı</th>
                  <th>İzlence %</th>
                  <th>Ders Bilgileri</th>
                  <th>Öğrenme Çıktıları</th>
                  <th>Haftalık Konular</th>
                  <th>Değerlendirme Sistemi</th>
                  <th>Öğretim Elemanları</th>
                  <th>AKTS Aktiviteleri</th>
                  <th>Sürdürülebilir Kalkınma</th>
                  <th>Program Çıktıları</th>
                </tr>
              </thead>
              <tbody>
                {filteredCourses.map((course, index) => (
                  <tr key={index}>
                    <td>{course.semester}</td>
                    <td><strong>{course.course_code}</strong></td>
                    <td>{course.course_name}</td>
                    <td style={{
                      color: getCompletionTextColor(course.completion_percentage),
                      fontWeight: 700,
                      fontSize: '0.85rem'
                    }}>
                      %{course.completion_percentage}
                    </td>
                    <td className="field-check" style={{ color: course.fields?.course_info ? '#27ae60' : '#bdc3c7' }}>{course.fields?.course_info ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.learning_outcomes ? '#27ae60' : '#bdc3c7' }}>{course.fields?.learning_outcomes ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.weekly_topics ? '#27ae60' : '#bdc3c7' }}>{course.fields?.weekly_topics ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.evaluation_system ? '#27ae60' : '#bdc3c7' }}>{course.fields?.evaluation_system ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.teaching_staff ? '#27ae60' : '#bdc3c7' }}>{course.fields?.teaching_staff ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.akts_activities ? '#27ae60' : '#bdc3c7' }}>{course.fields?.akts_activities ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.sustainable_development_goals ? '#27ae60' : '#bdc3c7' }}>{course.fields?.sustainable_development_goals ? 'Var' : '—'}</td>
                    <td className="field-check" style={{ color: course.fields?.program_outcomes ? '#27ae60' : '#bdc3c7' }}>{course.fields?.program_outcomes ? 'Var' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

// UniversityAdminsPage component
function UniversityAdminsPage({ adminToken, handleLogout }) {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ akademisyen_id: '', role_type_id: '', faculty_id: null });
  const [faculties, setFaculties] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);

  // Token'ı prop'tan veya storage'dan al
  const token = adminToken || readAuthItem('adminToken') || '';

  useEffect(() => {
    if (token) {
      fetchAssignments();
      fetchFaculties();
      fetchAcademics();
      fetchRoleTypes();
    } else {
      // Token yoksa admin paneline yönlendir
      navigate('/admin');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/admin/role-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        // Sadece Rektör, Rektör Yardımcısı, Dekan, Dekan Yardımcısı rollerini göster
        const filtered = data.filter(a => a.role_type_id <= 4);
        console.log('Fetched assignments:', filtered);
        setAssignments(filtered);
      } else {
        console.error('Error fetching assignments:', data);
      }
    } catch (error) {
      console.error('Error fetching role assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFaculties = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/faculties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFaculties(data);
      }
    } catch (error) {
      console.error('Error fetching faculties:', error);
    }
  };

  const fetchAcademics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/academics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setAcademics(data);
      } else {
        console.error('Error fetching academics:', data.error);
      }
    } catch (error) {
      console.error('Error fetching academics:', error);
    }
  };

  const fetchRoleTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/role-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        // Sadece Rektör, Rektör Yardımcısı, Dekan, Dekan Yardımcısı rollerini göster (id: 1-4)
        const filtered = data.filter(rt => rt.id <= 4);
        setRoleTypes(filtered);
      } else {
        console.error('Error fetching role types:', data.error);
      }
    } catch (error) {
      console.error('Error fetching role types:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/role-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          akademisyen_id: parseInt(newAssignment.akademisyen_id),
          role_type_id: parseInt(newAssignment.role_type_id),
          faculty_id: newAssignment.faculty_id ? parseInt(newAssignment.faculty_id) : null
        })
      });
      const data = await res.json();
      if (res.ok) {
        alert('Rol ataması başarıyla oluşturuldu');
        setShowAddForm(false);
        setNewAssignment({ akademisyen_id: '', role_type_id: '', faculty_id: null });
        fetchAssignments();
      } else {
        alert(data.error || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Error creating role assignment:', error);
      alert('Hata oluştu');
    }
  };

  // roleLabels'i roleTypes state'inden oluştur
  const roleLabels = {};
  roleTypes.forEach(rt => {
    roleLabels[rt.id] = rt.name;
  });

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin')} className="logout-button">Admin Paneli</button>
            <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
          </div>
        </div>
      </div>
      <div className="main-content dean-dashboard-main">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                padding: '10px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#4b5563';
                e.target.style.transform = 'translateX(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#6b7280';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              ← Geri
            </button>
            <h1 style={{ margin: 0 }}>Üniversite Yöneticileri</h1>
          </div>
          <button onClick={() => setShowAddForm(!showAddForm)} className="logout-button" style={{ backgroundColor: '#6b7280', borderColor: '#6b7280' }}>
            {showAddForm ? 'Formu Kapat' : '+ Yeni Yönetici Ekle'}
          </button>
        </div>

        {showAddForm && (
          <form onSubmit={handleCreate} style={{ marginBottom: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
            <h3>Yeni Rol Ataması</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Akademisyen:</label>
              <select
                value={newAssignment.akademisyen_id}
                onChange={(e) => setNewAssignment({ ...newAssignment, akademisyen_id: e.target.value })}
                required
                style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Akademisyen Seçiniz</option>
                {academics.map(academic => (
                  <option key={academic.id} value={academic.id}>
                    {academic.full_name} ({academic.email})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Rol:</label>
              <select
                value={newAssignment.role_type_id}
                onChange={(e) => {
                  const roleTypeId = parseInt(e.target.value);
                  setNewAssignment({
                    ...newAssignment,
                    role_type_id: roleTypeId,
                    faculty_id: (roleTypeId === 1 || roleTypeId === 2) ? null : newAssignment.faculty_id
                  });
                }}
                required
                style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Rol Seçiniz</option>
                {roleTypes.map(roleType => (
                  <option key={roleType.id} value={roleType.id}>
                    {roleType.name}
                    {roleType.id === 1 || roleType.id === 2
                      ? ' (Tüm fakülteleri görür)'
                      : roleType.id === 3 || roleType.id === 4
                        ? ' (Sadece kendi fakültesini görür)'
                        : ''}
                  </option>
                ))}
              </select>
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px' }}>
                {newAssignment.role_type_id === 1 || newAssignment.role_type_id === 2
                  ? 'Rektör ve Rektör Yardımcısı tüm fakültelerin izlence durumlarını görüntüleyebilir.'
                  : newAssignment.role_type_id === 3 || newAssignment.role_type_id === 4
                    ? 'Dekan ve Dekan Yardımcısı sadece kendi fakültesinin izlence durumlarını görüntüleyebilir.'
                    : 'Lütfen bir rol seçiniz.'}
              </p>
            </div>
            {(newAssignment.role_type_id === '3' || newAssignment.role_type_id === '4' || parseInt(newAssignment.role_type_id) === 3 || parseInt(newAssignment.role_type_id) === 4) && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Fakülte:</label>
                <select
                  value={newAssignment.faculty_id || ''}
                  onChange={(e) => setNewAssignment({ ...newAssignment, faculty_id: e.target.value ? parseInt(e.target.value) : null })}
                  required
                  style={{ width: '100%', padding: '10px', marginTop: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Fakülte Seçiniz</option>
                  {faculties.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}
            <button type="submit" className="logout-button" style={{ marginTop: '10px' }}>Oluştur</button>
          </form>
        )}

        {loading ? (
          <p>Yükleniyor...</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Akademisyen</th>
                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Email</th>
                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Rol</th>
                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Fakülte</th>
                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map(assignment => (
                <tr key={assignment.id}>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>{assignment.academic_name || '-'}</td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>{assignment.academic_email || '-'}</td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>{roleLabels[assignment.role_type_id] || '-'}</td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>{assignment.faculty_name || '-'}</td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>{assignment.is_active ? 'Aktif' : 'Pasif'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>
    </div>
  );
}

// UniversityAdminDashboard component
function UniversityAdminDashboard({ adminToken, handleLogout }) {
  const navigate = useNavigate();
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [programSearchQuery, setProgramSearchQuery] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState('');
  const [selectedProgramType, setSelectedProgramType] = useState('');
  const [selectedEducationLevel, setSelectedEducationLevel] = useState('');
  const [programSortDirection, setProgramSortDirection] = useState('asc');
  const [rectorView, setRectorView] = useState('home');

  // Token'ı prop'tan veya storage'dan al (programAdminToken da kontrol et)
  const token = adminToken || readAuthItem('adminToken') || readAuthItem('universityAdminToken') || readAuthItem('programAdminToken') || '';

  useEffect(() => {
    if (token) {
      fetchAdminInfo();
      fetchFaculties();
    } else {
      console.error('[UniversityAdminDashboard] No token available');
      setLoading(false);
    }
    // fetch fonksiyonları yalnızca token değiştiğinde yeniden çalışmalıdır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAdminInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/university-admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setAdminInfo(data.admin);
      } else {
        console.error('[UniversityAdminDashboard] Failed to fetch admin info:', data);
      }
    } catch (error) {
      console.error('[UniversityAdminDashboard] Error fetching admin info:', error);
    }
  };

  const fetchFaculties = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/university-admin/faculties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const facultyList = Array.isArray(data) ? data : [];
        const facultiesWithPrograms = await Promise.all(
          facultyList.map(async (faculty) => {
            if (Array.isArray(faculty.programs) && faculty.programs.length > 0) {
              return faculty;
            }

            try {
              // Eski backend sürümleri fakülte yanıtına programları eklemiyordu.
              // Fakülte kapsamı ilk istekle doğrulandıktan sonra tüm bölümleri
              // doğrudan faculty_id üzerinden tamamla.
              const programRes = await fetch(
                `${API_BASE_URL}/api/programs?faculty_id=${encodeURIComponent(faculty.id)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const programData = await programRes.json();
              if (programRes.ok && Array.isArray(programData)) {
                return {
                  ...faculty,
                  programs: programData.map((program) => ({
                    id: program.id,
                    name: program.name,
                  })),
                };
              }
            } catch (programError) {
              console.error('[UniversityAdminDashboard] Failed to fetch faculty programs:', programError);
            }

            return { ...faculty, programs: [] };
          }),
        );
        setFaculties(facultiesWithPrograms);
      } else {
        console.error('[UniversityAdminDashboard] Failed to fetch faculties:', data);
      }
    } catch (error) {
      console.error('[UniversityAdminDashboard] Error fetching faculties:', error);
    } finally {
      setLoading(false);
    }
  };

  const roleLabels = {
    'rector': 'Rektör',
    'rector_vice': 'Rektör Yardımcısı',
    'dean': 'Dekan',
    'dean_vice': 'Dekan Yardımcısı'
  };
  const managementRoleLabel = (
    (adminInfo && roleLabels[adminInfo.role])
    || (adminInfo && adminInfo.role_name)
    || 'Fakülte Yöneticisi'
  );

  const isRectorRole = adminInfo
    ? ['rector', 'rector_vice'].includes(adminInfo.role)
    : ['Rektör', 'Rektör Yardımcısı'].includes(managementRoleLabel);
  const accessiblePrograms = React.useMemo(
    () => faculties.flatMap((faculty) => (
      Array.isArray(faculty.programs)
        ? faculty.programs.map((program) => ({
            ...program,
            faculty_id: faculty.id,
            faculty_name: faculty.name,
            education_type_id: faculty.education_type_id,
          }))
        : []
    )),
    [faculties],
  );

  const facultyOptions = React.useMemo(
    () => [...new Set(accessiblePrograms.map((program) => program.faculty_name).filter(Boolean))]
      .sort((first, second) => first.localeCompare(second, 'tr', { sensitivity: 'base' })),
    [accessiblePrograms],
  );
  const programTypeOptions = React.useMemo(
    () => [...new Set(accessiblePrograms.map((program) => getProgramType(program.name)))]
      .sort((first, second) => first.localeCompare(second, 'tr', { sensitivity: 'base' })),
    [accessiblePrograms],
  );
  const educationLevelOptions = React.useMemo(
    () => [...new Set(accessiblePrograms.map((program) => getProgramEducationLevel(program.name, program.education_type_id)))]
      .sort((first, second) => first.localeCompare(second, 'tr', { sensitivity: 'base' })),
    [accessiblePrograms],
  );
  const filteredPrograms = React.useMemo(
    () => filterAndSortPrograms(accessiblePrograms, {
      searchQuery: programSearchQuery,
      faculty: selectedFaculty,
      programType: selectedProgramType,
      educationLevel: selectedEducationLevel,
      sortDirection: programSortDirection,
    }),
    [
      accessiblePrograms,
      programSearchQuery,
      selectedFaculty,
      selectedProgramType,
      selectedEducationLevel,
      programSortDirection,
    ],
  );
  const hasActiveProgramFilters = Boolean(
    programSearchQuery || selectedFaculty || selectedProgramType || selectedEducationLevel,
  );

  const clearProgramFilters = () => {
    setProgramSearchQuery('');
    setSelectedFaculty('');
    setSelectedProgramType('');
    setSelectedEducationLevel('');
  };

  const openRectorPrograms = (educationLevel = '', preserveSearch = false) => {
    if (!preserveSearch) setProgramSearchQuery('');
    setSelectedFaculty('');
    setSelectedProgramType('');
    setSelectedEducationLevel(educationLevel);
    setRectorView('programs');
    window.requestAnimationFrame(() => {
      document.getElementById('dean-program-management')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const openFacultySyllabusOverview = () => {
    setRectorView('programs');
    window.setTimeout(() => {
      document.getElementById('dean-syllabus-overview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const educationLevelCounts = React.useMemo(() => (
    accessiblePrograms.reduce((counts, program) => {
      const level = getProgramEducationLevel(program.name, program.education_type_id);
      counts[level] = (counts[level] || 0) + 1;
      return counts;
    }, {})
  ), [accessiblePrograms]);

  const highlightProgramName = (programName) => {
    const query = programSearchQuery.trim();
    if (!query) return programName;

    const TurkishCharacterPatterns = {
      C: '[cCçÇ]',
      G: '[gGğĞ]',
      I: '[iIİı]',
      O: '[oOöÖ]',
      S: '[sSşŞ]',
      U: '[uUüÜ]',
    };
    const normalizedQuery = normalizeProgramSearchText(query);
    const queryPattern = normalizedQuery
      .split('')
      .map((character) => (
        character === ' '
          ? '\\s+'
          : (TurkishCharacterPatterns[character] || character.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      ))
      .join('');
    if (!queryPattern) return programName;

    const parts = String(programName || '').split(new RegExp(`(${queryPattern})`, 'g'));
    return parts.map((part, index) => (
      index % 2 === 1
        ? <mark key={`${part}-${index}`} className="dean-program-highlight">{part}</mark>
        : part
    ));
  };

  const navigateToProgram = (program, section = 'courses') => {
    const programSlug = slugifyProgramName(program.name);
    const state = {
      programId: program.id,
      programName: program.name,
      program: { id: program.id, name: program.name },
    };
    const routes = {
      courses: `/program/${programSlug}?programId=${program.id}`,
      electives: `/program/${programSlug}/secmeli-dersler?programId=${program.id}`,
      syllabus: `/program/${programSlug}/syllabus-status`,
      outcomes: `/program/${programSlug}/program-ciktilari-olustur`,
      plan: `/program/${programSlug}/ders-plani-olustur`,
      info: `/program/sidebar?programId=${program.id}`,
    };
    navigate(routes[section] || routes.courses, { state });
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isRectorRole && (
              <>
                <button
                  type="button"
                  className="management-header-button active"
                  onClick={() => document.getElementById('dean-program-management')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Program Yönetimi
                </button>
                <button
                  type="button"
                  className="management-header-button"
                  disabled={loading || faculties.length === 0}
                  title={faculties.length === 0 ? 'Fakülte bilgisi yükleniyor' : 'Fakülte izlence durumunu aç'}
                  onClick={() => {
                    const faculty = faculties[0];
                    if (faculty && faculty.id) {
                      navigate(`/university-admin/faculty/${faculty.id}/syllabus-status`);
                    }
                  }}
                >
                  İzlence Durumu
                </button>
              </>
            )}
            {adminInfo && (
              <span className="management-role-badge">{managementRoleLabel}</span>
            )}
            <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
          </div>
        </div>
      </div>
      <div className="main-content rector-dashboard-main">
        {isRectorRole && rectorView === 'home' ? (
          <section className="rector-home" aria-labelledby="rector-dashboard-title">
            <div className="dean-dashboard-title rector-home__title">
              <div>
                <span className="dean-dashboard-eyebrow">{managementRoleLabel} yetkisi</span>
                <h1 id="rector-dashboard-title">Rektör Paneli</h1>
                <p>Üniversitenin tüm program, müfredat ve izlence süreçlerine tek ekrandan erişin.</p>
              </div>
              <div className="dean-dashboard-count">
                <strong>{accessiblePrograms.length}</strong>
                <span>Bölüm / Program</span>
              </div>
            </div>

            <form
              className="rector-quick-search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                openRectorPrograms('', true);
              }}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
              </svg>
              <label className="sr-only" htmlFor="rector-program-search">Bölüm veya program ara</label>
              <input
                id="rector-program-search"
                type="search"
                value={programSearchQuery}
                onChange={(event) => setProgramSearchQuery(event.target.value)}
                placeholder="Bölüm veya program adı ara..."
                autoComplete="off"
              />
              <button type="submit">Programlarda Ara</button>
            </form>

            <div className="rector-primary-grid">
              <article className="rector-primary-card">
                <div className="rector-card-icon" aria-hidden="true">☰</div>
                <div>
                  <span className="rector-card-kicker">PROGRAMLAR</span>
                  <h2>Üniversite Geneli Program Yönetimi</h2>
                  <p>{faculties.length} akademik birimdeki {accessiblePrograms.length} programı arayın ve yönetin.</p>
                </div>
                <button type="button" onClick={() => openRectorPrograms()}>Tüm Programları Aç <span aria-hidden="true">→</span></button>
              </article>

              <article className="rector-primary-card rector-primary-card--syllabus">
                <div className="rector-card-icon" aria-hidden="true">▥</div>
                <div>
                  <span className="rector-card-kicker">RAPORLAMA</span>
                  <h2>İzlence Doluluk Durumu</h2>
                  <p>Ders izlencelerinin ne kadar tamamlandığını üniversite, fakülte ve program düzeyinde inceleyin.</p>
                </div>
                <div className="rector-syllabus-actions" aria-label="İzlence raporları">
                  <button type="button" className="primary" onClick={() => navigate('/admin/syllabus-status')}>Üniversite Geneli Raporu <span aria-hidden="true">→</span></button>
                  <button type="button" onClick={openFacultySyllabusOverview}>Akademik Birimlere Göre <span aria-hidden="true">→</span></button>
                  <button type="button" onClick={() => navigate('/admin/syllabus-status#programlar')}>Programlara Göre <span aria-hidden="true">→</span></button>
                  <button type="button" className="warning" onClick={() => navigate('/admin/syllabus-status?filter=incomplete#programlar')}>Eksik İzlenceler <span aria-hidden="true">→</span></button>
                </div>
              </article>
            </div>

            <section className="rector-tools" aria-labelledby="rector-tools-title">
              <div className="rector-section-heading">
                <div>
                  <span className="dean-dashboard-eyebrow">Hızlı işlemler</span>
                  <h2 id="rector-tools-title">Diğer Yönetim Araçları</h2>
                </div>
              </div>
              <div className="rector-tools-grid">
                <button type="button" onClick={() => openRectorPrograms()}><strong>Ders ve Müfredat</strong><span>Program derslerini ve planlarını yönetin</span><b aria-hidden="true">→</b></button>
                <button type="button" onClick={() => openRectorPrograms()}><strong>Program Çıktıları</strong><span>Program seçerek çıktıları inceleyin</span><b aria-hidden="true">→</b></button>
              </div>
            </section>

            <section className="rector-education-access" aria-labelledby="rector-education-title">
              <div className="rector-section-heading">
                <div>
                  <span className="dean-dashboard-eyebrow">Akademik yapı</span>
                  <h2 id="rector-education-title">Akademik Birimlere Hızlı Erişim</h2>
                </div>
              </div>
              <div className="rector-education-grid">
                {['Ön Lisans', 'Lisans', 'Yüksek Lisans'].map((level) => (
                  <button type="button" key={level} onClick={() => openRectorPrograms(level)}>
                    <span>{level}</span>
                    <strong>{educationLevelCounts[level] || 0} program</strong>
                    <b aria-hidden="true">→</b>
                  </button>
                ))}
              </div>
            </section>
          </section>
        ) : (
          <>
        <section id="dean-program-management" className="dean-management-section">
          {isRectorRole && (
            <button type="button" className="dean-dashboard-back rector-program-back" onClick={() => setRectorView('home')}>
              ← Rektör Paneline Dön
            </button>
          )}
          <div className="dean-dashboard-title">
            <div>
              <span className="dean-dashboard-eyebrow">{managementRoleLabel} yetkisi</span>
              <h1>Program Yönetimi</h1>
              <p>
                {isRectorRole
                  ? 'Üniversitedeki tüm bölüm ve programları arayın; yönetim araçlarına doğrudan erişin.'
                  : 'Fakültenizdeki tüm bölüm ve programlarda, program başkanının kullanabildiği yönetim araçlarına buradan erişebilirsiniz.'}
              </p>
            </div>
            <div className="dean-dashboard-count">
              <strong>{accessiblePrograms.length}</strong>
              <span>Bölüm / Program</span>
            </div>
          </div>
          {!loading && accessiblePrograms.length > 0 && (
            <div className="dean-program-discovery" aria-label="Program arama ve filtreleme">
              <div className="dean-program-filters">
                <label className="dean-program-search">
                  <span className="sr-only">Bölüm veya program ara</span>
                  <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                    <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
                  </svg>
                  <input
                    type="search"
                    value={programSearchQuery}
                    onChange={(event) => setProgramSearchQuery(event.target.value)}
                    placeholder="Bölüm veya program adı ara..."
                    autoComplete="off"
                  />
                  {programSearchQuery && (
                    <button
                      type="button"
                      className="dean-program-search__clear"
                      onClick={() => setProgramSearchQuery('')}
                      aria-label="Aramayı temizle"
                    >
                      ×
                    </button>
                  )}
                </label>
                <label className="dean-program-filter">
                  <span>Fakülte / Enstitü / MYO</span>
                  <select value={selectedFaculty} onChange={(event) => setSelectedFaculty(event.target.value)}>
                    <option value="">Tümü</option>
                    {facultyOptions.map((facultyName) => (
                      <option value={facultyName} key={facultyName}>{facultyName}</option>
                    ))}
                  </select>
                </label>
                <label className="dean-program-filter">
                  <span>Program Türü</span>
                  <select value={selectedProgramType} onChange={(event) => setSelectedProgramType(event.target.value)}>
                    <option value="">Tümü</option>
                    {programTypeOptions.map((programType) => (
                      <option value={programType} key={programType}>{programType}</option>
                    ))}
                  </select>
                </label>
                <label className="dean-program-filter">
                  <span>Eğitim Düzeyi</span>
                  <select value={selectedEducationLevel} onChange={(event) => setSelectedEducationLevel(event.target.value)}>
                    <option value="">Tümü</option>
                    {educationLevelOptions.map((educationLevel) => (
                      <option value={educationLevel} key={educationLevel}>{educationLevel}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="dean-program-filter-reset"
                  onClick={clearProgramFilters}
                  disabled={!hasActiveProgramFilters}
                >
                  Filtreleri Temizle
                </button>
              </div>
              <div className="dean-program-results-bar" aria-live="polite">
                <p>
                  <strong>{accessiblePrograms.length}</strong> programdan{' '}
                  <strong>{filteredPrograms.length}</strong> tanesi gösteriliyor
                </p>
                <label className="dean-program-sort">
                  <span>Sıralama</span>
                  <select value={programSortDirection} onChange={(event) => setProgramSortDirection(event.target.value)}>
                    <option value="asc">Ad (A–Z)</option>
                    <option value="desc">Ad (Z–A)</option>
                  </select>
                </label>
              </div>
            </div>
          )}
          {loading ? (
            <div className="dean-dashboard-empty">Programlar yükleniyor...</div>
          ) : accessiblePrograms.length > 0 ? (
            filteredPrograms.length > 0 ? (
              <div className="dean-program-grid">
                {filteredPrograms.map((program) => (
                  <article className="dean-program-card" key={program.id}>
                    <div className="dean-program-card__top">
                      <div className="dean-program-card__avatar">
                        {(program.name || 'P').trim().charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span>{program.faculty_name}</span>
                        <h2>{highlightProgramName(program.name)}</h2>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="dean-program-primary"
                      onClick={() => navigateToProgram(program)}
                    >
                      Programı Yönet
                    </button>
                    <div className="dean-program-tools" aria-label={`${program.name} yönetim araçları`}>
                      <button type="button" onClick={() => navigateToProgram(program, 'courses')}>Program Dersleri</button>
                      <button type="button" onClick={() => navigateToProgram(program, 'electives')}>Seçmeli Dersler</button>
                      <button type="button" onClick={() => navigateToProgram(program, 'outcomes')}>Program Çıktıları</button>
                      <button type="button" onClick={() => navigateToProgram(program, 'plan')}>Ders Planı Oluştur</button>
                      <button type="button" onClick={() => navigateToProgram(program, 'syllabus')}>İzlence Doluluğu</button>
                      <button type="button" onClick={() => navigateToProgram(program, 'info')}>Program Bilgilerini Düzenle</button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="dean-dashboard-empty dean-dashboard-empty--filtered">
                <strong>Aramanızla eşleşen program bulunamadı.</strong>
                <span>Arama ifadesini değiştirin veya filtreleri temizleyin.</span>
                <button type="button" onClick={clearProgramFilters}>Filtreleri Temizle</button>
              </div>
            )
          ) : (
            <div className="dean-dashboard-empty">Yönetebileceğiniz bir program bulunamadı.</div>
          )}
        </section>
        <section id="dean-syllabus-overview" className="dean-syllabus-overview">
        <div className="dean-syllabus-header">
          <div className="dean-syllabus-heading">
            <button
              type="button"
              className="dean-dashboard-back"
              onClick={() => {
                if (isRectorRole) {
                  setRectorView('home');
                  return;
                }
                // Eğer programAdminToken varsa program sidebar'a, yoksa ana sayfaya git
                const programAdminToken = readAuthItem('programAdminToken');
                if (programAdminToken) {
                  const programIds = JSON.parse(localStorage.getItem('programAdminProgramIds') || '[]');
                  if (programIds.length > 0) {
                    navigate(`/program/sidebar?programId=${programIds[0]}`);
                  } else {
                    navigate('/');
                  }
                } else {
                  navigate('/');
                }
              }}
            >
              ← Geri
            </button>
            <div>
              <span className="dean-dashboard-eyebrow">Fakülte genel görünümü</span>
              <h1>İzlence Durumu</h1>
              <p>Programların izlence doluluklarını fakülte bazında inceleyin.</p>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="dean-dashboard-empty">İzlence bilgileri yükleniyor...</div>
        ) : faculties.length > 0 ? (
          <div className="dean-faculty-grid">
            {faculties.map(faculty => (
              <button
                type="button"
                key={faculty.id}
                className="dean-faculty-card"
                onClick={() => navigate(`/university-admin/faculty/${faculty.id}/syllabus-status`)}
              >
                <span>Fakülte</span>
                <h3>{faculty.name}</h3>
                <p>Program izlencelerini görüntüle <strong>→</strong></p>
              </button>
            ))}
          </div>
        ) : (
          <div className="dean-dashboard-empty">Fakülte bulunamadı veya yüklenemedi.</div>
        )}
        </section>
          </>
        )}
      </div>
    </div>
  );
}

// UniversityAdminFacultySyllabusStatusPage component
function UniversityAdminFacultySyllabusStatusPage({ adminToken, handleLogout }) {
  const { facultyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Token'ı prop'tan veya storage'dan al (programAdminToken da kontrol et)
  const token = adminToken || readAuthItem('adminToken') || readAuthItem('universityAdminToken') || readAuthItem('programAdminToken') || '';

  const handleBack = useCallback(() => {
    const programAdminToken = readAuthItem('programAdminToken');
    const programIds = (() => {
      try { return JSON.parse(localStorage.getItem('programAdminProgramIds') || '[]'); } catch { return []; }
    })();
    if (programAdminToken) {
      const pid = Array.isArray(programIds) ? programIds[0] : null;
      if (pid) return navigate(`/program/sidebar?programId=${pid}`);
      return navigate('/program/sidebar');
    }
    return navigate('/university-admin/dashboard');
  }, [navigate]);

  useEffect(() => {
    if (token && facultyId) {
      fetchData();
    } else if (!token) {
      console.error('[FacultyStatus] No token available');
      setLoading(false);
    } else if (!facultyId) {
      console.error('[FacultyStatus] No facultyId in URL');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultyId, token]); // fetchData'yı dependency array'e eklemiyoruz çünkü token ve facultyId değiştiğinde çalışması yeterli

  const fetchData = async () => {
    if (!facultyId) {
      console.error('[FacultyStatus] No facultyId provided');
      setLoading(false);
      return;
    }
    if (!token) {
      console.error('[FacultyStatus] No token available');
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/university-admin/faculties/${facultyId}/syllabus-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('[FacultyStatus] Response status:', res.status, 'Data:', data);
      console.log('[FacultyStatus] Data structure:', {
        hasData: !!data,
        hasPrograms: !!(data && data.programs),
        programsLength: data && data.programs ? data.programs.length : 0,
        overallAverage: data && data.overall_average_completion
      });
      if (res.ok) {
        // Data formatını kontrol et ve normalize et
        if (data && typeof data === 'object') {
          // programs array'i yoksa boş array ekle
          if (!data.programs || !Array.isArray(data.programs)) {
            console.warn('[FacultyStatus] programs array missing or invalid, setting to empty array');
            data.programs = [];
          }
          setData(data);
        } else {
          console.error('[FacultyStatus] Invalid data format:', data);
          alert('Geçersiz veri formatı alındı');
        }
      } else {
        console.error('[FacultyStatus] API error:', data);
        alert(`Veri yüklenemedi: ${data.error || 'Bilinmeyen hata'}`);
      }
    } catch (error) {
      console.error('[FacultyStatus] Error fetching faculty syllabus status:', error);
      alert(`Veri yüklenirken hata oluştu: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const slugify = (text) => {
    return String(text || '').toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
      .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '').trim()
      .replace(/\s+/g, '-').replace(/-+/g, '-');
  };

  const programs = Array.isArray(data?.programs) ? data.programs : [];
  const incompletePrograms = programs.filter(program => Number(program.average_completion || 0) < 100).length;

  return (
    <div className="App university-admin-app">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-right">
            <span className="ua-role-badge">{getStoredUniversityRoleLabel()}</span>
            <button onClick={() => navigate('/university-admin/dashboard')} className="ua-header-button">Yönetim Paneli</button>
            <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
          </div>
        </div>
      </div>
      <main className="main-content rector-dashboard-main">
        {loading ? (
          <div className="ua-state-card">İzlence bilgileri yükleniyor...</div>
        ) : data ? (
          <section className="ua-page">
            <div className="ua-page-topbar">
              <button onClick={handleBack} className="ua-button ua-button--quiet">← Yönetim Paneli</button>
            </div>
            <header className="ua-page-heading">
              <div>
                <span className="ua-eyebrow">FAKÜLTE / ENSTİTÜ RAPORU</span>
                <h1>Fakülte Bazında İzlence Doluluk Durumu</h1>
                <p>Bağlı programların ders izlencelerini tek ekrandan karşılaştırın ve eksik alanlara ulaşın.</p>
              </div>
            </header>

            <div className="ua-summary-grid ua-summary-grid--three">
              <article className="ua-summary-card ua-summary-card--accent">
                <span>Genel Doluluk</span>
                <strong>%{Number(data.overall_average_completion || 0).toFixed(1).replace('.0', '')}</strong>
                <small>Programların ortalaması</small>
              </article>
              <article className="ua-summary-card">
                <span>Program Sayısı</span>
                <strong>{programs.length}</strong>
                <small>Bu birime bağlı program</small>
              </article>
              <article className="ua-summary-card ua-summary-card--warning">
                <span>Eksik Program</span>
                <strong>{incompletePrograms}</strong>
                <small>%100 doluluğa ulaşmayan</small>
              </article>
            </div>

            <section className="ua-data-panel">
              <div className="ua-data-panel__heading">
                <div>
                  <span className="ua-eyebrow">PROGRAM BAZINDA</span>
                  <h2>İzlence Durumları</h2>
                </div>
                <span className="ua-record-count">{programs.length} program</span>
              </div>
              <div className="ua-table-scroll">
                <table className="ua-table">
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th>Toplam Ders</th>
                      <th>Ortalama Doluluk</th>
                      <th><span className="sr-only">İşlemler</span></th>
                    </tr>
                  </thead>
                  <tbody>
                {programs.length > 0 ? programs.map(program => {
                  const programSlug = slugify(program.program_name);
                  const completion = Number(program.average_completion || 0);
                  const statusClass = completion === 100 ? 'complete' : completion === 0 ? 'empty' : 'partial';
                  return (
                    <tr key={program.program_id}>
                      <td><strong className="ua-program-name">{program.program_name}</strong></td>
                      <td className="ua-table-number">{program.total_courses}</td>
                      <td>
                        <div className="ua-completion-cell">
                          <span className={`ua-status ${statusClass}`}>%{completion}</span>
                          <span className="ua-progress" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, completion))}%` }} /></span>
                        </div>
                      </td>
                      <td>
                        <div className="ua-table-actions">
                          <button
                            onClick={() => navigate(
                              `/program/${programSlug}?programId=${program.program_id}`,
                              {
                                state: {
                                  programId: program.program_id,
                                  programName: program.program_name,
                                  program: {
                                    id: program.program_id,
                                    name: program.program_name,
                                  },
                                },
                              },
                            )}
                            className="ua-button ua-button--primary"
                          >
                            Programı Yönet
                          </button>
                          <button
                            onClick={() => navigate(
                              `/university-admin/program/${program.program_id}/syllabus-status`,
                              { state: { facultyId } },
                            )}
                            className="ua-button ua-button--secondary"
                          >
                            İzlence Detayı
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="4" className="ua-table-empty">
                      Bu fakültede henüz program bulunmamaktadır.
                    </td>
                  </tr>
                )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        ) : (
          <div className="ua-state-card ua-state-card--error">
            <h2>Veri yüklenemedi</h2>
            <p>
              Fakülte bilgileri alınamadı. Lütfen sayfayı yenileyin veya yetkilerinizi kontrol edin.
            </p>
            <button onClick={() => window.location.reload()} className="ua-button ua-button--primary">
              Sayfayı Yenile
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

// UniversityAdminProgramSyllabusStatusPage component
function UniversityAdminProgramSyllabusStatusPage({ adminToken, handleLogout }) {
  const { programId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Token'ı prop'tan veya storage'dan al
  const token = adminToken || readAuthItem('adminToken') || readAuthItem('universityAdminToken') || '';

  const handleBack = useCallback(() => {
    const sourceFacultyId = location.state?.facultyId;
    if (sourceFacultyId) {
      return navigate(`/university-admin/faculty/${sourceFacultyId}/syllabus-status`);
    }
    const programAdminToken = readAuthItem('programAdminToken');
    const programIds = (() => {
      try { return JSON.parse(localStorage.getItem('programAdminProgramIds') || '[]'); } catch { return []; }
    })();
    if (programAdminToken) {
      const pid = Array.isArray(programIds) ? programIds[0] : null;
      if (pid) return navigate(`/program/sidebar?programId=${pid}`);
      return navigate('/program/sidebar');
    }
    return navigate('/university-admin/dashboard');
  }, [location.state, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [programId, token]);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/university-admin/programs/${programId}/syllabus-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setData(data);
      }
    } catch (error) {
      console.error('Error fetching program syllabus status:', error);
    } finally {
      setLoading(false);
    }
  };

  const courses = Array.isArray(data?.courses) ? data.courses : [];
  const statistics = data?.statistics || {};

  return (
    <div className="App university-admin-app">
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div className="logo">
                <img src="/kostu_logo.png" alt="Kocaeli Sağlık Ve Teknoloji Üniversitesi" className="logo-image" />
                <span className="logo-text">Kocaeli Sağlık Ve Teknoloji Üniversitesi</span>
              </div>
            </Link>
          </div>
          <div className="header-right">
            <span className="ua-role-badge">{getStoredUniversityRoleLabel()}</span>
            <button onClick={() => navigate('/university-admin/dashboard')} className="ua-header-button">Yönetim Paneli</button>
            <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
          </div>
        </div>
      </div>
      <main className="main-content rector-dashboard-main">
        {loading ? (
          <div className="ua-state-card">İzlence bilgileri yükleniyor...</div>
        ) : data ? (
          <section className="ua-page">
            <div className="ua-page-topbar">
              <button onClick={handleBack} className="ua-button ua-button--quiet">← Yönetim Paneli</button>
            </div>
            <header className="ua-page-heading">
              <div>
                <span className="ua-eyebrow">PROGRAM RAPORU</span>
                <h1>Program İzlence Doluluk Durumu</h1>
                <p>Derslerin izlence tamamlanma oranlarını dönem ve ders bazında inceleyin.</p>
              </div>
            </header>

            <div className="ua-summary-grid">
              <article className="ua-summary-card"><span>Toplam Ders</span><strong>{statistics.total_courses || 0}</strong><small>Programdaki ders sayısı</small></article>
              <article className="ua-summary-card ua-summary-card--accent"><span>Ortalama Doluluk</span><strong>%{statistics.average_completion || 0}</strong><small>Tüm derslerin ortalaması</small></article>
              <article className="ua-summary-card ua-summary-card--success"><span>Tamamlanan</span><strong>{statistics.completed_courses || 0}</strong><small>%100 doluluğa ulaşan</small></article>
              <article className="ua-summary-card ua-summary-card--warning"><span>Veri Yok</span><strong>{statistics.empty_courses || 0}</strong><small>Henüz doldurulmayan</small></article>
            </div>

            <section className="ua-data-panel">
              <div className="ua-data-panel__heading">
                <div><span className="ua-eyebrow">DERS BAZINDA</span><h2>İzlence Durumları</h2></div>
                <span className="ua-record-count">{courses.length} ders</span>
              </div>
              <div className="ua-table-scroll">
                <table className="ua-table ua-table--courses">
                  <thead><tr><th>Dönem</th><th>Ders Kodu</th><th>Ders Adı</th><th>Doluluk</th></tr></thead>
                  <tbody>
                    {courses.length > 0 ? courses.map((course, idx) => {
                      const completion = Number(course.completion_percentage || 0);
                      const statusClass = completion === 100 ? 'complete' : completion === 0 ? 'empty' : 'partial';
                      return (
                        <tr key={`${course.course_code}-${idx}`}>
                          <td className="ua-table-number">{course.semester}</td>
                          <td><span className="ua-course-code">{course.course_code}</span></td>
                          <td><strong>{course.course_name}</strong></td>
                          <td><div className="ua-completion-cell"><span className={`ua-status ${statusClass}`}>%{completion}</span><span className="ua-progress" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, completion))}%` }} /></span></div></td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan="4" className="ua-table-empty">Bu programda henüz ders bulunmamaktadır.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        ) : (
          <div className="ua-state-card ua-state-card--error">
            <h2>Veri yüklenemedi</h2>
            <p>Program izlence bilgileri alınamadı. Lütfen sayfayı yenileyin veya yetkilerinizi kontrol edin.</p>
            <button onClick={() => window.location.reload()} className="ua-button ua-button--primary">Sayfayı Yenile</button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
